// =============================================================================
// WTW Change Management Platform — BLANK TEMPLATE for client pilots
// =============================================================================
//
// Setup notes (for CodeSandbox or similar):
//   npm dependencies needed:
//     - react, react-dom (default)
//     - lucide-react   (icon library, ~0.383)
//     - recharts       (charts on the dashboard)
//     - mammoth        (Word .docx parsing — only loaded when a .docx is dropped)
//
//   Tailwind: include the Tailwind CDN <script> in public/index.html, e.g.:
//     <script src="https://cdn.tailwindcss.com"></script>
//
//   Other parsing libraries are loaded from a CDN at runtime — no install needed:
//     - pdf.js  (PDF text extraction)
//     - JSZip   (PowerPoint .pptx — pptx is a zip of XML)
//     - SheetJS (Excel .xlsx — renders sheets as CSV-style text)
//
//   First-run flow: app opens on Define → Project setup. Fill in the project
//   name, client, dates; drop client background docs into the Context tab;
//   then add your first change in the Change Impact tool.
//
//   Persistence: project state auto-saves to browser localStorage (debounced).
//   Reset button in the header (top-right) clears it.
//
// =============================================================================

import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import {
  ChevronRight,
  ChevronDown,
  Plus,
  Trash2,
  Users,
  User,
  Calendar,
  TrendingUp,
  AlertCircle,
  Sparkles,
  Settings,
  LayoutDashboard,
  GitBranch,
  Clock,
  Filter,
  X,
  Check,
  Download,
  Upload,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ZAxis,
} from "recharts";

// ============================================================================
// DESIGN TOKENS — WTW Brand Central palette per Nathan's notes
// ============================================================================
// =============================================================================
// DESIGN TOKENS — WTW Brand Central palette + polished surface/elevation system
// =============================================================================
// Polish pass:
//   - Added subtle shadow scale (xs → lg) for layered depth
//   - Added surface tokens for backgrounds (page vs panel vs accent)
//   - Added transition tokens for consistent micro-interactions
//   - Refined the WTW purple with a soft tint variant for backgrounds
const tokens = {
  impact: {
    none: "#E5E7EB",
    small: "#7FB3D5",
    medium: "#F39C12",
    large: "#E74C3C",
    extreme: "#922B21",
  },
  effort: {
    low: "#7FB3D5",
    medium: "#F39C12",
    high: "#922B21",
  },
  primary: "#4B2E83",
  primaryDark: "#2D1B4E",
  primaryLight: "#6B4DA8",
  primaryTint: "#F5F1FA", // very soft purple wash for selected rows / accent backgrounds
  primaryTintHover: "#EAE2F4",
  ink: "#1A1A2E",
  // Page-level surfaces
  surface: {
    page: "#FAF9F7", // warm off-white for the app background (slightly warmer than stone-50)
    panel: "#FFFFFF", // card / panel
    panelMuted: "#F7F5F2", // headers, table headers
    overlay: "rgba(26, 26, 46, 0.42)",
  },
  // Subtle shadows — kept gentle so the UI doesn't feel "floaty"
  shadow: {
    xs: "0 1px 2px 0 rgba(26, 26, 46, 0.04)",
    sm: "0 1px 3px 0 rgba(26, 26, 46, 0.06), 0 1px 2px 0 rgba(26, 26, 46, 0.04)",
    md: "0 4px 8px -2px rgba(26, 26, 46, 0.06), 0 2px 4px -2px rgba(26, 26, 46, 0.04)",
    lg: "0 12px 24px -6px rgba(26, 26, 46, 0.10), 0 6px 12px -4px rgba(26, 26, 46, 0.06)",
    focus: "0 0 0 3px rgba(75, 46, 131, 0.18)",
  },
  // Transition curves
  transition: {
    fast: "120ms cubic-bezier(0.2, 0.0, 0.0, 1.0)",
    base: "200ms cubic-bezier(0.2, 0.0, 0.0, 1.0)",
  },
};

// ============================================================================
// SEED DATA
// ============================================================================
const SEED = {
  project: {
    id: "p1",
    name: "New project",
    client: "",
    description: "",
    startDate: "",
    endDate: "",
  },
  clientContext: [],
  taxonomies: {
    impactTypes: [
      { id: "it1", label: "Structure" },
      { id: "it2", label: "Process & Policies" },
      { id: "it3", label: "Tools & Technology" },
      { id: "it4", label: "Skills & Knowledge" },
      { id: "it5", label: "Behaviors & Ways of Working" },
      { id: "it6", label: "Culture" },
    ],
    reactionTypes: [
      { id: "r1", label: "Attachment" },
      { id: "r2", label: "Uncertainty" },
      { id: "r3", label: "Overload" },
      { id: "r4", label: "Loss of Control" },
      { id: "r5", label: "Competency" },
      { id: "r6", label: "Ambivalence" },
      { id: "r7", label: "Anger" },
      { id: "r8", label: "Enthusiasm" },
    ],
    changeLevers: [
      {
        id: "l1",
        label: "Inform",
        desc: "Ensure employees understand the change, its reasons, and impacts.",
      },
      {
        id: "l2",
        label: "Promote",
        desc: "Encourage acceptance and active support for the change.",
      },
      {
        id: "l3",
        label: "Enable",
        desc: "Provide tools, skills, and resources for employees to adapt.",
      },
      {
        id: "l4",
        label: "Embed",
        desc: "Integrate the change into systems, processes, and culture.",
      },
      {
        id: "l5",
        label: "Listen",
        desc: "Gauge employee feedback and perceptions related to the change.",
      },
      {
        id: "l6",
        label: "Ongoing Measurement",
        desc: "Regularly assess the impact and effectiveness of the change.",
      },
    ],
    roles: [
      { id: "ro1", label: "Executive" },
      { id: "ro2", label: "Management" },
      { id: "ro3", label: "Professional/Expert" },
      { id: "ro4", label: "Technical Support" },
      { id: "ro5", label: "Business Support" },
      { id: "ro6", label: "Production" },
      { id: "ro7", label: "All employees" },
    ],
    functions: [
      { id: "f1", label: "Human Resources" },
      { id: "f2", label: "Finance" },
      { id: "f3", label: "Information Systems & Technology" },
      { id: "f4", label: "Marketing and Sales" },
      { id: "f5", label: "Operations Management" },
      { id: "f6", label: "Customer Service" },
      { id: "f7", label: "Legal, Risk & Compliance" },
      { id: "f8", label: "All functions" },
    ],
    levels: [
      { id: "lv1", label: "Executive VP / Chief" },
      { id: "lv2", label: "Vice President" },
      { id: "lv3", label: "Senior" },
      { id: "lv4", label: "Expert" },
      { id: "lv5", label: "Specialist" },
      { id: "lv6", label: "Intermediate" },
      { id: "lv7", label: "Entry Level" },
      { id: "lv8", label: "All Employees" },
    ],
    workstreams: [
      { id: "ws1", label: "HR" },
      { id: "ws2", label: "Finance" },
      { id: "ws3", label: "Information Technology" },
      { id: "ws4", label: "External Comms" },
      { id: "ws5", label: "Tech Ops" },
      { id: "ws6", label: "Legal" },
    ],
    audienceSizes: [
      { id: "as1", label: "Dozens", value: 10 },
      { id: "as2", label: "Hundreds", value: 100 },
      { id: "as3", label: "Thousands", value: 1000 },
      { id: "as4", label: "Ten-thousands", value: 10000 },
      { id: "as5", label: "Hundred-thousands", value: 100000 },
    ],
    impactLevels: [
      { id: "il1", label: "No impact", value: 1 },
      { id: "il2", label: "A small impact", value: 2 },
      { id: "il3", label: "Medium impact", value: 3 },
      { id: "il4", label: "Large impact", value: 4 },
      { id: "il5", label: "Extremely large impact", value: 5 },
    ],
    effortLevels: [
      { id: "el1", label: "Low", value: 1 },
      { id: "el2", label: "Medium", value: 2 },
      { id: "el3", label: "High", value: 3 },
    ],
    // 17 typical end-user personas from the change levers file (Who tab)
    personas: [
      {
        id: "pe1",
        label: "Executives & Senior Leadership",
        desc: "Top-tier decision-makers",
      },
      {
        id: "pe2",
        label: "Business Unit Leaders",
        desc: "Leaders overseeing specific departments",
      },
      {
        id: "pe3",
        label: "People Managers",
        desc: "Supervisory roles overseeing teams",
      },
      {
        id: "pe4",
        label: "Technical & IT Staff",
        desc: "Technology infrastructure & support",
      },
      {
        id: "pe5",
        label: "Frontline Workers",
        desc: "Direct interaction with products, services, customers",
      },
      {
        id: "pe6",
        label: "Administrative & Support Staff",
        desc: "HR, finance, administrative professionals",
      },
      {
        id: "pe7",
        label: "Specialized Roles or Departments",
        desc: "R&D, Marketing, specialized expertise",
      },
      {
        id: "pe8",
        label: "Remote or Distributed Workers",
        desc: "Outside the main office or in varied locations",
      },
      {
        id: "pe9",
        label: "Contractors, Freelancers, Temporary",
        desc: "Non-permanent engagement",
      },
      { id: "pe10", label: "All Employees", desc: "The broadest category" },
      {
        id: "pe11",
        label: "Human Resources",
        desc: "People strategy & employee relations",
      },
      {
        id: "pe12",
        label: "Communications",
        desc: "Delivering employee communications",
      },
      {
        id: "pe13",
        label: "Compliance & Regulatory",
        desc: "Aligning with regulations and policies",
      },
      {
        id: "pe14",
        label: "Project Teams",
        desc: "Implementing the change initiative",
      },
      {
        id: "pe15",
        label: "Training & Development",
        desc: "Designing and delivering training",
      },
      {
        id: "pe16",
        label: "Internal Consultants / SMEs",
        desc: "Deep expertise in specific areas",
      },
      {
        id: "pe17",
        label: "Shareholders / Board",
        desc: "Financial interest or strategic direction",
      },
      {
        id: "pe18",
        label: "External Stakeholders",
        desc: "Clients, customers, suppliers, partners",
      },
    ],
    craCategories: [
      {
        num: 1,
        name: "Vision and Goal",
        question:
          "We have a clear view of what the change initiative is trying to achieve.",
        keysToSuccess:
          "1. Clarity in Vision: For effective change, the vision acts as a north star; its clarity is critical to ensure employees can align their efforts and understand the direction of transformation.\n\n2. Vision-centric Employee Engagement: Engage employees effectively by explaining a transparent roadmap for the future and the milestones of the transformation process.\n\n3. Consistent Leadership Messaging: Emphasize the importance of a consistent and authentic message from senior leaders to foster trust and a sense of unity during change.\n\n4. Enhanced Vision Communication: Elevate communication strategies when necessary to ensure comprehensive employee understanding and buy-in regarding the envisioned change.",
        tacticsR:
          "\u2022 Re-evaluate the Vision: Before re-communicating, ensure the vision itself is clear, compelling, and attainable. Simplify if necessary.\n\n\u2022 Involve Employees: Engage employees in co-creating or refining the vision. When they have a hand in shaping it, they are more likely to understand and commit.\n\n\u2022 Focused Training Sessions: Organize sessions dedicated to understanding the vision, its implications, and its benefits.\n\n\u2022 Visual Representations: Use infographics, videos, and other visual tools to represent the vision. A visual depiction can often be clearer than a lengthy document.\n\n\u2022 Understand the individual change impact: Identify different employee personas (representing different business groups/functions, roles, career levels, geographies, etc.) and complete a stakeholder change impact analysis. ",
        tacticsY:
          "\u2022 Clarify Communication: Review how the vision and goal have been communicated. Consider how to make it clearer or more relatable. Review how the messages are being shared and to whom they\u2019re going.\n\n\u2022 Engage in Workshops: Organize workshops to delve deeper into the vision, allowing a cross-representation (roles, geographies, levels etc.) of employees to explore its meaning and implications. \n\n\u2022 Use Multiple Communication Channels: Use various channels like intranet, newsletters, town halls, and team meetings to regularly communicate and clarify the vision.\n\n\u2022 Leadership Alignment: Ensure all leaders, from top management to front line supervisors, understand and believe in the vision. Provide supporting resources to ensure leaders are prepared to communicate the vision consistently.",
        tacticsG:
          "\u2022 Reinforce Regularly: Even if the vision and goal are well-understood, frequent reinforcement through communications, meetings, and learning sessions can keep the momentum going.\n\n\u2022 Celebrate Milestones: As you reach specific goals or milestones related to the vision, celebrate them. This reinforces the vision, shows progress and keeps employees energized.\n\n\u2022 Encourage Leadership: Leaders should consistently embody the vision in their actions and decisions.\n\n\u2022 Feedback Mechanism: Regularly solicit feedback to ensure the vision remains clear and relevant as the change progresses.",
      },
      {
        num: 2,
        name: "Case for Change",
        question: "People understand why we need to change.",
        keysToSuccess:
          "1. Clear and Compelling: Connect with employees (rationally and emotionally) by presenting a strong and persuasive reason to change. This rationale captures both the hearts and minds of employees.\n\n2. Inclusive: All employees, not just select groups, understand and believe in the need for change.\n\n3. Highlight the Importance: Emphasize the need for change and the risks of maintaining the status quo.\n\n4. Consistent Communication and Leadership: Advocate for routine feedback, maintain open lines of dialogue, and ensure leadership remains coordinated throughout the transformation journey.",
        tacticsR:
          '\u2022 Diagnose the Cause: Before acting, understand the root cause. Is it a communication issue? Do people not understand the "why" behind the change? Have the messages not been shared with the appropriate stakeholders and audiences? Have appropriate stakeholders been engaged?\n\n\u2022 Leadership Alignment: Build alignment around the change by understanding individual leader perspectives, needs, opportunities and challenges. Conduct leadership workshops and provide consistent messaging and communication resources to build understanding, gain buy-in and ensure clarity of vision. \n\n\u2022 Clear, Consistent Communication: Simplify the message if necessary and communicate it consistently across multiple channels. Test messaging to ensure it resonates with different audiences and stakeholders.\n\n\u2022 Seek External Help: Consider hiring change management advisors who can bring an external perspective and proven strategies.\n\n\u2022 Pilot the Change: Start with a small group, implement the change, and highlight its benefits. Success stories can then be used to influence the wider organization.',
        tacticsY:
          "\u2022 Open Dialogue: Host forums, town halls, or workshops where employees can express their ideas, excitement, concerns, ask questions, and get clarifications.\n\n\u2022 Enhance Communication: Clarify the messaging for the change, focusing on the rationale for why, the benefits and the long-term vision.\n\n\u2022 Engage Middle Management: Ensure that managers and front-line team leaders are on board \u2013 they play a crucial role in cascading information down to their teams.\n\n\u2022 Provide Training: Offer training sessions that align with the proposed changes, ensuring everyone has the tools and knowledge they need.",
        tacticsG:
          "\u2022 Reinforce and Reward: Recognize and reward those who are actively supporting and promoting the change. This reinforces the importance of their role.\n\n\u2022 Maintain Communication: Continue sharing success stories, data, and progress reports to keep everyone informed and engaged.\n\n\u2022 Identify Champions: Pinpoint individuals who are particularly passionate about the change and enlist them as change ambassadors.\n\n\u2022 Evaluate and Iterate: Regularly gather feedback (e.g. ongoing change readiness assessments) and be prepared to refine, adjust and/or pivot the change strategy, if necessary.",
      },
      {
        num: 3,
        name: "Change Strategy and Plan",
        question:
          "We have a structured change plan with realistic and achievable timelines.",
        keysToSuccess:
          "1. Change Impact Analysis: Thoroughly evaluate the effects on employees, acknowledging potential hurdles and determining the necessary support mechanisms.\n\n2. Comprehensive Change Strategy: Craft a robust change management approach that outlines success indicators, potential risks, and primary business objectives.\n\n3. Role Clarity: Ensure employees are clear on their role and responsibilities in the context of the impending change.",
        tacticsR:
          "\u2022 Reassess the Strategy: A low score indicates foundational issues. Review and reassess the strategy.\n\n\u2022 Hire External Experts: Consider engaging change management advisors who can provide an outsider's perspective and bring in best practices.\n\n\u2022 Re-engage Leadership: Leaders play a pivotal role in change management. Ensure they are aligned, committed, and vocal supporters of the change.\n\n\u2022 Redevelop Communication Plans: There is a strong possibility that the message is not getting across. Develop a clear, consistent, and comprehensive communication plan. Solicit employee input to ensure the messages are resonating.\n\n\u2022 Involve the Workforce: Engage teams in co-creating parts of the plan. When people have a say in a process, they are more likely to support it.",
        tacticsY:
          "\u2022 Stakeholder Engagement: Engage with key stakeholders to understand their concerns and gather suggestions. Their buy-in can influence others.\n\n\u2022 Task Force or Committee: Consider establishing a change management committee or task force that can delve deeper into potential issues and suggest refinements.\n\n\u2022 Pilot Programs: Before a full-scale rollout, test the plan in smaller teams or departments. Use this as a learning opportunity to iron out any kinks.\n\n\u2022 Resource Alignment: Keep track of resources (e.g., staffing, budget) to ensure there are the levels required to achieve the milestones in the change plan. ",
        tacticsG:
          "\u2022 Refine & Reinforce: Even the best plans can benefit from ongoing refinement. Regularly review and adjust, as necessary.\n\n\u2022 Ongoing Awareness: Ensure that any new members of the team, or those who need refreshers, understand the change strategy and their role.\n\n\u2022 Feedback Loops: Set up mechanisms for continuous feedback from all levels of the organization to ensure any minor issues are caught early and addressed and any identified risks are mitigated.\n\n\u2022 Celebrate Milestones: Recognize and celebrate achievements and milestones as they occur. This boosts morale and emphasizes the importance of the change process.",
      },
      {
        num: 4,
        name: "Organizational Infrastructure and Capability",
        question:
          "We have the capacity and capability to successfully implement the change.",
        keysToSuccess:
          "1. Project Governance: Guide efficient use of resource through strong project governance and management \u2013 move at pace and pivot when required.\n\n2. Skillset roadmap: Ensure a clear plan is in place for delivering the skills needed to implement the change. \n\n3. Operational enablement: Ensure the required systems and processes are in place to enable employees to function as effectively as possible.",
        tacticsR:
          "\u2022 Continuous Training: Focus on keeping the workforce updated with the latest skills, ensuring that they remain competent and adaptive in the latest change management tools/resources.\n\n\u2022 Monitor and Upgrade: Regularly evaluate the technology and process infrastructure. Implement upgrades when necessary to ensure they remain current and efficient (enterprise-wide change assessments, CoE\u2019s, templates, etc.)\n\n\u2022 Feedback Channels: Ensure there are open channels for feedback from employees (especially leaders) about tools, processes, or training they feel might need updating.\n\n\u2022 Documentation: Ensure that all processes, best practices, and protocols are documented. This can be a reference for both current and future employees.",
        tacticsY:
          "\u2022 Gap Analysis: Determine the exact areas where the organization falls short in terms of infrastructure or capability.\n\n\u2022 Targeted Investments: Prioritize and invest in areas that are crucial to the intended change. This might mean new software, tools, or training programs.\n\n\u2022 Engage Middle Management: Middle managers are crucial in translating high-level strategy to ground-level execution. Engage and train them specifically.\n\n\u2022 Collaborative Workshops: Organize workshops where teams can come together to learn, brainstorm, and collaboratively address capability gaps.",
        tacticsG:
          "\u2022 Continuous Training: Focus on keeping the workforce updated with the latest skills, ensuring that they remain competent and adaptive in the latest change management tools/resources.\n\n\u2022 Monitor and Upgrade: Regularly evaluate the technology and process infrastructure. Implement upgrades when necessary to ensure they remain current and efficient (enterprise-wide change assessments, CoE\u2019s, templates, etc.)\n\n\u2022 Feedback Channels: Ensure there are open channels for feedback from employees (especially leaders) about tools, processes, or training they feel might need updating.\n\n\u2022 Documentation: Ensure that all processes, best practices, and protocols are documented. This can be a reference for both current and future employees.",
      },
      {
        num: 5,
        name: "Personal Motivation",
        question: "People are ready and willing to participate in the change.",
        keysToSuccess:
          "1. Empower Employees: Engage and involve employees by giving them the ability to contribute and challenge. \n\n2. Clearly Communicate the Value: Clearly communicate the benefits (personal and organizational) for different stakeholder groups.\n\n3. Align Incentives: Ensure recognition and rewards (financial and non-financial) are aligned to transformation objectives to motivate the desired behaviors to support the change ",
        tacticsR:
          '\u2022 Revisit the Vision: Ensure that the vision and purpose of the change are clear and compelling. If people understand the "why" they are more likely to support the \u201cwhat\u201d and "how".\n\n\u2022 Involve Leaders: Leaders should be role models. Build alignment and sense of ownership for the change(s). Their authentic and visible enthusiasm, commitment, and confidence in the change helps employees get comfortable.\n\n\u2022 Personalized Incentives: Consider introducing incentives (monetary, promotions, recognition) tailored to individual needs to motivate participation.\n\n\u2022 Empathy and Support: Understand that resistance might stem from fear or uncertainty. Offer emotional and logistical support, counseling, or other resources to help employees through their concerns.',
        tacticsY:
          "\u2022 Engage in Dialogues: Organize forums or town halls where employees can share ideas, voice concerns, ask questions, and gain a deeper understanding for the rationale for change.\n\n\u2022 Personalize Learning & Development: Offer learning opportunities that address individual needs, making it more relevant and engaging for each employee.\n\n\u2022 Build Employee Advocacy : Pair employees less motivated with those enthusiastic about the change to build positive energy and understanding.\n\n\u2022 Share Success Stories: Highlighting positive outcomes, even small ones, can inspire and motivate employees who are on the fence about the change.",
        tacticsG:
          "\u2022 Acknowledge and Reward: Recognize and celebrate individuals and teams who are champions of change. This will reinforce their commitment and inspire others.\n\n\u2022 Maintain Open Communication: Continually provide updates about the change process. Keeping employees informed is key to retaining their support.\n\n\u2022 Provide Leadership Opportunities: Empower those who are motivated by giving them leadership roles or opportunities within the change initiative.\n\n\u2022 Feedback Mechanisms: Allow motivated employees to share their experiences, challenges, and suggestions to refine the change process.",
      },
      {
        num: 6,
        name: "Leadership Change Capability",
        question:
          "Leaders are investing as much energy in leading the change as they did determining what needed to change.",
        keysToSuccess:
          "1. Equip managers: Provide managers with essential resources and backing, enabling them to effectively guide their teams during the transition.\n\n2. Visible Leadership: Ensure leaders are highly visible and accessible throughout the transformation and act as early adopters and role models for desired behaviours. \n\n3. Trust-building Leadership: Foster a deeper trust in leadership by emphasizing genuine interactions and upholding mutual respect throughout the change process.",
        tacticsR:
          "\u2022 Clarify Roles and Responsibilities: Provide clear documentation and briefings to define managers' specific roles and responsibilities during the change process.\n\n\u2022 Understand Change Implications: Conduct informational sessions detailing the implications and benefits of the change for both managers and their respective teams.\n\n\u2022 Equip Managers for Guidance: Offer managers comprehensive resources, including talking points, briefing decks, and FAQs, to effectively steer their teams through the transformation.",
        tacticsY:
          "\u2022 Manager Change Toolkit: Create a comprehensive toolkit that includes a change roadmap, role clarification, key messaging, FAQs, and practical advice on navigating change.\n\n\u2022 Change Leadership Training: Implement specialized training sessions that enhance managerial proficiency in leading and facilitating organizational change.\n\n\u2022 Ongoing Data for Managers: Regularly furnish managers with actionable insights from pulse surveys and virtual focus groups to understand and address team needs during the transition.",
        tacticsG:
          "\u2022 Gather Ongoing Manager Feedback: Understand where further support may be needed (e.g., virtual focus groups / pulse surveys / transformation inbox / Q&A sessions)\n\n\u2022 Make Information Access Easy Develop a dedicated manager section of a transformation website providing information updates and easy access to all resources they need to lead the change. \n\n\u2022 Assess and Develop: Conduct leadership assessment and development (e.g., resilient agility assessment) to help managers understand their strengths and areas for development in leading change and provide the project team with a view of manager capability across the organisation.",
      },
      {
        num: 7,
        name: "Communication",
        question:
          "The right messages are being delivered by the right people at the right time.",
        keysToSuccess:
          "1. Tailored Content Delivery: Distribute compelling and user-friendly content strategically, ensuring it reaches the intended audience precisely when it is most impactful.\n\n2. Seamless Information Exchange: Promote a culture where information circulates effortlessly both within teams and across different departments.\n\n3. Culture of Open Dialogue: Cultivate an atmosphere that encourages transparent discussions, values constructive debates, and celebrates shared achievements.",
        tacticsR:
          "\u2022 Change Narrative: Craft an impactful change narrative tailored to distinct audience segments, ensuring resonance with varied stakeholders.\n\n\u2022 Audit Communication Channels : Conduct a thorough audit of current communication channels to identify and maximize the most effective mediums for delivering change messages.\n\n\u2022 Create a Multi-channel Communication Plan: Design a holistic multi-channel communication strategy that integrates varied methods such as emails, webinars, roadshows, and interactive documents, while emphasizing real-time feedback on transformation developments.\n\n\u2022 Clarify Roles and Responsibilities in Communication: Clearly delineate and communicate roles and responsibilities within the team for content creation, approval, and dissemination, ensuring agile and timely communication.",
        tacticsY:
          "\u2022 Transformation Website: Create a central transformation website as a comprehensive resource hub, incorporating features for user feedback.\n\n\u2022 Stakeholder Personas: Craft distinct personas for varied stakeholder groups, utilizing these profiles to shape and refine targeted communication strategies.\n\n\u2022 Change Champion Network: Initiate a network of Change Champions to bolster communication channels and facilitate consistent feedback throughout the transformation process.\n\n\u2022 Employee Listening: Regularly engage in employee listening mechanisms, such as pulse surveys and virtual focus groups, to gather real-time feedback and adjust change and communication strategies accordingly.",
        tacticsG:
          "\u2022 Transformation Website: Launch a transformation website tailored to specific stakeholder groups, ensuring streamlined access to relevant information for both employees and managers.\n\n\u2022 Pulse Survey: Implement a periodic pulse survey to evaluate the success of communication strategies and identify areas for enhancement based on employee feedback.\n\n\u2022 Segmented Communications: Continue to refine and tailor communication content based on individual stakeholder preferences and profiles to boost overall engagement.",
      },
      {
        num: 8,
        name: "Change Measurement",
        question:
          "Measures for the change have been identified and will be tracked and communicated.",
        keysToSuccess:
          "1. Define Success Indicators: From the onset, establish the critical indicators that will signify a successful change.\n\n2. Monitoring Progress and Build Flexibility: Continuously track and share readiness progression with stakeholders, adapting the readiness strategy as insights emerge.\n\n3. Build Employee Engagement and Resilience: Clarify change-related expectations for employees and foster a culture that champions open dialogue and iterative learning during the readiness phase.",
        tacticsR:
          "\u2022 Agree on Measures: Establish specific metrics for gauging change, such as business performance, past project data, and employee feedback, while defining success criteria for each.\n\n\u2022 Track and Report: Systematically monitor and communicate the identified metrics at predetermined intervals to evaluate progress and determine any necessary adjustments to the change strategy.\n\n\u2022 Incorporate into Existing Channels: Integrate transformation-related inquiries into pre-existing employee feedback mechanisms like annual evaluations and ongoing pulse surveys.",
        tacticsY:
          "\u2022 Ongoing Tracking: Continuously monitor and communicate key metrics during the project to guide the change strategy and inform any course corrections.\n\n\u2022 Progress Updates: Regularly inform employees about advancements concerning pivotal measures to maintain transparency and engagement.\n\n\u2022 Pulse Surveys/Focus Groups: Implement periodic pulse surveys and virtual focus groups to gauge employee perceptions of the transformation, subsequently reporting on remedial actions.\n\n\u2022 Embed Objectives: Integrate transformation-specific goals into the objectives of leaders, managers, and employees to foster desired behaviors and outcomes.",
        tacticsG:
          "\u2022 Change Dashboard: Create a dynamic dashboard highlighting pivotal metrics for consistent reporting and analysis to the change steering committee.\n\n\u2022 Data Access for Leaders: Grant leaders and managers consistent access to relevant data, objectives, and team-specific metrics to fine-tune their change management approaches.\n\n\u2022 Updates to Employees: Disseminate periodic updates to staff about performance metrics, tailoring information to specific teams where necessary.\n\n\u2022 Review Decision Rights: Assess and adjust decision-making rights to ensure individuals are fully empowered to drive transformation and effectively fulfil their designated roles.",
      },
    ],
    sizingQuestions: [
      {
        num: 1,
        text: "How many employees are impacted by this change?",
        category: "sizing",
        reverse: false,
        scale: [
          "Dozens",
          "Hundreds",
          "Thousands",
          "Ten-thousands",
          "Hundred-thousands",
        ],
      },
      {
        num: 2,
        text: "What is the magnitude of the change?",
        category: "sizing",
        reverse: false,
        scale: [
          "Single impact, single audience",
          "Multiple impacts, single audience",
          "Multiple impacts, multiple audiences",
          "Multiple change programs, single audience",
          "Multiple change programs, multiple audiences",
        ],
      },
      {
        num: 3,
        text: "How many changes are currently underway with your impacted audiences?",
        category: "impact",
        reverse: false,
        scale: ["None", "A few", "Some", "Many", "A very large number"],
      },
      {
        num: 4,
        text: "How positively or negatively will impacted audience(s) react to the implementation of this change?",
        category: "impact",
        reverse: true,
        scale: [
          "Very negatively",
          "Negatively",
          "Neither negative nor positive",
          "Positive",
          "Very positive",
        ],
      },
      {
        num: 5,
        text: "How involved will HR leadership need to be in overseeing this change?",
        category: "sizing",
        reverse: false,
        scale: [
          "Not at all involved",
          "Somewhat involved",
          "Involved",
          "Very involved",
          "Extremely involved",
        ],
      },
      {
        num: 6,
        text: "How involved will corporate leadership need to be in overseeing this change?",
        category: "sizing",
        reverse: false,
        scale: [
          "Not at all involved",
          "Somewhat involved",
          "Involved",
          "Very involved",
          "Extremely involved",
        ],
      },
      {
        num: 7,
        text: "How involved will business unit leadership need to be in overseeing this change?",
        category: "sizing",
        reverse: false,
        scale: [
          "Not at all involved",
          "Somewhat involved",
          "Involved",
          "Very involved",
          "Extremely involved",
        ],
      },
      {
        num: 8,
        text: "How many HR resources will be needed to deliver this change?",
        category: "impact",
        reverse: false,
        scale: ["None", "A few", "Some", "Many", "A very large number"],
      },
      {
        num: 9,
        text: "How many corporate resources will be needed to deliver this change?",
        category: "impact",
        reverse: false,
        scale: ["None", "A few", "Some", "Many", "A very large number"],
      },
      {
        num: 10,
        text: "How many business unit resources will be needed to deliver this change?",
        category: "impact",
        reverse: false,
        scale: ["None", "A few", "Some", "Many", "A very large number"],
      },
      {
        num: 11,
        text: "In general, how effective are leaders and/or managers at leading change?",
        category: "impact",
        reverse: false,
        scale: [
          "Not at all effective",
          "Somewhat effective",
          "Effective",
          "Very effective",
          "Extremely effective",
        ],
      },
      {
        num: 12,
        text: "How big of an impact will this change have on employees (e.g., new skills, roles, culture, technology)?",
        category: "impact",
        reverse: false,
        scale: [
          "No impact",
          "A small impact",
          "Medium impact",
          "Large impact",
          "Extremely large impact",
        ],
      },
      {
        num: 13,
        text: "How big of an impact will this change have on leaders?",
        category: "impact",
        reverse: false,
        scale: [
          "No impact",
          "A small impact",
          "Medium impact",
          "Large impact",
          "Extremely large impact",
        ],
      },
    ],
    sizingInterventions: [
      { phase: "Define", name: "Case for Change and Key Messages", tier: 3 },
      { phase: "Define", name: "Change Readiness Assessment", tier: 3 },
      { phase: "Define", name: "Measurement Approach", tier: 3 },
      { phase: "Discover", name: "Stakeholder Assessment", tier: 3 },
      {
        phase: "Discover",
        name: "Change Impact Assessment - High Level",
        tier: 3,
      },
      { phase: "Design", name: "Stakeholder Engagement Plan", tier: 3 },
      { phase: "Design", name: "Change Management Plan", tier: 3 },
      {
        phase: "Design",
        name: "Change Management Deliverables Creation",
        tier: 3,
      },
      { phase: "Transform", name: "Deliverables Rollout", tier: 3 },
      { phase: "Transform", name: "Measurement dashboard", tier: 3 },
      {
        phase: "Discover",
        name: "Change Impact Assessment - Detailed",
        tier: 2,
      },
      { phase: "Discover", name: "Stakeholder Alignment", tier: 2 },
      { phase: "Design", name: "Persona Development", tier: 2 },
      { phase: "Discover", name: "Training Needs Analysis", tier: 1 },
      { phase: "Discover", name: "Ease/Benefit Matrix", tier: 1 },
      { phase: "Design", name: "Change Agent Network Development", tier: 1 },
      { phase: "Transform", name: "Kick-off Change Agent Network", tier: 1 },
    ],
  },
  reactionLeverMap: {
    r1: { primary: "l2", secondary: "l4" },
    r2: { primary: "l1", secondary: "l5" },
    r3: { primary: "l3", secondary: "l1" },
    r4: { primary: "l3", secondary: "l5" },
    r5: { primary: "l3", secondary: "l1" },
    r6: { primary: "l2", secondary: "l1" },
    r7: { primary: "l5", secondary: "l1" },
    r8: { primary: "l4", secondary: "l3" },
  },
  changes: [],
  changesToImplement: [],
  audiences: [],
  stakeholders: [],
  interventions: [],
  assessments: [],
  sizingAssessments: [],
  pendingProposals: [],
  terminologyOverrides: {},
};

// ============================================================================
// HELPERS
// ============================================================================
function uid(prefix = "x") {
  return `${prefix}${Math.random().toString(36).slice(2, 9)}`;
}

function lookupLabel(state, taxonomyKey, id) {
  if (!id) return "";
  const list = state.taxonomies[taxonomyKey];
  if (!list) return "";
  const row = list.find((r) => r.id === id);
  if (!row) return "";
  const overrides = state.terminologyOverrides[taxonomyKey];
  if (overrides && overrides[id]) return overrides[id];
  return row.label;
}

function impactColor(impactLevelId) {
  const m = {
    il1: tokens.impact.none,
    il2: tokens.impact.small,
    il3: tokens.impact.medium,
    il4: tokens.impact.large,
    il5: tokens.impact.extreme,
  };
  return m[impactLevelId] || tokens.impact.none;
}
function effortColor(effortLevelId) {
  const m = {
    el1: tokens.effort.low,
    el2: tokens.effort.medium,
    el3: tokens.effort.high,
  };
  return m[effortLevelId] || "#ccc";
}

// ============================================================================
// CLIENT CONTEXT — extract text from dropped files for use as AI context
// ============================================================================
// Supported: PDF, Word (.docx), PowerPoint (.pptx), Excel (.xlsx), plain text (.txt),
// Markdown (.md, .markdown).
//
// Parsing strategy by format:
//   PDF    — pdf.js (CDN)        full text per page
//   DOCX   — mammoth (npm)       raw text
//   PPTX   — jszip (CDN) + XML   slide titles + bullets, joined per slide
//   XLSX   — SheetJS (CDN)       each sheet rendered as CSV-style text
//   TXT/MD — FileReader          straight read

const ACCEPTED_TYPES = [
  ".pdf",
  ".docx",
  ".pptx",
  ".xlsx",
  ".txt",
  ".md",
  ".markdown",
];

// ----- Singleton CDN loaders ------------------------------------------------
// Each library is loaded once and reused. Keeps the initial bundle small and
// avoids reloading on every file drop.

let pdfjsPromise = null;
function loadPdfJs() {
  if (pdfjsPromise) return pdfjsPromise;
  pdfjsPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined")
      return reject(new Error("PDF parsing requires a browser environment"));
    if (window.pdfjsLib) return resolve(window.pdfjsLib);
    const script = document.createElement("script");
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    script.onload = () => {
      try {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
        resolve(window.pdfjsLib);
      } catch (e) {
        reject(e);
      }
    };
    script.onerror = () =>
      reject(new Error("Could not load pdf.js — check network connection"));
    document.head.appendChild(script);
  });
  return pdfjsPromise;
}

let jszipPromise = null;
function loadJSZip() {
  if (jszipPromise) return jszipPromise;
  jszipPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined")
      return reject(
        new Error("PowerPoint parsing requires a browser environment")
      );
    if (window.JSZip) return resolve(window.JSZip);
    const script = document.createElement("script");
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js";
    script.onload = () => resolve(window.JSZip);
    script.onerror = () =>
      reject(new Error("Could not load JSZip — check network connection"));
    document.head.appendChild(script);
  });
  return jszipPromise;
}

let xlsxPromise = null;
function loadXLSX() {
  if (xlsxPromise) return xlsxPromise;
  xlsxPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined")
      return reject(new Error("Excel parsing requires a browser environment"));
    if (window.XLSX) return resolve(window.XLSX);
    const script = document.createElement("script");
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
    script.onload = () => resolve(window.XLSX);
    script.onerror = () =>
      reject(new Error("Could not load SheetJS — check network connection"));
    document.head.appendChild(script);
  });
  return xlsxPromise;
}

// ----- PPTX text extraction -------------------------------------------------
// .pptx is a zip archive. Each slide is at ppt/slides/slideN.xml. The text we
// want lives in <a:t> elements (one per run of formatted text). Slides are
// numbered in file order — we sort by the numeric suffix so slide 2 comes
// before slide 10 (string sort would put "slide10" before "slide2").

function decodeXmlEntities(s) {
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'");
}

function extractPptxText(slideXml) {
  // Pull every <a:t>...</a:t> text run. Different paragraphs are separated by
  // <a:p> wrappers; we'll insert a line break when we cross paragraph boundaries
  // so titles and bullets don't all run together on one line.
  const out = [];
  const paraRegex = /<a:p\b[^>]*>([\s\S]*?)<\/a:p>/g;
  let pm;
  while ((pm = paraRegex.exec(slideXml)) !== null) {
    const paraInner = pm[1];
    const runs = [];
    const tRegex = /<a:t\b[^>]*>([\s\S]*?)<\/a:t>/g;
    let tm;
    while ((tm = tRegex.exec(paraInner)) !== null) {
      runs.push(decodeXmlEntities(tm[1]));
    }
    const line = runs.join("").trim();
    if (line) out.push(line);
  }
  return out.join("\n");
}

// ----- Main extraction router ----------------------------------------------
async function extractTextFromFile(file) {
  const name = (file.name || "").toLowerCase();
  const ext = name.slice(name.lastIndexOf("."));
  if (!ACCEPTED_TYPES.includes(ext)) {
    throw new Error(
      `Unsupported file type "${ext}". Accepted: ${ACCEPTED_TYPES.join(", ")}`
    );
  }

  // Plain text / markdown — straight read
  if (ext === ".txt" || ext === ".md" || ext === ".markdown") {
    return await file.text();
  }

  // Word document — Mammoth
  if (ext === ".docx") {
    try {
      const mammoth = await import("mammoth");
      const buf = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer: buf });
      return result.value || "";
    } catch (e) {
      throw new Error("Could not parse Word doc: " + (e.message || e));
    }
  }

  // PDF — pdf.js
  if (ext === ".pdf") {
    try {
      const pdfjs = await loadPdfJs();
      const buf = await file.arrayBuffer();
      const pdf = await pdfjs.getDocument({ data: buf }).promise;
      const pages = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const content = await page.getTextContent();
        const pageText = content.items.map((item) => item.str).join(" ");
        pages.push(pageText);
      }
      return pages.join("\n\n");
    } catch (e) {
      throw new Error("Could not parse PDF: " + (e.message || e));
    }
  }

  // PowerPoint — JSZip + XML parsing
  if (ext === ".pptx") {
    try {
      const JSZip = await loadJSZip();
      const buf = await file.arrayBuffer();
      const zip = await JSZip.loadAsync(buf);
      // Find all slide files. Names look like "ppt/slides/slide1.xml", "ppt/slides/slide2.xml"
      const slideFiles = Object.keys(zip.files)
        .filter((p) => /^ppt\/slides\/slide\d+\.xml$/.test(p))
        .sort((a, b) => {
          const ai = parseInt(a.match(/slide(\d+)/)[1], 10);
          const bi = parseInt(b.match(/slide(\d+)/)[1], 10);
          return ai - bi;
        });
      if (slideFiles.length === 0) {
        throw new Error("No slides found in this PowerPoint file");
      }
      const slides = [];
      // Cap at 100 slides to keep extraction quick — a real deck rarely needs more,
      // and the per-file 6000-char cap on AI context will trim further anyway.
      const cap = Math.min(slideFiles.length, 100);
      for (let i = 0; i < cap; i++) {
        const xml = await zip.files[slideFiles[i]].async("string");
        const text = extractPptxText(xml);
        if (text.trim()) {
          slides.push(text);
        }
      }
      return slides.join("\n\n");
    } catch (e) {
      throw new Error("Could not parse PowerPoint: " + (e.message || e));
    }
  }

  // Excel — SheetJS. Render each sheet as a CSV-style block so the AI can read
  // table relationships. Skip sheets that are empty or only contain blank rows.
  if (ext === ".xlsx") {
    try {
      const XLSX = await loadXLSX();
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheets = [];
      for (const sheetName of wb.SheetNames) {
        const ws = wb.Sheets[sheetName];
        // Use CSV with sensible defaults; blank cells render as empty fields
        const csv = XLSX.utils.sheet_to_csv(ws, {
          blankrows: false,
          skipHidden: true,
        });
        if (csv && csv.trim()) {
          sheets.push(`=== Sheet: ${sheetName} ===\n${csv}`);
        }
      }
      if (sheets.length === 0) {
        throw new Error("No data found in this Excel file");
      }
      return sheets.join("\n\n");
    } catch (e) {
      throw new Error("Could not parse Excel: " + (e.message || e));
    }
  }

  throw new Error("Unreachable: unsupported file type slipped past the gate");
}

// Format the client context into a short snippet suitable for inclusion in an AI prompt.
// We cap each file at ~6000 chars (≈1500 tokens) and the overall block at ~24000 chars
// so the model has room to think without us blowing the context window.
function formatClientContextForPrompt(clientContext) {
  if (!Array.isArray(clientContext) || clientContext.length === 0) return "";
  const PER_FILE_CAP = 6000;
  const TOTAL_CAP = 24000;
  let used = 0;
  const blocks = [];
  for (const file of clientContext) {
    if (used >= TOTAL_CAP) break;
    const remaining = TOTAL_CAP - used;
    const cap = Math.min(PER_FILE_CAP, remaining);
    const text = (file.text || "").slice(0, cap);
    if (!text.trim()) continue;
    blocks.push(
      `--- BEGIN CLIENT FILE: ${file.filename} ---\n${text}\n--- END CLIENT FILE: ${file.filename} ---`
    );
    used += text.length + 100; // a small overhead for the markers
  }
  if (blocks.length === 0) return "";
  return `\n\nCLIENT BACKGROUND MATERIALS (the consultant uploaded these for context — use them when relevant, cite them by filename if you reference specific facts):\n\n${blocks.join(
    "\n\n"
  )}\n`;
}

// ============================================================================
// AI HELPER — calls Claude API for drafts, structured JSON output
// ============================================================================

// Robustly extract a JSON object/array from a model response that may include
// stray markdown fences, preamble text, or trailing commentary.
function extractJson(raw) {
  if (!raw || typeof raw !== "string") throw new Error("Empty AI response");
  // Strip markdown code fences if present
  let s = raw
    .replace(/```(?:json|javascript|js)?\s*/gi, "")
    .replace(/```/g, "")
    .trim();
  // Try direct parse first (the common case when Claude follows instructions)
  try {
    return JSON.parse(s);
  } catch (_) {
    /* fall through */
  }
  // Find the first balanced JSON object or array via brace scanning
  const startIdx = s.search(/[{\[]/);
  if (startIdx === -1) throw new Error("No JSON object found in AI response");
  const opener = s[startIdx];
  const closer = opener === "{" ? "}" : "]";
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = startIdx; i < s.length; i++) {
    const ch = s[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (ch === "\\") {
      escape = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (ch === opener) depth++;
    else if (ch === closer) {
      depth--;
      if (depth === 0) {
        const candidate = s.slice(startIdx, i + 1);
        return JSON.parse(candidate);
      }
    }
  }
  throw new Error("Unbalanced JSON in AI response");
}

async function aiDraft(systemPrompt, userPrompt) {
  let response;
  try {
    response = await fetch("/api/ai/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 2500,
        system:
          systemPrompt +
          "\n\nRespond ONLY with valid JSON. No preamble, no markdown code fences, no commentary.",
        messages: [{ role: "user", content: userPrompt }],
      }),
    });
  } catch (e) {
    throw new Error(`Network error reaching the AI service: ${e.message}`);
  }
  if (!response.ok) {
    let body = "";
    try {
      body = await response.text();
    } catch (_) {}
    throw new Error(
      `AI request failed (${response.status}): ${
        body.slice(0, 200) || response.statusText
      }`
    );
  }
  const data = await response.json();
  const text = (data.content || [])
    .map((b) => (b.type === "text" ? b.text : ""))
    .filter(Boolean)
    .join("\n");
  if (!text) throw new Error("AI returned no text content");
  try {
    return extractJson(text);
  } catch (e) {
    throw new Error(
      `Could not parse AI response as JSON: ${
        e.message
      }. First 200 chars of response: ${text.slice(0, 200)}`
    );
  }
}

// =============================================================================
// AI EXTRACTOR — parse a client document into proposed entities for review
// =============================================================================
// Given the raw text of a document (PowerPoint, Word, PDF, etc) and the current
// project state, ask Claude to identify and classify content into four buckets:
//   - changes (top-level workstreams or themes)
//   - audiences (impacted populations with details)
//   - stakeholders (named individuals + role/influence)
//   - interventions (mitigations, risks-to-address, planned activities)
//
// The model returns a proposal object; the consultant reviews each item before
// it actually lands in the project. This is intentionally a separate call from
// aiDraft because it needs much more output tokens and a richer system prompt.

async function aiExtractFromDocument(
  documentText,
  documentName,
  existingState
) {
  // Cap the document text to keep within model token limits. The Allstate deck
  // we tested with is about 12k chars when extracted — plenty of headroom here.
  const docCap = 50000;
  const docExcerpt = (documentText || "").slice(0, docCap);
  if (!docExcerpt.trim()) {
    throw new Error("Document has no text to extract");
  }

  // Summarise what already exists so the model can flag duplicates.
  const existing = {
    changes: (existingState.changes || []).map((c) => c.name),
    audiences: (existingState.audiences || []).map((a) => a.name),
    stakeholders: (existingState.stakeholders || []).map((s) => s.name),
    interventions: (existingState.interventions || []).map((i) => i.name),
  };

  const systemPrompt = `You are a WTW change management consultant assistant. Your job is to read a client document and extract structured entities for a change management project.

You will identify four types of entities:

1. CHANGES — Top-level change themes, workstreams, transformation initiatives, or programs. These are the big buckets the project organizes around (e.g., "Leadership Reorganization", "Engineering Platform Consolidation", "Readiness", "Activation").

2. AUDIENCES — Specific populations impacted by the change. Could be roles (e.g., "Learning Advisors"), levels (e.g., "VPs of Learning"), functions (e.g., "Engineering Managers"), or named groups (e.g., "All Employees"). For each, identify what specifically changes for them.

3. STAKEHOLDERS — Named individuals or specific roles called out in the document (e.g., "Lena Juliano, Director GLD", "CHRO Andrea Carter"). These are people whose engagement matters to the change, not categories of employees.

4. INTERVENTIONS — Planned actions, mitigations to risks, activities in the work plan, or specific behaviors to drive. These are things being done about the change, not the change itself.

Be conservative. If something doesn't clearly fit a category, leave it out. Quality over coverage.

For each extracted entity, include a 'sourceQuote': a short verbatim phrase (≤120 chars) from the document that justifies the extraction. This is critical — it lets the consultant verify the AI's reasoning.

Respond ONLY with valid JSON. No preamble, no markdown code fences, no commentary.`;

  const userPrompt = `Document name: ${documentName}

Document text:
${docExcerpt}

Already in this project (so you can flag possible duplicates):
${JSON.stringify(existing, null, 2)}

Extract entities into this JSON structure:
{
  "changes": [
    { "name": "Short change name (4-8 words)", "description": "1-2 sentence description", "isKey": true|false, "sourceQuote": "short verbatim phrase from doc" }
  ],
  "audiences": [
    { "name": "Audience name", "impactDescription": "What changes for this audience specifically (1-2 sentences).", "suggestedChange": "Name of one of the changes above (or existing change), if obvious", "sourceQuote": "..." }
  ],
  "stakeholders": [
    { "name": "Person's name or specific role", "role": "Their title/position", "abilityToImpact": "high"|"medium"|"low", "sourceQuote": "..." }
  ],
  "interventions": [
    { "name": "Short intervention name", "description": "What this intervention does", "suggestedAudience": "Audience name if obvious, or empty", "sourceQuote": "..." }
  ]
}

Cap each category at 20 items. If an entity looks similar to one already in the project (by name), still include it — the app will flag it as a possible duplicate for the consultant to merge.`;

  // Bigger token budget than drafting because the output has many items.
  let response;
  try {
    response = await fetch("/api/ai/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 8000,
        system: systemPrompt,
        messages: [{ role: "user", content: userPrompt }],
      }),
    });
  } catch (e) {
    throw new Error(`Network error reaching the AI service: ${e.message}`);
  }
  if (!response.ok) {
    let body = "";
    try {
      body = await response.text();
    } catch (_) {}
    throw new Error(
      `AI extraction failed (${response.status}): ${
        body.slice(0, 200) || response.statusText
      }`
    );
  }
  const data = await response.json();
  const text = (data.content || [])
    .map((b) => (b.type === "text" ? b.text : ""))
    .filter(Boolean)
    .join("\n");
  if (!text) throw new Error("AI returned no text content");

  let parsed;
  try {
    parsed = extractJson(text);
  } catch (e) {
    throw new Error(
      `Could not parse AI extraction response: ${
        e.message
      }. First 200 chars: ${text.slice(0, 200)}`
    );
  }

  // Coerce into a safe shape — defensively, since model output isn't guaranteed.
  return {
    changes: Array.isArray(parsed.changes) ? parsed.changes : [],
    audiences: Array.isArray(parsed.audiences) ? parsed.audiences : [],
    stakeholders: Array.isArray(parsed.stakeholders) ? parsed.stakeholders : [],
    interventions: Array.isArray(parsed.interventions)
      ? parsed.interventions
      : [],
  };
}

// Detect potential duplicates by name similarity. Returns the existing entity's id
// if a likely match exists, else null. Uses normalized lowercased comparison plus
// a simple Jaccard-ish word overlap for short fuzzy matches.
function findPossibleDuplicate(proposedName, existingItems) {
  if (
    !proposedName ||
    !Array.isArray(existingItems) ||
    existingItems.length === 0
  )
    return null;
  const norm = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, " ");
  const target = norm(proposedName);
  if (!target) return null;

  // Exact match (case-insensitive)
  for (const item of existingItems) {
    if (norm(item.name) === target) return item.id;
  }

  // Word-overlap match — if 80%+ of words from the shorter name appear in the longer
  const targetWords = new Set(target.split(" ").filter((w) => w.length > 2));
  if (targetWords.size === 0) return null;
  for (const item of existingItems) {
    const existingWords = new Set(
      norm(item.name)
        .split(" ")
        .filter((w) => w.length > 2)
    );
    if (existingWords.size === 0) continue;
    const small =
      targetWords.size <= existingWords.size ? targetWords : existingWords;
    const big =
      targetWords.size <= existingWords.size ? existingWords : targetWords;
    let overlap = 0;
    small.forEach((w) => {
      if (big.has(w)) overlap++;
    });
    if (overlap / small.size >= 0.8 && small.size >= 2) return item.id;
  }
  return null;
}

// =============================================================================
// INTERVENTION CADENCE
// =============================================================================
// Interventions come in three rhythms:
//   - 'one_time'   → a single span from startDate to endDate (the default).
//                    Renders as a bar.
//   - 'recurring'  → repeats on a fixed interval ('weekly', 'biweekly',
//                    'monthly', or 'quarterly') across startDate → endDate.
//                    Renders as a series of dots at each occurrence.
//   - 'milestones' → discrete moments. Each date listed in `milestoneDates`
//                    renders as a dot. startDate/endDate still define the
//                    visible span (for filtering, sorting, etc.) but the
//                    activity itself happens only at those specific dates.
//
// The helper below returns a normalized array of { date, kind } points the
// renderer can iterate over. kind === 'span' means render-as-bar from
// `date` to the next `endDate`; kind === 'point' means render-as-dot at
// `date`. One-time interventions return a single 'span' point.

function getInterventionRenderPoints(iv) {
  const cadence = iv?.cadence || "one_time";
  const parseDate = (d) => {
    if (!d) return null;
    const t = new Date(d);
    return isNaN(t.getTime()) ? null : t;
  };
  const start = parseDate(iv?.startDate);
  const end = parseDate(iv?.endDate);

  // Default fallback for any malformed cadence value
  if (cadence === "milestones") {
    const dates = Array.isArray(iv.milestoneDates) ? iv.milestoneDates : [];
    return dates
      .map((d) => ({ date: parseDate(d), kind: "point" }))
      .filter((p) => p.date !== null);
  }

  if (cadence === "recurring") {
    if (!start || !end) return [];
    const interval = iv.recurrenceInterval || "monthly";
    const stepMs =
      {
        weekly: 7 * 24 * 60 * 60 * 1000,
        biweekly: 14 * 24 * 60 * 60 * 1000,
        monthly: 30 * 24 * 60 * 60 * 1000, // approximation; fine for visual spacing
        quarterly: 91 * 24 * 60 * 60 * 1000,
      }[interval] || 30 * 24 * 60 * 60 * 1000;
    const points = [];
    // Cap at 200 points as a safety guard against pathological inputs
    let cur = start.getTime();
    let safety = 0;
    while (cur <= end.getTime() && safety < 200) {
      points.push({ date: new Date(cur), kind: "point" });
      cur += stepMs;
      safety++;
    }
    return points;
  }

  // one_time (default)
  if (!start || !end) return [];
  return [{ date: start, endDate: end, kind: "span" }];
}

// Human-readable label for the cadence — used in compact list views.
function describeCadence(iv) {
  const cadence = iv?.cadence || "one_time";
  if (cadence === "milestones") {
    const count = (iv.milestoneDates || []).length;
    return count > 0 ? `${count} milestone${count === 1 ? "" : "s"}` : "";
  }
  if (cadence === "recurring") {
    const interval = iv.recurrenceInterval || "monthly";
    const label =
      {
        weekly: "Weekly",
        biweekly: "Bi-weekly",
        monthly: "Monthly",
        quarterly: "Quarterly",
      }[interval] || "Recurring";
    return label;
  }
  return ""; // one-time interventions show nothing extra
}

// Safely coerce an AI-returned value to a string array. If the model returns a string,
// wrap it; if undefined/null, return []; if it's already an array, ensure every item is a string.
function asStringArray(value) {
  if (Array.isArray(value))
    return value.map((v) => (typeof v === "string" ? v : String(v ?? "")));
  if (value === null || value === undefined) return [];
  if (typeof value === "string") return value ? [value] : [];
  if (typeof value === "object")
    return Object.values(value).map((v) =>
      typeof v === "string" ? v : String(v ?? "")
    );
  return [String(value)];
}
function asString(value, max) {
  if (value === null || value === undefined) return "";
  const s = typeof value === "string" ? value : String(value);
  return max ? s.slice(0, max) : s;
}

// Build a context object describing an audience to feed an AI prompt
function audienceContext(state, audience) {
  const cti = state.changesToImplement.find(
    (c) => c.id === audience.changeToImplementId
  );
  const change = state.changes.find((c) => c.id === cti?.changeId);
  return {
    project: state.project.name,
    projectDescription: state.project.description,
    change: change
      ? {
          name: change.name,
          description: change.description,
          isKey: change.isKey,
        }
      : null,
    changeToImplement: cti
      ? {
          name: cti.name,
          description: cti.description,
          from: cti.from,
          to: cti.to,
        }
      : null,
    audience: {
      name: audience.name,
      audienceType: audience.audienceType,
      role: lookupLabel(state, "roles", audience.roleId),
      function: lookupLabel(state, "functions", audience.functionId),
      level: lookupLabel(state, "levels", audience.levelId),
      country: audience.country,
      region: audience.region,
      size: lookupLabel(state, "audienceSizes", audience.audienceSizeId),
      persona: lookupLabel(state, "personas", audience.personaId),
      severity: lookupLabel(state, "impactLevels", audience.impactLevelId),
      effort: lookupLabel(state, "effortLevels", audience.effortLevelId),
      impactTypes: (audience.impactTypeIds || []).map((id) =>
        lookupLabel(state, "impactTypes", id)
      ),
      currentImpactDescription: audience.impactDescription || null,
      currentReactions: (audience.reactionIds || []).map((id) =>
        lookupLabel(state, "reactionTypes", id)
      ),
    },
  };
}

// ============================================================================
// COMPONENTS
// ============================================================================

// Methodology phase definitions — drives navigation and color coding
// Phases are ordered as a natural left-to-right workflow:
//   1. Set up    — project info, client docs, terminology
//   2. Assess    — change readiness & sizing
//   3. Impact    — changes, audiences, stakeholders, insights
//   4. Plan      — interventions & timeline
//   5. Deliver   — status tracking & exec summary
//
// IDs are stable identifiers used by phaseForView(); labels are what the user sees.
const PHASES = [
  {
    id: "setup",
    label: "Set up",
    sub: "Project info & client context",
    color: "#6D28D9", // deep purple — primary brand, signals "start here"
    bg: "#F5F3FF",
    tools: [
      { id: "settings", label: "Project & Terminology", icon: Settings },
      { id: "review", label: "AI Extraction Review", icon: Sparkles },
    ],
  },
  {
    id: "assess",
    label: "Assess",
    sub: "Readiness & sizing",
    color: "#0369A1", // sky blue
    bg: "#F0F9FF",
    tools: [
      { id: "cra", label: "Change Readiness Assessment", icon: AlertCircle },
      { id: "sizing", label: "Sizing & Impact", icon: TrendingUp },
    ],
  },
  {
    id: "impact",
    label: "Impact",
    sub: "Changes, audiences, stakeholders",
    color: "#C2410C", // burnt orange
    bg: "#FFF7ED",
    tools: [
      { id: "editor", label: "Change Impact", icon: GitBranch },
      { id: "stakeholders", label: "Stakeholders", icon: Users },
      { id: "timeline", label: "Audience Timeline", icon: Clock },
      { id: "dashboard", label: "Insights", icon: LayoutDashboard },
    ],
  },
  {
    id: "plan",
    label: "Plan",
    sub: "Interventions & timeline",
    color: "#0F766E", // teal
    bg: "#F0FDFA",
    tools: [{ id: "cmplan", label: "Change Mgmt Plan", icon: Calendar }],
  },
  {
    id: "deliver",
    label: "Deliver",
    sub: "Status & exec summary",
    color: "#047857", // emerald
    bg: "#ECFDF5",
    tools: [
      { id: "interventions", label: "Intervention Status", icon: TrendingUp },
      { id: "execsummary", label: "Exec Summary", icon: Sparkles },
    ],
  },
];

// Lookup which phase a view belongs to. Defaults to the first phase (Set up)
// for unknown views so the header always renders something sensible.
function phaseForView(viewId) {
  return PHASES.find((p) => p.tools.some((t) => t.id === viewId)) || PHASES[0];
}

function Header({
  state,
  view,
  setView,
  role,
  setRole,
  useClientTerms,
  setUseClientTerms,
  onReset,
  onOpenContext,
}) {
  const activePhase = phaseForView(view);
  const contextCount = (state.clientContext || []).length;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const isConsultant = role === "consultant";

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  return (
    <header
      className="bg-white"
      style={{ boxShadow: tokens.shadow.sm, position: "relative", zIndex: 10 }}
    >
      {/* Row 1: brand + phase ribbon + controls */}
      <div className="px-6 py-3 flex items-center gap-6">
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center font-bold text-white"
            style={{
              background: `linear-gradient(135deg, ${tokens.primary} 0%, ${tokens.primaryDark} 100%)`,
              boxShadow: tokens.shadow.sm,
              fontFamily: "Georgia, serif",
              fontSize: "15px",
              letterSpacing: "-0.02em",
            }}
          >
            cm
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-semibold">
              WTW · Change Management Platform
            </div>
            <div className="text-sm font-semibold text-stone-900 leading-tight mt-0.5">
              {state.project.name || "Untitled project"}
            </div>
          </div>
        </div>

        {/* Phase ribbon — refined active state with subtle elevation */}
        <nav
          className="flex items-stretch ml-4 rounded-lg overflow-hidden"
          style={{
            border: "1px solid rgb(229, 231, 235)",
            boxShadow: tokens.shadow.xs,
          }}
        >
          {PHASES.map((p, i) => {
            const isActive = activePhase.id === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setView(p.tools[0].id)}
                title={p.sub}
                className="px-3.5 py-2 text-left flex items-center gap-1.5"
                style={{
                  background: isActive ? p.bg : "white",
                  color: isActive ? p.color : "#6b7280",
                  borderLeft: i > 0 ? "1px solid rgb(229, 231, 235)" : "none",
                  transition: tokens.transition.base,
                  fontSize: "12px",
                  fontWeight: 600,
                  lineHeight: 1,
                  letterSpacing: "0.02em",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.background = "#FAFAF9";
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.background = "white";
                }}
              >
                {/* Inline number — part of the label, not a separate badge */}
                <span
                  style={{
                    color: isActive ? p.color : "#A8A29E",
                    fontWeight: 700,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {i + 1}
                </span>
                <span
                  style={{
                    color: isActive ? p.color : "#A8A29E",
                    opacity: 0.5,
                    margin: "0 1px",
                  }}
                >
                  ·
                </span>
                <span className="uppercase">{p.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2.5">
          <button
            onClick={onOpenContext}
            title={
              contextCount > 0
                ? `${contextCount} client context file${
                    contextCount === 1 ? "" : "s"
                  } loaded`
                : "Drop client background materials here"
            }
            className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md border ${
              contextCount > 0
                ? "text-purple-900"
                : "text-stone-600 hover:text-stone-900"
            }`}
            style={{
              background: contextCount > 0 ? tokens.primaryTint : "white",
              borderColor: contextCount > 0 ? "#D8C9EC" : "rgb(214, 211, 209)",
              transition: tokens.transition.base,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background =
                contextCount > 0 ? tokens.primaryTintHover : "#FAFAF9";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background =
                contextCount > 0 ? tokens.primaryTint : "white";
            }}
          >
            <Upload size={12} />
            <span>Context</span>
            {contextCount > 0 && (
              <span
                className="text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none"
                style={{
                  background: tokens.primary,
                  minWidth: 18,
                  textAlign: "center",
                }}
              >
                {contextCount}
              </span>
            )}
          </button>

          <div className="h-5 w-px bg-stone-200" />

          <label className="flex items-center gap-1.5 text-xs text-stone-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={useClientTerms}
              onChange={(e) => setUseClientTerms(e.target.checked)}
              className="accent-purple-700 rounded"
            />
            <span>Client terms</span>
          </label>

          <div className="relative">
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="appearance-none text-stone-800 text-xs font-medium pl-3 pr-8 py-1.5 rounded-md cursor-pointer"
              style={{
                background: "#F5F4F2",
                border: "1px solid rgb(229, 231, 235)",
                transition: tokens.transition.base,
              }}
            >
              <option value="consultant">WTW Consultant</option>
              <option value="client_lead">Client Lead</option>
              <option value="client_viewer">Client Viewer</option>
            </select>
            <ChevronDown
              size={12}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none"
            />
          </div>

          {/* Overflow menu — consultant-only utilities live in here so the header stays clean */}
          {isConsultant && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((p) => !p)}
                className="w-8 h-8 rounded-md flex items-center justify-center text-stone-500"
                style={{
                  background: menuOpen ? "#F5F4F2" : "transparent",
                  transition: tokens.transition.base,
                }}
                onMouseEnter={(e) => {
                  if (!menuOpen) e.currentTarget.style.background = "#F5F4F2";
                }}
                onMouseLeave={(e) => {
                  if (!menuOpen)
                    e.currentTarget.style.background = "transparent";
                }}
                title="More"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="currentColor"
                >
                  <circle cx="3" cy="7" r="1.3" />
                  <circle cx="7" cy="7" r="1.3" />
                  <circle cx="11" cy="7" r="1.3" />
                </svg>
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-52 rounded-lg overflow-hidden bg-white"
                  style={{
                    boxShadow: tokens.shadow.lg,
                    border: "1px solid rgb(229, 231, 235)",
                    zIndex: 50,
                  }}
                >
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      onReset();
                    }}
                    className="w-full text-left px-3 py-2.5 text-xs text-stone-700 hover:bg-red-50 hover:text-red-700 flex items-center gap-2"
                    style={{ transition: tokens.transition.fast }}
                  >
                    <Trash2 size={12} />
                    <div>
                      <div className="font-medium">Reset project</div>
                      <div className="text-[10px] text-stone-500 mt-0.5">
                        Erase all data and start fresh
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Row 2: tools within the active phase */}
      <div
        className="px-6 py-2 flex items-center gap-1"
        style={{
          background: activePhase.bg,
          borderTop: "1px solid rgba(0,0,0,0.04)",
        }}
      >
        <div
          className="text-[10px] uppercase tracking-[0.18em] font-semibold mr-3"
          style={{ color: activePhase.color, opacity: 0.85 }}
        >
          {activePhase.label}
        </div>
        {activePhase.tools.map((t) => {
          const active = view === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setView(t.id)}
              className="px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-2"
              style={{
                background: active ? activePhase.color : "transparent",
                color: active ? "white" : activePhase.color,
                transition: tokens.transition.base,
                boxShadow: active ? tokens.shadow.sm : "none",
              }}
              onMouseEnter={(e) => {
                if (!active)
                  e.currentTarget.style.background = "rgba(255,255,255,0.5)";
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = "transparent";
              }}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>
    </header>
  );
}

function HierarchyTree({
  state,
  selectedId,
  onSelect,
  onAddChange,
  onAddCTI,
  onAddAudience,
  role,
}) {
  const [expanded, setExpanded] = useState(() => {
    const ex = {};
    state.changes.forEach((c) => (ex[c.id] = true));
    state.changesToImplement.forEach((c) => (ex[c.id] = true));
    return ex;
  });
  const canEdit = role !== "client_viewer";

  function toggle(id) {
    setExpanded((p) => ({ ...p, [id]: !p[id] }));
  }

  return (
    <div className="text-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-stone-500">
          Change Hierarchy
        </div>
        {canEdit && (
          <button
            onClick={onAddChange}
            className="text-xs flex items-center gap-1 font-medium px-2 py-1 rounded-md"
            style={{
              color: tokens.primary,
              transition: tokens.transition.fast,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = tokens.primaryTint;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <Plus size={12} /> Change
          </button>
        )}
      </div>

      <div className="space-y-0.5">
        {state.changes.length === 0 && (
          <div
            className="rounded-lg p-5 text-center"
            style={{
              border: "1.5px dashed #D6D3D1",
              background: tokens.surface.panelMuted,
            }}
          >
            <div
              className="w-9 h-9 rounded-full mx-auto mb-3 flex items-center justify-center"
              style={{ background: "white", boxShadow: tokens.shadow.xs }}
            >
              <Plus size={16} className="text-stone-400" />
            </div>
            <div className="text-[11.5px] text-stone-600 mb-3 leading-relaxed">
              No changes yet. Start by adding the
              <br />
              top-level change themes for this project.
            </div>
            {canEdit && (
              <button
                onClick={onAddChange}
                className="text-xs text-white px-3.5 py-2 rounded-md font-medium flex items-center gap-1.5 mx-auto"
                style={{
                  background: tokens.primary,
                  boxShadow: tokens.shadow.sm,
                  transition: tokens.transition.base,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = tokens.primaryDark;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = tokens.primary;
                }}
              >
                <Plus size={12} /> Add first change
              </button>
            )}
          </div>
        )}
        {state.changes.map((change) => {
          const ctis = state.changesToImplement.filter(
            (c) => c.changeId === change.id
          );
          const isExpanded = expanded[change.id];
          const isSelected = selectedId === change.id;
          return (
            <div key={change.id}>
              <div
                className={`group flex items-center gap-1 px-2 py-1.5 rounded cursor-pointer ${
                  isSelected
                    ? "bg-purple-50 text-purple-900"
                    : "hover:bg-stone-50"
                }`}
                onClick={() => onSelect(change.id, "change")}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(change.id);
                  }}
                  className="p-0.5 -ml-1"
                >
                  {isExpanded ? (
                    <ChevronDown size={13} />
                  ) : (
                    <ChevronRight size={13} />
                  )}
                </button>
                {change.isKey && (
                  <span className="w-1 h-1 rounded-full bg-amber-500" />
                )}
                <span className="font-medium truncate flex-1">
                  {change.name}
                </span>
                <span className="text-[10px] text-stone-400">
                  {ctis.length}
                </span>
              </div>

              {isExpanded &&
                ctis.map((cti) => {
                  const auds = state.audiences.filter(
                    (a) => a.changeToImplementId === cti.id
                  );
                  const ctiExpanded = expanded[cti.id];
                  const ctiSelected = selectedId === cti.id;
                  return (
                    <div key={cti.id} className="ml-4">
                      <div
                        className={`group flex items-center gap-1 px-2 py-1.5 rounded cursor-pointer text-[13px] ${
                          ctiSelected
                            ? "bg-orange-50 text-orange-900"
                            : "hover:bg-stone-50 text-stone-700"
                        }`}
                        onClick={() => onSelect(cti.id, "cti")}
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggle(cti.id);
                          }}
                          className="p-0.5 -ml-1"
                        >
                          {ctiExpanded ? (
                            <ChevronDown size={12} />
                          ) : (
                            <ChevronRight size={12} />
                          )}
                        </button>
                        <span className="truncate flex-1">{cti.name}</span>
                        <span className="text-[10px] text-stone-400">
                          {auds.length}
                        </span>
                      </div>
                      {ctiExpanded && (
                        <div className="ml-4 border-l border-stone-200 pl-2">
                          {auds.map((aud) => {
                            const audSelected = selectedId === aud.id;
                            return (
                              <div
                                key={aud.id}
                                onClick={() => onSelect(aud.id, "audience")}
                                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-[12px] ${
                                  audSelected ? "text-white" : "text-stone-600"
                                }`}
                                style={{
                                  background: audSelected
                                    ? tokens.primary
                                    : "transparent",
                                  boxShadow: audSelected
                                    ? tokens.shadow.sm
                                    : "none",
                                  transition: tokens.transition.fast,
                                }}
                                onMouseEnter={(e) => {
                                  if (!audSelected)
                                    e.currentTarget.style.background =
                                      tokens.surface.panelMuted;
                                }}
                                onMouseLeave={(e) => {
                                  if (!audSelected)
                                    e.currentTarget.style.background =
                                      "transparent";
                                }}
                              >
                                <span
                                  className="w-2 h-2 rounded-sm"
                                  style={{
                                    background: impactColor(aud.impactLevelId),
                                  }}
                                />
                                <span className="truncate flex-1">
                                  {aud.name}
                                </span>
                              </div>
                            );
                          })}
                          {canEdit && (
                            <button
                              onClick={() => onAddAudience(cti.id)}
                              className="flex items-center gap-1 text-[11px] text-stone-400 hover:text-purple-700 px-2 py-1"
                            >
                              <Plus size={10} /> audience
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              {isExpanded && canEdit && (
                <button
                  onClick={() => onAddCTI(change.id)}
                  className="ml-6 flex items-center gap-1 text-[11px] text-stone-400 hover:text-purple-700 px-2 py-1"
                >
                  <Plus size={10} /> change to implement
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-4 border-t border-stone-200">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-stone-500 mb-2">
          Quick stats
        </div>
        <div className="space-y-1 text-xs text-stone-600">
          <div className="flex justify-between">
            <span>Changes</span>
            <span className="font-medium text-stone-900">
              {state.changes.length}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Key changes</span>
            <span className="font-medium text-stone-900">
              {state.changes.filter((c) => c.isKey).length}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Changes to implement</span>
            <span className="font-medium text-stone-900">
              {state.changesToImplement.length}
            </span>
          </div>
          <div className="flex justify-between">
            <span>Audiences impacted</span>
            <span className="font-medium text-stone-900">
              {state.audiences.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ state, setView }) {
  const hasNoData = state && state.changes && state.changes.length === 0;
  const hasNoProject =
    state &&
    (!state.project?.name || state.project.name === "New project") &&
    !state.project?.client;
  const hasNoContext =
    state && (!state.clientContext || state.clientContext.length === 0);

  if (hasNoData) {
    const steps = [
      {
        done: !hasNoProject,
        num: 1,
        title: "Set up the project",
        desc: "Name, client, dates, description. The AI uses this on every prompt.",
        actionLabel: "Project setup",
        actionView: "settings",
      },
      {
        done: !hasNoContext,
        num: 2,
        title: "Drop in client background",
        desc: 'Strategy docs, briefings, RFPs — the AI uses them on every draft. Click "Context" in the header from any view.',
        actionLabel: hasNoContext ? "Open Context drawer" : null,
        actionView: "settings",
      },
      {
        done: false,
        num: 3,
        title: "Add your first change",
        desc: "Build out the change hierarchy, then audiences, stakeholders, and your CM plan.",
        actionLabel: "Open Change Impact",
        actionView: "editor",
      },
    ];
    const completedCount = steps.filter((s) => s.done).length;

    return (
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-2xl">
          {/* Hero card */}
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              background: "white",
              boxShadow: tokens.shadow.lg,
              border: "1px solid rgb(229, 231, 235)",
            }}
          >
            {/* Top banner with subtle gradient */}
            <div
              className="px-8 py-8 text-white relative overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${tokens.primary} 0%, ${tokens.primaryDark} 100%)`,
              }}
            >
              {/* Decorative circle pattern */}
              <div
                className="absolute -right-12 -top-12 w-48 h-48 rounded-full"
                style={{ background: "rgba(255,255,255,0.06)" }}
              />
              <div
                className="absolute -right-4 top-8 w-24 h-24 rounded-full"
                style={{ background: "rgba(255,255,255,0.04)" }}
              />
              <div className="relative">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] opacity-80 font-semibold mb-2">
                  <Sparkles size={12} />
                  <span>Get started</span>
                </div>
                <h1
                  className="text-3xl font-semibold mb-2 leading-tight"
                  style={{
                    fontFamily: "Georgia, serif",
                    letterSpacing: "-0.01em",
                  }}
                >
                  Welcome — let's set up this engagement
                </h1>
                <p className="text-sm opacity-90 max-w-md leading-relaxed">
                  Three quick steps and the AI-powered change management
                  workspace will be ready for your client work.
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div
              className="px-8 pt-5 pb-3 flex items-center gap-3"
              style={{ background: tokens.surface.panelMuted }}
            >
              <div
                className="flex-1 h-1.5 rounded-full overflow-hidden"
                style={{ background: "#E7E5E4" }}
              >
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${(completedCount / steps.length) * 100}%`,
                    background: tokens.primary,
                    transition: tokens.transition.base,
                  }}
                />
              </div>
              <div className="text-[11px] font-semibold text-stone-600 tabular-nums">
                {completedCount}/{steps.length} complete
              </div>
            </div>

            {/* Steps */}
            <div className="px-8 py-6 space-y-1">
              {steps.map((s, i) => (
                <div
                  key={s.num}
                  className="flex items-start gap-4 py-3 rounded-lg px-3 -mx-3"
                  style={{ transition: tokens.transition.base }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      tokens.surface.panelMuted;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{
                      background: s.done ? "#D1FAE5" : tokens.primaryTint,
                      color: s.done ? "#047857" : tokens.primary,
                      boxShadow: tokens.shadow.xs,
                    }}
                  >
                    {s.done ? <Check size={14} strokeWidth={3} /> : s.num}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-stone-900">
                      {s.title}
                    </div>
                    <div className="text-[12.5px] text-stone-600 mt-0.5 leading-relaxed">
                      {s.desc}
                    </div>
                  </div>
                  {setView && s.actionLabel && !s.done && (
                    <button
                      onClick={() => setView(s.actionView)}
                      className="flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-md"
                      style={{
                        background:
                          i === completedCount ? tokens.primary : "white",
                        color: i === completedCount ? "white" : tokens.primary,
                        border: `1px solid ${tokens.primary}`,
                        transition: tokens.transition.base,
                      }}
                      onMouseEnter={(e) => {
                        if (i === completedCount)
                          e.currentTarget.style.background = tokens.primaryDark;
                        else
                          e.currentTarget.style.background = tokens.primaryTint;
                      }}
                      onMouseLeave={(e) => {
                        if (i === completedCount)
                          e.currentTarget.style.background = tokens.primary;
                        else e.currentTarget.style.background = "white";
                      }}
                    >
                      {s.actionLabel} →
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Quiet footer hint */}
          <div className="text-center mt-6 text-[11px] text-stone-500">
            Project data is saved locally to this browser. Use the Reset option
            in the header menu to start a new engagement.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex items-center justify-center text-stone-400">
      <div className="text-center max-w-sm px-6">
        <div
          className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
          style={{ background: tokens.surface.panelMuted }}
        >
          <Sparkles className="text-stone-400" size={22} />
        </div>
        <div className="text-sm text-stone-600">
          Select a change, change to implement, or audience from the left to
          begin editing.
        </div>
      </div>
    </div>
  );
}

function ChangeEditor({ change, state, update, remove, role }) {
  const canEdit = role !== "client_viewer";
  const ctis = state.changesToImplement.filter((c) => c.changeId === change.id);
  const audiences = state.audiences.filter((a) =>
    ctis.some((c) => c.id === a.changeToImplementId)
  );

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="text-[10px] uppercase tracking-[0.18em] text-purple-700 font-semibold mb-2">
        Change
      </div>
      <input
        disabled={!canEdit}
        value={change.name}
        onChange={(e) => update({ ...change, name: e.target.value })}
        className="text-2xl font-semibold text-stone-900 bg-transparent w-full focus:outline-none focus:bg-stone-50 px-2 -ml-2 py-1 rounded"
      />

      <div className="mt-4 flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input
            disabled={!canEdit}
            type="checkbox"
            checked={change.isKey}
            onChange={(e) => update({ ...change, isKey: e.target.checked })}
            className="accent-amber-500"
          />
          <span className="text-stone-700">Key change</span>
          {change.isKey && (
            <span className="text-[10px] uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-0.5 rounded">
              flagged
            </span>
          )}
        </label>
      </div>

      <div className="mt-4">
        <div className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
          Description
        </div>
        <textarea
          disabled={!canEdit}
          value={change.description}
          onChange={(e) => update({ ...change, description: e.target.value })}
          rows={3}
          className="w-full text-sm text-stone-800 bg-stone-50 border border-stone-200 rounded p-3 focus:outline-none focus:border-purple-400 resize-none"
        />
      </div>

      <div className="mt-8 grid grid-cols-3 gap-3">
        <div className="border border-stone-200 rounded p-3">
          <div className="text-2xl font-semibold text-stone-900">
            {ctis.length}
          </div>
          <div className="text-[11px] text-stone-500 uppercase tracking-wider">
            Changes to implement
          </div>
        </div>
        <div className="border border-stone-200 rounded p-3">
          <div className="text-2xl font-semibold text-stone-900">
            {audiences.length}
          </div>
          <div className="text-[11px] text-stone-500 uppercase tracking-wider">
            Audiences
          </div>
        </div>
        <div className="border border-stone-200 rounded p-3">
          <div className="text-2xl font-semibold text-stone-900">
            {audiences
              .reduce((s, a) => {
                const size = state.taxonomies.audienceSizes.find(
                  (x) => x.id === a.audienceSizeId
                );
                return s + (size?.value || 0);
              }, 0)
              .toLocaleString()}
          </div>
          <div className="text-[11px] text-stone-500 uppercase tracking-wider">
            Est. people impacted
          </div>
        </div>
      </div>

      {canEdit && (
        <button
          onClick={remove}
          className="mt-12 text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
        >
          <Trash2 size={12} /> Delete this change
        </button>
      )}
    </div>
  );
}

function CTIEditor({ cti, state, update, remove, role }) {
  const canEdit = role !== "client_viewer";
  const parent = state.changes.find((c) => c.id === cti.changeId);
  const auds = state.audiences.filter((a) => a.changeToImplementId === cti.id);

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="text-[10px] uppercase tracking-[0.18em] text-orange-700 font-semibold mb-2">
        Change to implement ·{" "}
        <span className="text-stone-500">in {parent?.name}</span>
      </div>
      <input
        disabled={!canEdit}
        value={cti.name}
        onChange={(e) => update({ ...cti, name: e.target.value })}
        className="text-2xl font-semibold text-stone-900 bg-transparent w-full focus:outline-none focus:bg-stone-50 px-2 -ml-2 py-1 rounded"
      />

      <div className="mt-4">
        <div className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
          Description
        </div>
        <textarea
          disabled={!canEdit}
          value={cti.description}
          onChange={(e) => update({ ...cti, description: e.target.value })}
          rows={3}
          className="w-full text-sm text-stone-800 bg-stone-50 border border-stone-200 rounded p-3 focus:outline-none focus:border-orange-400 resize-none"
        />
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
            Workstream
          </div>
          <select
            disabled={!canEdit}
            value={cti.workstreamId || ""}
            onChange={(e) => update({ ...cti, workstreamId: e.target.value })}
            className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
          >
            <option value="">—</option>
            {state.taxonomies.workstreams.map((w) => (
              <option key={w.id} value={w.id}>
                {lookupLabel(state, "workstreams", w.id)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div>
          <div className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
            From
          </div>
          <input
            disabled={!canEdit}
            value={cti.from || ""}
            onChange={(e) => update({ ...cti, from: e.target.value })}
            className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
          />
        </div>
        <div>
          <div className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-2">
            To
          </div>
          <input
            disabled={!canEdit}
            value={cti.to || ""}
            onChange={(e) => update({ ...cti, to: e.target.value })}
            className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
          />
        </div>
      </div>

      <div className="mt-8 border border-stone-200 rounded p-4">
        <div className="text-xs font-medium text-stone-500 uppercase tracking-wider mb-3">
          {auds.length} audiences in this change to implement
        </div>
        <div className="space-y-1">
          {auds.map((a) => (
            <div key={a.id} className="flex items-center gap-2 text-sm py-1">
              <span
                className="w-2.5 h-2.5 rounded-sm"
                style={{ background: impactColor(a.impactLevelId) }}
              />
              <span className="text-stone-800">{a.name}</span>
              <span className="text-xs text-stone-400 ml-auto">
                {lookupLabel(state, "audienceSizes", a.audienceSizeId)}
              </span>
            </div>
          ))}
          {auds.length === 0 && (
            <div className="text-sm text-stone-400 italic">
              No audiences yet
            </div>
          )}
        </div>
      </div>

      {canEdit && (
        <button
          onClick={remove}
          className="mt-12 text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
        >
          <Trash2 size={12} /> Delete this change to implement
        </button>
      )}
    </div>
  );
}

function AudienceEditor({ audience, state, update, remove, role }) {
  const canEdit = role !== "client_viewer";
  const cti = state.changesToImplement.find(
    (c) => c.id === audience.changeToImplementId
  );
  const parent = state.changes.find((c) => c.id === cti?.changeId);

  function toggleMulti(field, id) {
    const list = audience[field] || [];
    const next = list.includes(id)
      ? list.filter((x) => x !== id)
      : [...list, id];
    update({ ...audience, [field]: next });
  }

  const recommendedLeverIds = useMemo(() => {
    const recs = new Set();
    const labels = {};
    (audience.reactionIds || []).forEach((rid) => {
      const map = state.reactionLeverMap[rid];
      if (map) {
        recs.add(map.primary);
        recs.add(map.secondary);
        labels[map.primary] = "primary";
        if (!labels[map.secondary] || labels[map.secondary] !== "primary") {
          labels[map.secondary] = labels[map.secondary] || "secondary";
        }
      }
    });
    return { ids: [...recs], labels };
  }, [audience.reactionIds, state.reactionLeverMap]);

  function applyAllRecommended() {
    const next = new Set(audience.leverIds || []);
    recommendedLeverIds.ids.forEach((id) => next.add(id));
    update({ ...audience, leverIds: [...next] });
  }

  return (
    <div className="p-6 max-w-4xl mx-auto pb-20">
      <div className="text-[10px] uppercase tracking-[0.18em] text-stone-700 font-semibold mb-2">
        Audience ·{" "}
        <span className="text-stone-400">
          in {parent?.name} → {cti?.name}
        </span>
      </div>
      <input
        disabled={!canEdit}
        value={audience.name}
        onChange={(e) => update({ ...audience, name: e.target.value })}
        className="text-2xl font-semibold text-stone-900 bg-transparent w-full focus:outline-none focus:bg-stone-50 px-2 -ml-2 py-1 rounded"
      />

      {canEdit && (
        <AIAssistPanel
          key={audience.id}
          audience={audience}
          state={state}
          update={update}
        />
      )}

      <Section title="Who" subtitle="Identity, demographics, and size">
        <Field
          label="Persona template"
          subtitle="Optional starting point from WTW persona library"
        >
          <Picker
            state={state}
            taxonomyKey="personas"
            value={audience.personaId}
            disabled={!canEdit}
            onChange={(id) => update({ ...audience, personaId: id })}
          />
        </Field>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <Field label="Audience type">
            <select
              disabled={!canEdit}
              value={audience.audienceType || "end_user"}
              onChange={(e) =>
                update({ ...audience, audienceType: e.target.value })
              }
              className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
            >
              <option value="end_user">End user (impacted by change)</option>
              <option value="project_user">
                Project user (delivering change)
              </option>
            </select>
          </Field>
          <Field label="Audience size">
            <select
              disabled={!canEdit}
              value={audience.audienceSizeId || ""}
              onChange={(e) =>
                update({ ...audience, audienceSizeId: e.target.value })
              }
              className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
            >
              <option value="">—</option>
              {state.taxonomies.audienceSizes.map((s) => (
                <option key={s.id} value={s.id}>
                  {lookupLabel(state, "audienceSizes", s.id)} (~
                  {s.value.toLocaleString()})
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4">
          <Field label="Role">
            <Picker
              state={state}
              taxonomyKey="roles"
              value={audience.roleId}
              disabled={!canEdit}
              onChange={(id) => update({ ...audience, roleId: id })}
            />
          </Field>
          <Field label="Function">
            <Picker
              state={state}
              taxonomyKey="functions"
              value={audience.functionId}
              disabled={!canEdit}
              onChange={(id) => update({ ...audience, functionId: id })}
            />
          </Field>
          <Field label="Level">
            <Picker
              state={state}
              taxonomyKey="levels"
              value={audience.levelId}
              disabled={!canEdit}
              onChange={(id) => update({ ...audience, levelId: id })}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <Field label="Country">
            <input
              disabled={!canEdit}
              value={audience.country || ""}
              onChange={(e) => update({ ...audience, country: e.target.value })}
              className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
            />
          </Field>
          <Field label="Region">
            <input
              disabled={!canEdit}
              value={audience.region || ""}
              onChange={(e) => update({ ...audience, region: e.target.value })}
              className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
            />
          </Field>
        </div>
      </Section>

      <Section title="What" subtitle="Impact description, types, dates">
        <Field label="Impact description">
          <textarea
            disabled={!canEdit}
            value={audience.impactDescription || ""}
            onChange={(e) =>
              update({ ...audience, impactDescription: e.target.value })
            }
            rows={2}
            className="w-full text-sm border border-stone-200 rounded p-2 bg-white resize-none"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <Field label="Start date">
            <input
              disabled={!canEdit}
              type="date"
              value={audience.startDate || ""}
              onChange={(e) =>
                update({ ...audience, startDate: e.target.value })
              }
              className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
            />
          </Field>
          <Field label="End date">
            <input
              disabled={!canEdit}
              type="date"
              value={audience.endDate || ""}
              onChange={(e) => update({ ...audience, endDate: e.target.value })}
              className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <Field label="Impact on audience">
            <Picker
              state={state}
              taxonomyKey="impactLevels"
              value={audience.impactLevelId}
              disabled={!canEdit}
              onChange={(id) => update({ ...audience, impactLevelId: id })}
            />
          </Field>
          <Field label="Change management effort">
            <Picker
              state={state}
              taxonomyKey="effortLevels"
              value={audience.effortLevelId}
              disabled={!canEdit}
              onChange={(id) => update({ ...audience, effortLevelId: id })}
            />
          </Field>
        </div>
        <Field
          label="Impact types"
          subtitle="Select all that apply"
          className="mt-4"
        >
          <div className="flex flex-wrap gap-2">
            {state.taxonomies.impactTypes.map((it) => {
              const selected = (audience.impactTypeIds || []).includes(it.id);
              return (
                <button
                  key={it.id}
                  disabled={!canEdit}
                  onClick={() => toggleMulti("impactTypeIds", it.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    selected
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
                  }`}
                >
                  {lookupLabel(state, "impactTypes", it.id)}
                </button>
              );
            })}
          </div>
        </Field>
      </Section>

      <Section
        title="Outcomes"
        subtitle="What you want this audience to know, feel, and do for the change to succeed"
      >
        <div className="grid grid-cols-3 gap-4">
          <div className="border border-amber-200 bg-amber-50/40 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-amber-200 text-amber-900 flex items-center justify-center text-xs font-bold">
                K
              </div>
              <div className="text-xs font-semibold text-amber-900 uppercase tracking-wider">
                Know
              </div>
            </div>
            <div className="text-[11px] text-amber-700 mb-2">
              What information do they need?
            </div>
            <textarea
              disabled={!canEdit}
              value={audience.know || ""}
              onChange={(e) => update({ ...audience, know: e.target.value })}
              rows={5}
              placeholder="e.g. The rationale for the change; their specific role in the new structure; timelines and milestones."
              className="w-full text-sm border border-amber-200 rounded p-2 bg-white resize-none focus:outline-none focus:border-amber-400 placeholder:text-stone-400"
            />
          </div>
          <div className="border border-rose-200 bg-rose-50/40 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-rose-200 text-rose-900 flex items-center justify-center text-xs font-bold">
                F
              </div>
              <div className="text-xs font-semibold text-rose-900 uppercase tracking-wider">
                Feel
              </div>
            </div>
            <div className="text-[11px] text-rose-700 mb-2">
              How do you want them to feel?
            </div>
            <textarea
              disabled={!canEdit}
              value={audience.feel || ""}
              onChange={(e) => update({ ...audience, feel: e.target.value })}
              rows={5}
              placeholder="e.g. Heard and respected; equipped to succeed; clear-eyed about the path ahead."
              className="w-full text-sm border border-rose-200 rounded p-2 bg-white resize-none focus:outline-none focus:border-rose-400 placeholder:text-stone-400"
            />
          </div>
          <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-6 h-6 rounded-full bg-emerald-200 text-emerald-900 flex items-center justify-center text-xs font-bold">
                D
              </div>
              <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wider">
                Do
              </div>
            </div>
            <div className="text-[11px] text-emerald-700 mb-2">
              What actions do you need from them?
            </div>
            <textarea
              disabled={!canEdit}
              value={audience.doIt || ""}
              onChange={(e) => update({ ...audience, doIt: e.target.value })}
              rows={5}
              placeholder="e.g. Complete training by date X; attend the kickoff; share questions with their HRBP."
              className="w-full text-sm border border-emerald-200 rounded p-2 bg-white resize-none focus:outline-none focus:border-emerald-400 placeholder:text-stone-400"
            />
          </div>
        </div>
      </Section>

      <Section
        title="Audience voice"
        subtitle="The human texture — what this audience says, worries about, and needs to see for the change to feel right"
      >
        <Field
          label="Representative quote"
          subtitle="A single sentence that captures how this audience talks about the change behind the scenes. Helps every consultant treat them as people, not rows."
        >
          <textarea
            disabled={!canEdit}
            value={audience.voiceQuote || ""}
            onChange={(e) =>
              update({ ...audience, voiceQuote: e.target.value })
            }
            rows={2}
            placeholder='e.g. "I joined this company specifically to avoid the kind of corporate process they keep proposing."'
            className="w-full text-sm border border-stone-200 rounded p-2 bg-white italic resize-none focus:outline-none focus:border-purple-400 placeholder:text-stone-400"
          />
        </Field>
        <div className="grid grid-cols-2 gap-4 mt-3">
          <Field
            label="Concerns / objections"
            subtitle="The questions they'd actually ask in a town hall — including the uncomfortable ones"
          >
            <textarea
              disabled={!canEdit}
              value={audience.concerns || ""}
              onChange={(e) =>
                update({ ...audience, concerns: e.target.value })
              }
              rows={5}
              placeholder="e.g. Will my equity vesting schedule change? Will I report to someone new? Do I keep my title?"
              className="w-full text-sm border border-stone-200 rounded p-2 bg-white resize-none focus:outline-none focus:border-purple-400 placeholder:text-stone-400"
            />
          </Field>
          <Field
            label="Success signals"
            subtitle="The concrete things they'd need to see or experience to feel the change is working"
          >
            <textarea
              disabled={!canEdit}
              value={audience.successSignals || ""}
              onChange={(e) =>
                update({ ...audience, successSignals: e.target.value })
              }
              rows={5}
              placeholder="e.g. Their first cross-org project ships smoothly. Their new manager remembers their name. Their toolchain feels comparable or better."
              className="w-full text-sm border border-stone-200 rounded p-2 bg-white resize-none focus:outline-none focus:border-purple-400 placeholder:text-stone-400"
            />
          </Field>
        </div>
      </Section>

      <Section title="How" subtitle="Reactions and change levers">
        <Field label="Anticipated reactions" subtitle="Select all that apply">
          <div className="flex flex-wrap gap-2">
            {state.taxonomies.reactionTypes.map((rt) => {
              const selected = (audience.reactionIds || []).includes(rt.id);
              return (
                <button
                  key={rt.id}
                  disabled={!canEdit}
                  onClick={() => toggleMulti("reactionIds", rt.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                    selected
                      ? "text-white border-transparent"
                      : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
                  }`}
                  style={selected ? { background: tokens.primary } : {}}
                >
                  {lookupLabel(state, "reactionTypes", rt.id)}
                </button>
              );
            })}
          </div>
        </Field>

        {recommendedLeverIds.ids.length > 0 && (
          <div className="mt-5 border border-purple-200 bg-purple-50/40 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2 text-xs font-semibold text-purple-900 uppercase tracking-wider">
                  <Sparkles size={12} /> Recommended levers
                </div>
                <div className="text-[11px] text-purple-700 mt-0.5">
                  Based on the reaction types you selected
                </div>
              </div>
              {canEdit && (
                <button
                  onClick={applyAllRecommended}
                  className="text-xs text-white px-3 py-1.5 rounded font-medium"
                  style={{ background: tokens.primary }}
                >
                  Apply all
                </button>
              )}
            </div>
            <div className="space-y-2">
              {recommendedLeverIds.ids.map((lid) => {
                const lev = state.taxonomies.changeLevers.find(
                  (l) => l.id === lid
                );
                if (!lev) return null;
                const label = recommendedLeverIds.labels[lid];
                const isSelected = (audience.leverIds || []).includes(lid);
                return (
                  <div key={lid} className="flex items-start gap-3 text-sm">
                    <div className="mt-0.5">
                      <input
                        disabled={!canEdit}
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleMulti("leverIds", lid)}
                        className="accent-purple-700"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-stone-900">
                          {lookupLabel(state, "changeLevers", lid)}
                        </span>
                        <span
                          className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                            label === "primary"
                              ? "bg-purple-700 text-white"
                              : "bg-purple-100 text-purple-800"
                          }`}
                        >
                          {label}
                        </span>
                      </div>
                      <div className="text-[12px] text-stone-600 mt-0.5">
                        {lev.desc}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <Field label="Selected change levers" className="mt-5">
          <div className="flex flex-wrap gap-2">
            {state.taxonomies.changeLevers.map((l) => {
              const selected = (audience.leverIds || []).includes(l.id);
              const recommended = recommendedLeverIds.ids.includes(l.id);
              return (
                <button
                  key={l.id}
                  disabled={!canEdit}
                  onClick={() => toggleMulti("leverIds", l.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors flex items-center gap-1.5 ${
                    selected
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
                  }`}
                >
                  {recommended && (
                    <Sparkles
                      size={10}
                      className={
                        selected ? "text-amber-300" : "text-purple-500"
                      }
                    />
                  )}
                  {lookupLabel(state, "changeLevers", l.id)}
                </button>
              );
            })}
          </div>
        </Field>
      </Section>

      {canEdit && (
        <button
          onClick={remove}
          className="mt-12 text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
        >
          <Trash2 size={12} /> Delete this audience
        </button>
      )}
    </div>
  );
}

// ============================================================================
// AI ASSIST PANEL — drafts impact description, reactions, Know/Feel/Do
// ============================================================================
function AIAssistPanel({ audience, state, update }) {
  const [busy, setBusy] = useState(null); // null | 'impact' | 'reactions' | 'kfd'
  const [error, setError] = useState(null);
  const [proposal, setProposal] = useState(null); // { type, data }

  function clearProposal() {
    setProposal(null);
    setError(null);
  }

  async function draftImpact() {
    setBusy("impact");
    setError(null);
    setProposal(null);
    try {
      const ctx = audienceContext(state, audience);
      const clientCtx = formatClientContextForPrompt(state.clientContext);
      const result = await aiDraft(
        "You are a WTW change management consultant assistant. You draft concise, professional impact descriptions for audiences affected by organizational change.",
        `Given this change-management context, draft a 2-3 sentence impact description for the audience. Be specific about what changes for them practically — not generic. If the client background materials below contain specific facts that make the impact more concrete, use them. Output JSON: {"impactDescription": "..."}\n\nContext:\n${JSON.stringify(
          ctx,
          null,
          2
        )}${clientCtx}`
      );
      setProposal({
        type: "impact",
        data: { impactDescription: asString(result.impactDescription) },
      });
    } catch (e) {
      setError(e.message || "AI request failed");
    } finally {
      setBusy(null);
    }
  }

  async function draftReactions() {
    setBusy("reactions");
    setError(null);
    setProposal(null);
    try {
      const ctx = audienceContext(state, audience);
      const clientCtx = formatClientContextForPrompt(state.clientContext);
      const reactionOptions = state.taxonomies.reactionTypes.map(
        (r) => r.label
      );
      const result = await aiDraft(
        "You are a WTW change management consultant assistant. You predict likely employee reactions to organizational change based on the WTW reaction taxonomy.",
        `Given this context, predict the 1-3 most likely reactions for this audience. Choose ONLY from this exact list: ${reactionOptions.join(
          ", "
        )}. For each reaction, give a brief one-sentence rationale. If the client background materials reveal specific cultural cues, prior history, or stated concerns, factor those in. Output JSON: {"reactions": [{"label": "ExactReactionName", "rationale": "..."}]}\n\nContext:\n${JSON.stringify(
          ctx,
          null,
          2
        )}${clientCtx}`
      );
      // Normalize: Claude might return [{label, rationale}] or [{"name": ...}] or just ["LabelStr"].
      // Coerce into a consistent [{label, rationale}] shape so the render is stable.
      const rawReactions = Array.isArray(result.reactions)
        ? result.reactions
        : [];
      const normalized = rawReactions
        .map((r) => {
          if (typeof r === "string") return { label: r, rationale: "" };
          if (r && typeof r === "object")
            return {
              label: asString(r.label || r.name),
              rationale: asString(r.rationale || r.reason || ""),
            };
          return { label: "", rationale: "" };
        })
        .filter((r) => r.label);
      setProposal({ type: "reactions", data: { reactions: normalized } });
    } catch (e) {
      setError(e.message || "AI request failed");
    } finally {
      setBusy(null);
    }
  }

  async function draftKFD() {
    setBusy("kfd");
    setError(null);
    setProposal(null);
    try {
      const ctx = audienceContext(state, audience);
      const clientCtx = formatClientContextForPrompt(state.clientContext);
      const result = await aiDraft(
        "You are a WTW change management consultant assistant. You write Know/Feel/Do statements that drive comms, training, and engagement strategy for impacted audiences.",
        `Write Know/Feel/Do for this audience.\n- KNOW: 1-2 sentences listing what this audience needs to understand.\n- FEEL: 1-2 sentences describing the emotional state you want them in.\n- DO: 1-2 sentences with the concrete actions you need from them.\nBe specific to this audience and change, not generic. Anchor specifics in the client background materials when relevant. Output JSON: {"know": "...", "feel": "...", "doIt": "..."}\n\nContext:\n${JSON.stringify(
          ctx,
          null,
          2
        )}${clientCtx}`
      );
      setProposal({
        type: "kfd",
        data: {
          know: asString(result.know),
          feel: asString(result.feel),
          doIt: asString(result.doIt),
        },
      });
    } catch (e) {
      setError(e.message || "AI request failed");
    } finally {
      setBusy(null);
    }
  }

  function applyImpact() {
    if ((audience.impactDescription || "").trim().length > 0) {
      const ok = window.confirm(
        "This will replace your existing impact description. Proceed?"
      );
      if (!ok) return;
    }
    update({
      ...audience,
      impactDescription: asString(proposal.data.impactDescription),
    });
    clearProposal();
  }
  function applyReactions() {
    // Map labels back to IDs — surface anything that doesn't match the taxonomy.
    // Claude might return reactions as objects [{label, evidence}] OR as plain strings — handle both.
    const labelToId = {};
    state.taxonomies.reactionTypes.forEach((r) => {
      labelToId[r.label] = r.id;
    });
    const proposed = Array.isArray(proposal.data.reactions)
      ? proposal.data.reactions
      : [];
    const mapped = proposed.map((r) => {
      const label = typeof r === "string" ? r : (r && r.label) || "";
      return { label, id: labelToId[label] };
    });
    const unmapped = mapped.filter((m) => m.label && !m.id).map((m) => m.label);
    if (unmapped.length > 0) {
      setError(
        `AI returned reaction label(s) not in the taxonomy: ${unmapped.join(
          ", "
        )}. Adding only the valid ones.`
      );
    }
    const validIds = mapped.filter((m) => m.id).map((m) => m.id);
    // Merge with existing (union)
    const merged = [...new Set([...(audience.reactionIds || []), ...validIds])];
    update({ ...audience, reactionIds: merged });
    // Keep proposal panel closed but error visible if any
    setProposal(null);
  }
  function applyKFD() {
    const hasExisting =
      (audience.know || audience.feel || audience.doIt || "").trim().length > 0;
    if (hasExisting) {
      const ok = window.confirm(
        "This will replace your existing Know/Feel/Do content. Proceed?"
      );
      if (!ok) return;
    }
    update({
      ...audience,
      know: asString(proposal.data.know),
      feel: asString(proposal.data.feel),
      doIt: asString(proposal.data.doIt),
    });
    clearProposal();
  }

  return (
    <div
      className="mt-4 rounded-xl overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${tokens.primaryTint} 0%, white 60%)`,
        border: `1px solid ${tokens.primary}22`,
        boxShadow: tokens.shadow.sm,
      }}
    >
      <div
        className="px-4 py-2.5 flex items-center justify-between"
        style={{
          background: `linear-gradient(90deg, ${tokens.primary}10 0%, transparent 100%)`,
          borderBottom: `1px solid ${tokens.primary}1a`,
        }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-5 h-5 rounded-md flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${tokens.primary} 0%, ${tokens.primaryDark} 100%)`,
              boxShadow: tokens.shadow.xs,
            }}
          >
            <Sparkles size={11} className="text-white" strokeWidth={2.5} />
          </div>
          <span
            className="text-[11px] uppercase tracking-[0.14em] font-semibold"
            style={{ color: tokens.primaryDark }}
          >
            AI Assist
          </span>
          <span
            className="text-[10.5px]"
            style={{ color: tokens.primary, opacity: 0.7 }}
          >
            Claude proposes drafts — you accept or reject.
          </span>
        </div>
      </div>

      <div className="p-3 flex flex-wrap gap-2">
        {[
          {
            id: "impact",
            label: "Draft impact description",
            onClick: draftImpact,
          },
          {
            id: "reactions",
            label: "Suggest reactions",
            onClick: draftReactions,
          },
          { id: "kfd", label: "Draft Know/Feel/Do", onClick: draftKFD },
        ].map((b) => (
          <button
            key={b.id}
            onClick={b.onClick}
            disabled={busy !== null}
            className="text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-wait font-medium"
            style={{
              background: "white",
              border: `1px solid ${tokens.primary}40`,
              color: tokens.primaryDark,
              boxShadow: tokens.shadow.xs,
              transition: tokens.transition.fast,
            }}
            onMouseEnter={(e) => {
              if (busy === null) {
                e.currentTarget.style.background = tokens.primaryTint;
                e.currentTarget.style.borderColor = `${tokens.primary}80`;
              }
            }}
            onMouseLeave={(e) => {
              if (busy === null) {
                e.currentTarget.style.background = "white";
                e.currentTarget.style.borderColor = `${tokens.primary}40`;
              }
            }}
          >
            {busy === b.id ? (
              <span
                className="inline-block w-3 h-3 rounded-full animate-spin"
                style={{
                  border: `2px solid ${tokens.primary}33`,
                  borderTopColor: tokens.primary,
                }}
              />
            ) : (
              <Sparkles size={11} />
            )}
            {b.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="px-4 pb-3 text-xs text-red-700 bg-red-50/50 border-t border-red-200 py-2">
          <strong>Error:</strong> {error}
        </div>
      )}

      {proposal && (
        <div className="border-t border-purple-100 bg-white p-4">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-purple-900 mb-2">
            AI Proposal
          </div>

          {proposal.type === "impact" && (
            <div>
              <div className="text-xs font-medium text-stone-600 mb-1">
                Suggested impact description
              </div>
              <div className="text-sm text-stone-800 bg-purple-50/40 border border-purple-200 rounded p-3">
                {proposal.data.impactDescription}
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={applyImpact}
                  className="text-xs text-white px-3 py-1.5 rounded font-medium flex items-center gap-1.5"
                  style={{ background: tokens.primary }}
                >
                  <Check size={11} /> Apply
                </button>
                <button
                  onClick={clearProposal}
                  className="text-xs text-stone-600 hover:text-stone-900 px-3 py-1.5"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {proposal.type === "reactions" && (
            <div>
              <div className="text-xs font-medium text-stone-600 mb-2">
                Suggested reactions (will add to existing selections)
              </div>
              <div className="space-y-2">
                {(proposal.data.reactions || []).map((r, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2 bg-purple-50/40 border border-purple-200 rounded p-2.5"
                  >
                    <span
                      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded mt-0.5 flex-shrink-0"
                      style={{ background: tokens.primary, color: "white" }}
                    >
                      {r.label}
                    </span>
                    <span className="text-xs text-stone-700">
                      {r.rationale}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={applyReactions}
                  className="text-xs text-white px-3 py-1.5 rounded font-medium flex items-center gap-1.5"
                  style={{ background: tokens.primary }}
                >
                  <Check size={11} /> Add all
                </button>
                <button
                  onClick={clearProposal}
                  className="text-xs text-stone-600 hover:text-stone-900 px-3 py-1.5"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {proposal.type === "kfd" && (
            <div>
              <div className="text-xs font-medium text-stone-600 mb-2">
                Suggested Know/Feel/Do
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-amber-50 border border-amber-200 rounded p-2.5">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-900 mb-1">
                    Know
                  </div>
                  <div className="text-xs text-stone-700">
                    {proposal.data.know}
                  </div>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded p-2.5">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-rose-900 mb-1">
                    Feel
                  </div>
                  <div className="text-xs text-stone-700">
                    {proposal.data.feel}
                  </div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2.5">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-emerald-900 mb-1">
                    Do
                  </div>
                  <div className="text-xs text-stone-700">
                    {proposal.data.doIt}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={applyKFD}
                  className="text-xs text-white px-3 py-1.5 rounded font-medium flex items-center gap-1.5"
                  style={{ background: tokens.primary }}
                >
                  <Check size={11} /> Apply all (replaces current)
                </button>
                <button
                  onClick={clearProposal}
                  className="text-xs text-stone-600 hover:text-stone-900 px-3 py-1.5"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="mt-8 border-t border-stone-200 pt-6">
      <div className="mb-4">
        <div className="text-base font-semibold text-stone-900">{title}</div>
        {subtitle && <div className="text-xs text-stone-500">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, subtitle, children, className = "" }) {
  return (
    <div className={className}>
      <div className="text-xs font-medium text-stone-600 mb-1.5">
        {label}
        {subtitle && (
          <span className="ml-2 text-stone-400 font-normal">— {subtitle}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function Picker({ state, taxonomyKey, value, onChange, disabled }) {
  return (
    <select
      disabled={disabled}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
    >
      <option value="">—</option>
      {state.taxonomies[taxonomyKey].map((row) => (
        <option key={row.id} value={row.id}>
          {lookupLabel(state, taxonomyKey, row.id)}
        </option>
      ))}
    </select>
  );
}

/**
 * MultiSelect — collapsed dropdown for multi-value selection.
 *
 * Used in filter rows where the option count is large enough that a chip-strip
 * would dominate the UI (e.g. 15+ audiences). The trigger button shows the
 * current selection summary; clicking opens a panel with a search input and
 * a checkbox list. Clicking outside closes it.
 *
 * Props:
 *   options:   [{ id, label, sublabel? }]  — items to show
 *   selected:  string[]                    — selected ids
 *   onChange:  (string[]) => void          — new selected ids
 *   placeholder: string                    — text when nothing is selected
 *   itemNoun:  string                      — label for the "X selected" pill
 */
function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Select…",
  itemNoun = "selected",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target))
        setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel || "").toLowerCase().includes(q)
    );
  }, [options, query]);

  function toggleOne(id) {
    if (selected.includes(id)) onChange(selected.filter((s) => s !== id));
    else onChange([...selected, id]);
  }
  function selectAll() {
    onChange(filteredOptions.map((o) => o.id));
  }
  function clearAll() {
    onChange([]);
  }

  // Summary text — "All", "Acme", "Acme, Beta", or "3 selected"
  const selectedOptions = options.filter((o) => selected.includes(o.id));
  let summary;
  if (selected.length === 0) summary = placeholder;
  else if (selected.length <= 2)
    summary = selectedOptions.map((o) => o.label).join(", ");
  else summary = `${selected.length} ${itemNoun}`;

  return (
    <div className="relative inline-block" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1.5 ${
          selected.length > 0
            ? "bg-stone-900 text-white border-stone-900"
            : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
        }`}
      >
        <span className="truncate max-w-[200px]">{summary}</span>
        <ChevronDown
          size={11}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-80 bg-white border border-stone-200 rounded-lg shadow-lg overflow-hidden">
          {/* Search */}
          <div className="px-2.5 py-2 border-b border-stone-100 bg-stone-50">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full text-[12px] border border-stone-200 rounded px-2 py-1 bg-white focus:outline-none focus:border-purple-400"
            />
          </div>
          {/* Options list */}
          <div className="max-h-64 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-4 text-[11px] text-stone-400 italic text-center">
                No matches
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = selected.includes(opt.id);
                return (
                  <button
                    type="button"
                    key={opt.id}
                    onClick={() => toggleOne(opt.id)}
                    className={`w-full text-left px-2.5 py-1.5 flex items-center gap-2 text-[12px] ${
                      isSelected ? "bg-purple-50" : "hover:bg-stone-50"
                    }`}
                  >
                    <span
                      className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${
                        isSelected
                          ? "bg-stone-900 border-stone-900"
                          : "bg-white border-stone-300"
                      }`}
                    >
                      {isSelected && <Check size={9} className="text-white" />}
                    </span>
                    <span className="flex-1 truncate text-stone-800">
                      {opt.label}
                    </span>
                    {opt.sublabel && (
                      <span className="text-[10px] text-stone-400 truncate">
                        {opt.sublabel}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
          {/* Footer */}
          <div className="px-2.5 py-1.5 border-t border-stone-100 bg-stone-50 flex items-center justify-between">
            <div className="text-[10px] text-stone-500">
              <span className="font-semibold text-stone-700">
                {selected.length}
              </span>{" "}
              of {options.length}
            </div>
            <div className="flex items-center gap-2">
              {query && (
                <button
                  onClick={selectAll}
                  className="text-[10px] text-purple-700 hover:text-purple-900"
                >
                  Select matches
                </button>
              )}
              {selected.length > 0 && (
                <button
                  onClick={clearAll}
                  className="text-[10px] text-stone-500 hover:text-stone-800"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Dashboard({ state }) {
  const totalPeople = useMemo(() => {
    const byAudienceName = {};
    state.audiences.forEach((a) => {
      const size =
        state.taxonomies.audienceSizes.find((s) => s.id === a.audienceSizeId)
          ?.value || 0;
      byAudienceName[a.name] = Math.max(byAudienceName[a.name] || 0, size);
    });
    return Object.values(byAudienceName).reduce((s, v) => s + v, 0);
  }, [state.audiences]);

  const dateRange = useMemo(() => {
    const dates = state.audiences
      .flatMap((a) => [a.startDate, a.endDate])
      .filter(Boolean);
    if (dates.length === 0) return null;
    const sorted = dates.sort();
    return { start: sorted[0], end: sorted[sorted.length - 1] };
  }, [state.audiences]);

  const monthsDuration = useMemo(() => {
    if (!dateRange) return 0;
    const a = new Date(dateRange.start);
    const b = new Date(dateRange.end);
    return Math.round((b - a) / (1000 * 60 * 60 * 24 * 30));
  }, [dateRange]);

  const impactDist = useMemo(() => {
    const counts = {};
    state.taxonomies.impactLevels.forEach((l) => (counts[l.id] = 0));
    state.audiences.forEach((a) => {
      if (a.impactLevelId) counts[a.impactLevelId]++;
    });
    return state.taxonomies.impactLevels.map((l) => ({
      label: l.label,
      count: counts[l.id],
      color: impactColor(l.id),
    }));
  }, [state.audiences, state.taxonomies.impactLevels]);

  const effortDist = useMemo(() => {
    const counts = {};
    state.taxonomies.effortLevels.forEach((l) => (counts[l.id] = 0));
    state.audiences.forEach((a) => {
      if (a.effortLevelId) counts[a.effortLevelId]++;
    });
    return state.taxonomies.effortLevels.map((l) => ({
      label: l.label,
      count: counts[l.id],
      color: effortColor(l.id),
    }));
  }, [state.audiences, state.taxonomies.effortLevels]);

  const reactionDist = useMemo(() => {
    const counts = {};
    state.taxonomies.reactionTypes.forEach((r) => (counts[r.id] = 0));
    state.audiences.forEach((a) =>
      (a.reactionIds || []).forEach(
        (rid) => (counts[rid] = (counts[rid] || 0) + 1)
      )
    );
    return state.taxonomies.reactionTypes
      .map((r) => ({
        label: lookupLabel(state, "reactionTypes", r.id),
        count: counts[r.id],
      }))
      .sort((a, b) => b.count - a.count);
  }, [state]);

  const impactTypeDist = useMemo(() => {
    const counts = {};
    state.taxonomies.impactTypes.forEach((r) => (counts[r.id] = 0));
    state.audiences.forEach((a) =>
      (a.impactTypeIds || []).forEach(
        (rid) => (counts[rid] = (counts[rid] || 0) + 1)
      )
    );
    return state.taxonomies.impactTypes
      .map((r) => ({
        label: lookupLabel(state, "impactTypes", r.id),
        count: counts[r.id],
      }))
      .sort((a, b) => b.count - a.count);
  }, [state]);

  const scatterData = useMemo(() => {
    return state.changesToImplement
      .map((cti) => {
        const auds = state.audiences.filter(
          (a) => a.changeToImplementId === cti.id
        );
        if (auds.length === 0) return null;
        const avgImpact =
          auds.reduce((s, a) => {
            const il = state.taxonomies.impactLevels.find(
              (l) => l.id === a.impactLevelId
            );
            return s + (il?.value || 0);
          }, 0) / auds.length;
        const avgEffort =
          auds.reduce((s, a) => {
            const el = state.taxonomies.effortLevels.find(
              (l) => l.id === a.effortLevelId
            );
            return s + (el?.value || 0);
          }, 0) / auds.length;
        const totalSize = auds.reduce(
          (s, a) =>
            s +
            (state.taxonomies.audienceSizes.find(
              (z) => z.id === a.audienceSizeId
            )?.value || 0),
          0
        );
        const change = state.changes.find((c) => c.id === cti.changeId);
        return {
          x: avgEffort,
          y: avgImpact,
          z: Math.log10(totalSize + 1) * 80,
          name: cti.name,
          change: change?.name || "",
          audCount: auds.length,
          size: totalSize,
        };
      })
      .filter(Boolean);
  }, [state]);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-medium mb-1">
            Project Dashboard
          </div>
          <h2
            className="text-3xl font-semibold text-stone-900"
            style={{
              fontFamily: "Georgia, serif",
              letterSpacing: "-0.015em",
              lineHeight: 1.15,
            }}
          >
            {state.project.name}
          </h2>
        </div>
        <button className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1.5">
          <Download size={12} /> Export deck{" "}
          <span className="text-[9px] uppercase tracking-wider bg-stone-200 text-stone-600 px-1.5 py-0.5 rounded ml-1">
            phase 2
          </span>
        </button>
      </div>

      {state.audiences.length === 0 ? (
        <div
          className="rounded-2xl p-14 text-center"
          style={{
            background: "white",
            border: "1.5px dashed #D6D3D1",
            boxShadow: tokens.shadow.xs,
          }}
        >
          <div
            className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{
              background: tokens.surface.panelMuted,
              boxShadow: tokens.shadow.xs,
            }}
          >
            <LayoutDashboard size={22} className="text-stone-400" />
          </div>
          <div
            className="text-base font-semibold text-stone-800 mb-1.5"
            style={{ fontFamily: "Georgia, serif" }}
          >
            No data to summarise yet
          </div>
          <div className="text-[13px] text-stone-500 max-w-md mx-auto leading-relaxed">
            Add changes and audiences in the Change Impact tool, and this
            dashboard will populate with KPIs, distributions, and the effort ×
            impact map.
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-6 gap-3 mb-6">
            <KPI label="Changes" value={state.changes.length} />
            <KPI
              label="Key changes"
              value={state.changes.filter((c) => c.isKey).length}
              accent="amber"
            />
            <KPI
              label="Changes to implement"
              value={state.changesToImplement.length}
            />
            <KPI label="Audiences" value={state.audiences.length} />
            <KPI
              label="~People impacted"
              value={`${totalPeople.toLocaleString()}+`}
              accent="purple"
            />
            <KPI
              label="Duration"
              value={monthsDuration ? `${monthsDuration} mo` : "—"}
            />
          </div>

          <Card
            title="Effort × Impact"
            subtitle="Each change to implement plotted by avg effort and avg impact. Bubble size = total audience size."
          >
            <div style={{ width: "100%", height: 360 }}>
              <ResponsiveContainer>
                <ScatterChart
                  margin={{ top: 16, right: 24, bottom: 40, left: 24 }}
                >
                  <CartesianGrid strokeDasharray="2 4" stroke="#e7e5e0" />
                  <XAxis
                    type="number"
                    dataKey="x"
                    domain={[0.5, 3.5]}
                    ticks={[1, 2, 3]}
                    tickFormatter={(v) =>
                      ({ 1: "Low", 2: "Medium", 3: "High" }[v] || "")
                    }
                    tick={{ fontSize: 11, fill: "#6b6b7b" }}
                    label={{
                      value: "Change Management Effort",
                      position: "insideBottom",
                      offset: -10,
                      style: { fontSize: 11, fill: "#6b6b7b" },
                    }}
                  />
                  <YAxis
                    type="number"
                    dataKey="y"
                    domain={[0.5, 5.5]}
                    ticks={[1, 2, 3, 4, 5]}
                    tickFormatter={(v) =>
                      ({
                        1: "None",
                        2: "Small",
                        3: "Medium",
                        4: "Large",
                        5: "Extreme",
                      }[v] || "")
                    }
                    tick={{ fontSize: 11, fill: "#6b6b7b" }}
                    label={{
                      value: "Impact on Audience",
                      angle: -90,
                      position: "insideLeft",
                      style: {
                        fontSize: 11,
                        fill: "#6b6b7b",
                        textAnchor: "middle",
                      },
                    }}
                  />
                  <ZAxis type="number" dataKey="z" range={[60, 600]} />
                  <Tooltip
                    cursor={{ strokeDasharray: "3 3" }}
                    content={({ active, payload }) => {
                      if (!active || !payload || !payload[0]) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-white border border-stone-300 rounded p-3 text-xs shadow-lg max-w-xs">
                          <div className="font-semibold text-stone-900">
                            {d.name}
                          </div>
                          <div className="text-stone-500 mb-2">
                            in {d.change}
                          </div>
                          <div className="text-stone-700">
                            {d.audCount} audiences · ~{d.size.toLocaleString()}{" "}
                            people
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Scatter
                    data={scatterData}
                    fill={tokens.primary}
                    fillOpacity={0.7}
                    stroke={tokens.primary}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <Card
              title="Impact distribution"
              subtitle="Number of audiences at each impact level"
            >
              <DistBars data={impactDist} />
            </Card>
            <Card
              title="Effort distribution"
              subtitle="Number of audiences at each effort level"
            >
              <DistBars data={effortDist} />
            </Card>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <Card
              title="Top reactions"
              subtitle="Across all audiences (multi-select)"
            >
              <RankedBars data={reactionDist} color={tokens.primary} />
            </Card>
            <Card
              title="Impact types"
              subtitle="Across all audiences (multi-select)"
            >
              <RankedBars data={impactTypeDist} color={tokens.ink} />
            </Card>
          </div>

          <div className="mt-4">
            <Card
              title="Most impacted audiences"
              subtitle="Ranked by total disruption: severity × number of impact dimensions, summed across every change to implement that hits this audience."
            >
              <MostImpactedTable state={state} />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

function MostImpactedTable({ state }) {
  const [sortKey, setSortKey] = useState("totalScore");
  const [sortDir, setSortDir] = useState("desc");

  const baseRows = useMemo(() => {
    // Aggregate by audience NAME (since the same named audience appears in multiple CTIs)
    const byName = {};
    state.audiences.forEach((a) => {
      const severity =
        state.taxonomies.impactLevels.find((l) => l.id === a.impactLevelId)
          ?.value || 0;
      const typeCount = (a.impactTypeIds || []).length;
      const size =
        state.taxonomies.audienceSizes.find((s) => s.id === a.audienceSizeId)
          ?.value || 0;
      const score = severity * typeCount;
      if (!byName[a.name]) {
        byName[a.name] = {
          name: a.name,
          totalScore: 0,
          rowCount: 0,
          maxSize: 0,
          typeSet: new Set(),
          maxSeverity: 0,
          totalTypeInstances: 0,
        };
      }
      const r = byName[a.name];
      r.totalScore += score;
      r.rowCount += 1;
      r.maxSize = Math.max(r.maxSize, size);
      r.maxSeverity = Math.max(r.maxSeverity, severity);
      r.totalTypeInstances += typeCount;
      (a.impactTypeIds || []).forEach((id) => r.typeSet.add(id));
    });
    return Object.values(byName).map((r) => ({
      ...r,
      uniqueTypes: r.typeSet.size,
    }));
  }, [state.audiences, state.taxonomies]);

  const rows = useMemo(() => {
    const sorted = [...baseRows].sort((a, b) => {
      let av = a[sortKey],
        bv = b[sortKey];
      if (sortKey === "name") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return sorted;
  }, [baseRows, sortKey, sortDir]);

  const maxScore = Math.max(...rows.map((r) => r.totalScore), 1);

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // Default to descending for numeric, ascending for name
      setSortDir(key === "name" ? "asc" : "desc");
    }
  }

  function SortHeader({ k, label, title, align = "left" }) {
    const active = sortKey === k;
    return (
      <button
        onClick={() => handleSort(k)}
        title={title}
        className={`flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold hover:text-stone-900 transition-colors ${
          align === "center" ? "justify-center w-full" : ""
        } ${active ? "text-stone-900" : "text-stone-500"}`}
      >
        <span>{label}</span>
        <span className="text-stone-400" style={{ fontSize: 9 }}>
          {active ? (sortDir === "desc" ? "▼" : "▲") : "↕"}
        </span>
      </button>
    );
  }

  return (
    <div>
      <div className="grid grid-cols-[2fr_60px_60px_70px_70px_1fr] px-2 py-2 border-b border-stone-200">
        <SortHeader k="name" label="Audience" />
        <SortHeader
          k="rowCount"
          label="Changes"
          title="Number of change-to-implements that hit this audience"
          align="center"
        />
        <SortHeader
          k="maxSeverity"
          label="Max sev"
          title="Max severity across all change-to-implements (1-5)"
          align="center"
        />
        <SortHeader
          k="uniqueTypes"
          label="Dimensions"
          title="Unique impact dimensions across all change-to-implements (out of 6)"
          align="center"
        />
        <SortHeader
          k="maxSize"
          label="Size"
          title="Max audience size"
          align="center"
        />
        <SortHeader
          k="totalScore"
          label="Disruption score"
          title="Severity × impact-type count, summed across rows"
        />
      </div>
      {rows.map((r) => (
        <div
          key={r.name}
          className="grid grid-cols-[2fr_60px_60px_70px_70px_1fr] px-2 py-2 items-center text-[12px] border-b border-stone-100 hover:bg-stone-50"
        >
          <div className="font-medium text-stone-800 truncate" title={r.name}>
            {r.name}
          </div>
          <div className="text-center text-stone-700">{r.rowCount}</div>
          <div className="text-center">
            <span
              className="inline-block w-6 h-6 rounded text-white font-bold text-[11px] leading-6"
              style={{
                background: impactColor(
                  ["il1", "il2", "il3", "il4", "il5"][r.maxSeverity - 1] ||
                    "il1"
                ),
              }}
            >
              {r.maxSeverity}
            </span>
          </div>
          <div className="text-center text-stone-700">
            <span className="font-semibold">{r.uniqueTypes}</span>
            <span className="text-stone-400">/6</span>
          </div>
          <div className="text-center text-stone-700">
            ~{r.maxSize.toLocaleString()}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-4 bg-stone-100 rounded overflow-hidden relative">
              <div
                className="h-full rounded"
                style={{
                  width: `${(r.totalScore / maxScore) * 100}%`,
                  background: tokens.primary,
                  opacity: 0.85,
                }}
              />
            </div>
            <span className="font-semibold text-stone-900 w-6 text-right">
              {r.totalScore}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

function KPI({ label, value, accent }) {
  const accentStyles = {
    amber: {
      background: "linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)",
      border: "1px solid #FCD34D33",
    },
    purple: {
      background: `linear-gradient(135deg, ${tokens.primaryTint} 0%, #EDE3F7 100%)`,
      border: `1px solid ${tokens.primary}22`,
    },
  };
  const style = accentStyles[accent] || {
    background: "white",
    border: "1px solid rgb(229, 231, 235)",
  };
  return (
    <div
      className="rounded-lg p-4"
      style={{
        ...style,
        boxShadow: tokens.shadow.xs,
        transition: tokens.transition.base,
      }}
    >
      <div
        className="text-2xl font-semibold text-stone-900 tabular-nums"
        style={{
          fontFamily: "Georgia, serif",
          letterSpacing: "-0.02em",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      <div className="text-[10px] text-stone-600 uppercase tracking-[0.12em] mt-1.5 font-semibold">
        {label}
      </div>
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div
      className="rounded-lg p-5"
      style={{
        background: "white",
        border: "1px solid rgb(229, 231, 235)",
        boxShadow: tokens.shadow.xs,
      }}
    >
      <div className="mb-4">
        <div
          className="text-sm font-semibold text-stone-900"
          style={{ letterSpacing: "-0.005em" }}
        >
          {title}
        </div>
        {subtitle && (
          <div className="text-[11px] text-stone-500 mt-1 leading-relaxed">
            {subtitle}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function DistBars({ data }) {
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="space-y-2">
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-3 text-xs">
          <div className="w-32 text-stone-700 truncate">{d.label}</div>
          <div className="flex-1 h-6 bg-stone-100 rounded relative overflow-hidden">
            <div
              className="h-full rounded"
              style={{
                width: `${(d.count / max) * 100}%`,
                background: d.color,
              }}
            />
          </div>
          <div className="w-8 text-right font-medium text-stone-900">
            {d.count}
          </div>
        </div>
      ))}
    </div>
  );
}

function RankedBars({ data, color }) {
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="space-y-2">
      {data.map((d) => (
        <div key={d.label} className="flex items-center gap-3 text-xs">
          <div className="w-36 text-stone-700 truncate">{d.label}</div>
          <div className="flex-1 h-5 bg-stone-100 rounded relative overflow-hidden">
            <div
              className="h-full rounded"
              style={{
                width: `${(d.count / max) * 100}%`,
                background: color,
                opacity: d.count > 0 ? 0.85 : 0,
              }}
            />
          </div>
          <div className="w-8 text-right font-medium text-stone-900">
            {d.count}
          </div>
        </div>
      ))}
    </div>
  );
}

function Timeline({ state }) {
  const [grouping, setGrouping] = useState("change");

  const auds = useMemo(
    () => state.audiences.filter((a) => a.startDate && a.endDate),
    [state.audiences]
  );

  const dateExtent = useMemo(() => {
    if (auds.length === 0) return null;
    const all = auds.flatMap((a) => [
      new Date(a.startDate),
      new Date(a.endDate),
    ]);
    return { min: new Date(Math.min(...all)), max: new Date(Math.max(...all)) };
  }, [auds]);

  const monthTicks = useMemo(() => {
    if (!dateExtent) return [];
    const ticks = [];
    const cur = new Date(
      dateExtent.min.getFullYear(),
      dateExtent.min.getMonth(),
      1
    );
    const end = new Date(
      dateExtent.max.getFullYear(),
      dateExtent.max.getMonth() + 1,
      1
    );
    while (cur <= end) {
      ticks.push(new Date(cur));
      cur.setMonth(cur.getMonth() + 1);
    }
    return ticks;
  }, [dateExtent]);

  const grouped = useMemo(() => {
    const groups = {};
    auds.forEach((a) => {
      let key;
      if (grouping === "change") {
        const cti = state.changesToImplement.find(
          (c) => c.id === a.changeToImplementId
        );
        const change = state.changes.find((c) => c.id === cti?.changeId);
        key = change?.name || "Unknown";
      } else if (grouping === "workstream") {
        const cti = state.changesToImplement.find(
          (c) => c.id === a.changeToImplementId
        );
        key =
          lookupLabel(state, "workstreams", cti?.workstreamId) || "Unassigned";
      } else if (grouping === "region") {
        key = a.region || "Unspecified";
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(a);
    });
    return Object.entries(groups);
  }, [auds, grouping, state]);

  if (!dateExtent)
    return (
      <div className="p-8 text-stone-500">No dated audiences to display.</div>
    );

  const totalMs = dateExtent.max - dateExtent.min;
  // Guard against degenerate cases:
  //  - totalMs === 0 (all dates identical → divide-by-zero → NaN)
  //  - invalid date strings (new Date(badStr) → NaN → NaN math)
  // In either case, fall back to 0 so layout stays sane instead of producing broken CSS.
  function pct(date) {
    if (!totalMs || isNaN(totalMs)) return 0;
    const d = new Date(date).getTime();
    if (isNaN(d)) return 0;
    return Math.max(0, Math.min(100, ((d - dateExtent.min) / totalMs) * 100));
  }
  function widthPct(start, end) {
    if (!totalMs || isNaN(totalMs)) return 0;
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    if (isNaN(s) || isNaN(e)) return 0;
    // Negative widths (end before start) clamp to 0 so the bar disappears rather than rendering backwards.
    return Math.max(0, ((e - s) / totalMs) * 100);
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-medium mb-1">
            Audience Timeline
          </div>
          <h2
            className="text-3xl font-semibold text-stone-900"
            style={{
              fontFamily: "Georgia, serif",
              letterSpacing: "-0.015em",
              lineHeight: 1.15,
            }}
          >
            When changes hit which audiences
          </h2>
        </div>
        <div className="flex items-center gap-1 text-xs">
          <span className="text-stone-500 mr-2">Group by</span>
          {[
            { id: "change", label: "Change" },
            { id: "workstream", label: "Workstream" },
            { id: "region", label: "Region" },
          ].map((g) => (
            <button
              key={g.id}
              onClick={() => setGrouping(g.id)}
              className={`px-2.5 py-1 rounded ${
                grouping === g.id
                  ? "bg-stone-900 text-white"
                  : "bg-stone-100 text-stone-700 hover:bg-stone-200"
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
        <div className="flex border-b border-stone-200">
          <div className="w-56 px-4 py-2 bg-stone-50 border-r border-stone-200 text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
            Audience
          </div>
          <div className="flex-1 relative" style={{ height: 36 }}>
            {monthTicks.map((t, i) => (
              <div
                key={i}
                className="absolute top-0 bottom-0 border-l border-stone-200"
                style={{ left: `${pct(t)}%` }}
              >
                <div className="text-[10px] text-stone-500 px-1 py-1.5 whitespace-nowrap">
                  {t.toLocaleDateString("en-US", { month: "short" })}
                  {t.getMonth() === 0 && (
                    <span className="text-stone-400 ml-1">
                      {t.getFullYear()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {grouped.map(([groupName, items]) => (
          <div key={groupName}>
            <div className="flex bg-stone-50/50 border-b border-stone-200">
              <div className="w-56 px-4 py-1.5 text-[11px] uppercase tracking-wider text-stone-700 font-semibold border-r border-stone-200">
                {groupName}
              </div>
              <div className="flex-1" />
            </div>
            {items.map((a) => {
              const impactLevel = state.taxonomies.impactLevels.find(
                (l) => l.id === a.impactLevelId
              );
              const severity = impactLevel?.value || 0;
              const typeCount = (a.impactTypeIds || []).length;
              const typeNames = (a.impactTypeIds || []).map((id) =>
                lookupLabel(state, "impactTypes", id)
              );
              return (
                <div
                  key={a.id}
                  className="flex border-b border-stone-100 hover:bg-stone-50"
                >
                  <div className="w-56 px-4 py-2.5 border-r border-stone-200">
                    <div className="text-xs font-medium text-stone-800 truncate">
                      {a.name}
                    </div>
                    <div className="text-[10px] text-stone-500 flex items-center gap-1.5">
                      <span>
                        {lookupLabel(state, "audienceSizes", a.audienceSizeId)}
                      </span>
                      {typeCount > 0 && (
                        <span className="text-stone-400">·</span>
                      )}
                      {typeCount > 0 && (
                        <span
                          className="inline-flex items-center gap-0.5 text-stone-600"
                          title={typeNames.join(", ")}
                        >
                          <span className="font-semibold">{typeCount}</span>
                          <span>type{typeCount === 1 ? "" : "s"}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className="flex-1 relative py-2.5"
                    style={{ minHeight: 44 }}
                  >
                    {monthTicks.map((t, i) => (
                      <div
                        key={i}
                        className="absolute top-0 bottom-0 border-l border-stone-100"
                        style={{ left: `${pct(t)}%` }}
                      />
                    ))}
                    <div
                      className="absolute rounded-md flex items-center gap-1.5 px-2 text-[10px] text-white font-medium whitespace-nowrap overflow-hidden"
                      style={{
                        left: `${pct(a.startDate)}%`,
                        width: `${widthPct(a.startDate, a.endDate)}%`,
                        top: 8,
                        height: 24,
                        background: impactColor(a.impactLevelId),
                        minWidth: 4,
                      }}
                      title={`${a.name} — ${a.startDate} → ${
                        a.endDate
                      }\nSeverity ${severity}/5 · ${typeCount} impact type${
                        typeCount === 1 ? "" : "s"
                      }${typeNames.length ? ": " + typeNames.join(", ") : ""}`}
                    >
                      <span className="bg-white/25 rounded px-1 py-px text-[9px] font-bold flex-shrink-0">
                        L{severity}
                      </span>
                      <span className="truncate flex-1">
                        {lookupLabel(state, "impactLevels", a.impactLevelId)}
                      </span>
                      {typeCount > 0 && (
                        <span
                          className="bg-white/25 rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold flex-shrink-0"
                          title={`${typeCount} impact types`}
                        >
                          {typeCount}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        <div className="flex items-center gap-4 text-[11px] text-stone-600 flex-wrap">
          <span className="font-medium uppercase tracking-wider text-stone-500">
            Severity
          </span>
          {state.taxonomies.impactLevels.map((l) => (
            <span key={l.id} className="flex items-center gap-1.5">
              <span
                className="w-3 h-3 rounded-sm"
                style={{ background: impactColor(l.id) }}
              />
              <span className="font-mono text-stone-500">L{l.value}</span>
              {l.label}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-4 text-[11px] text-stone-500">
          <span className="font-medium uppercase tracking-wider text-stone-500">
            Notation
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-white font-medium"
              style={{ background: tokens.impact.large }}
            >
              <span className="bg-white/25 rounded px-1 py-px text-[9px] font-bold">
                L4
              </span>
              <span className="text-[10px]">Large impact</span>
              <span className="bg-white/25 rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold">
                3
              </span>
            </span>
            <span className="text-stone-600">
              = Severity level 4 of 5, impacting 3 dimensions (e.g. tools,
              skills, behaviors)
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CLIENT CONTEXT PANEL — drop zone, file list, used by SettingsView and the
// header's "Context" drawer so the consultant can drop files from anywhere.
// ============================================================================
function ClientContextPanel({
  state,
  setState,
  canEdit,
  compact = false,
  onOpenReview,
}) {
  const [dragActive, setDragActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Track which files (by id) are currently being extracted, plus any extraction errors
  const [extracting, setExtracting] = useState(() => new Set());
  const [extractError, setExtractError] = useState(null);
  const [lastExtraction, setLastExtraction] = useState(null); // { fileId, counts: {changes, audiences, ...} }
  const fileInputRef = useRef(null);

  async function handleFiles(fileList) {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setBusy(true);
    setError(null);
    const added = [];
    const errors = [];
    for (const file of files) {
      try {
        const text = await extractTextFromFile(file);
        if (!text.trim()) {
          errors.push(`${file.name}: no extractable text`);
          continue;
        }
        added.push({
          id: uid("cc"),
          filename: file.name,
          type: file.type || "application/octet-stream",
          sizeBytes: file.size,
          text,
          addedAt: new Date().toISOString(),
        });
      } catch (e) {
        errors.push(`${file.name}: ${e.message || "parse failed"}`);
      }
    }
    if (added.length > 0) {
      setState((p) => ({
        ...p,
        clientContext: [...(p.clientContext || []), ...added],
      }));
    }
    if (errors.length > 0) {
      setError(errors.join(" · "));
    }
    setBusy(false);
  }

  function removeFile(id) {
    if (
      !window.confirm(
        "Remove this file from client context? The AI will no longer see it on future drafts."
      )
    )
      return;
    setState((p) => ({
      ...p,
      clientContext: (p.clientContext || []).filter((f) => f.id !== id),
    }));
  }

  // ---- AI extraction ----
  // Run aiExtractFromDocument on a single file, dedup against existing project state,
  // and stash the proposals in state.pendingProposals for the consultant to review.
  async function extractFromFile(file) {
    if (!file?.text) return;
    setExtracting((prev) => new Set(prev).add(file.id));
    setExtractError(null);
    try {
      const result = await aiExtractFromDocument(
        file.text,
        file.filename,
        state
      );
      const newProposals = [];
      const now = new Date().toISOString();

      // Changes
      (result.changes || []).forEach((item) => {
        if (!item?.name) return;
        const dupOf = findPossibleDuplicate(item.name, state.changes);
        newProposals.push({
          id: uid("prop"),
          kind: "change",
          status: "pending",
          source: file.filename,
          sourceFileId: file.id,
          sourceQuote: asString(item.sourceQuote, 200),
          data: {
            name: asString(item.name, 120),
            description: asString(item.description, 500),
            isKey: !!item.isKey,
          },
          dupOf,
          extractedAt: now,
        });
      });

      // Audiences
      (result.audiences || []).forEach((item) => {
        if (!item?.name) return;
        const dupOf = findPossibleDuplicate(item.name, state.audiences);
        newProposals.push({
          id: uid("prop"),
          kind: "audience",
          status: "pending",
          source: file.filename,
          sourceFileId: file.id,
          sourceQuote: asString(item.sourceQuote, 200),
          data: {
            name: asString(item.name, 120),
            impactDescription: asString(item.impactDescription, 600),
            suggestedChange: asString(item.suggestedChange, 120),
          },
          dupOf,
          extractedAt: now,
        });
      });

      // Stakeholders
      (result.stakeholders || []).forEach((item) => {
        if (!item?.name) return;
        const dupOf = findPossibleDuplicate(item.name, state.stakeholders);
        const impact = ["high", "medium", "low"].includes(item.abilityToImpact)
          ? item.abilityToImpact
          : "medium";
        newProposals.push({
          id: uid("prop"),
          kind: "stakeholder",
          status: "pending",
          source: file.filename,
          sourceFileId: file.id,
          sourceQuote: asString(item.sourceQuote, 200),
          data: {
            name: asString(item.name, 120),
            role: asString(item.role, 200),
            abilityToImpact: impact,
          },
          dupOf,
          extractedAt: now,
        });
      });

      // Interventions
      (result.interventions || []).forEach((item) => {
        if (!item?.name) return;
        const dupOf = findPossibleDuplicate(item.name, state.interventions);
        newProposals.push({
          id: uid("prop"),
          kind: "intervention",
          status: "pending",
          source: file.filename,
          sourceFileId: file.id,
          sourceQuote: asString(item.sourceQuote, 200),
          data: {
            name: asString(item.name, 120),
            description: asString(item.description, 600),
            suggestedAudience: asString(item.suggestedAudience, 120),
          },
          dupOf,
          extractedAt: now,
        });
      });

      const counts = {
        changes: newProposals.filter((p) => p.kind === "change").length,
        audiences: newProposals.filter((p) => p.kind === "audience").length,
        stakeholders: newProposals.filter((p) => p.kind === "stakeholder")
          .length,
        interventions: newProposals.filter((p) => p.kind === "intervention")
          .length,
      };

      setState((p) => ({
        ...p,
        pendingProposals: [...(p.pendingProposals || []), ...newProposals],
      }));
      setLastExtraction({
        fileId: file.id,
        counts,
        total: newProposals.length,
      });
    } catch (e) {
      setExtractError(`${file.filename}: ${e.message || "extraction failed"}`);
    } finally {
      setExtracting((prev) => {
        const next = new Set(prev);
        next.delete(file.id);
        return next;
      });
    }
  }

  async function extractFromAll() {
    for (const file of files) {
      // Run them sequentially so we don't hammer the API; each call uses up to 8000 tokens.
      // eslint-disable-next-line no-await-in-loop
      await extractFromFile(file);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (!canEdit) return;
    handleFiles(e.dataTransfer?.files);
  }

  function fmtSize(b) {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  }

  const files = state.clientContext || [];
  const totalChars = files.reduce((s, f) => s + (f.text?.length || 0), 0);
  const pendingCount = (state.pendingProposals || []).filter(
    (p) => p.status === "pending"
  ).length;
  const anyExtracting = extracting.size > 0;

  return (
    <div
      className={
        compact ? "" : "border border-stone-200 rounded-lg bg-white p-5"
      }
    >
      {!compact && (
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="text-sm font-semibold text-stone-900">
              Client background materials
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5">
              Drop strategy docs, briefing memos, RFPs, project charters here.
              The AI will use them as context when drafting impact descriptions,
              reactions, K/F/D statements, and executive briefings — or extract
              structured entities (changes, audiences, stakeholders,
              interventions) for your review.
            </div>
          </div>
          {files.length > 0 && (
            <div className="text-[11px] text-stone-500">
              {files.length} file{files.length === 1 ? "" : "s"} ·{" "}
              {Math.round(totalChars / 1000)}k chars
            </div>
          )}
        </div>
      )}

      {/* Drop zone */}
      {canEdit && (
        <div
          onDragEnter={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(true);
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDragActive(false);
          }}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`mt-1 border-2 border-dashed rounded-lg px-4 py-6 text-center cursor-pointer transition-colors ${
            dragActive
              ? "border-purple-500 bg-purple-50/60"
              : "border-stone-300 bg-stone-50 hover:border-stone-400 hover:bg-stone-100/50"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.pptx,.xlsx,.txt,.md,.markdown"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = "";
            }}
            className="hidden"
          />
          <Upload
            size={20}
            className={`mx-auto mb-2 ${
              dragActive ? "text-purple-600" : "text-stone-400"
            }`}
          />
          <div
            className={`text-sm font-medium ${
              dragActive ? "text-purple-900" : "text-stone-700"
            }`}
          >
            {busy
              ? "Reading files…"
              : dragActive
              ? "Drop to upload"
              : "Drop files here, or click to browse"}
          </div>
          <div className="text-[11px] text-stone-500 mt-1">
            PDF, Word, PowerPoint, Excel, text, Markdown · up to ~5MB each
          </div>
        </div>
      )}

      {error && (
        <div className="mt-3 px-3 py-2 text-xs text-red-700 bg-red-50/60 border border-red-200 rounded">
          <strong>Couldn't read some files:</strong> {error}
        </div>
      )}

      {extractError && (
        <div className="mt-3 px-3 py-2 text-xs text-red-700 bg-red-50/60 border border-red-200 rounded">
          <strong>Extraction error:</strong> {extractError}
        </div>
      )}

      {lastExtraction && (
        <div
          className="mt-3 px-3 py-2.5 text-xs rounded flex items-center justify-between gap-3"
          style={{
            background: tokens.primaryTint,
            border: `1px solid ${tokens.primary}33`,
            color: tokens.primaryDark,
          }}
        >
          <div>
            <strong>
              Extracted {lastExtraction.total} item
              {lastExtraction.total === 1 ? "" : "s"}
            </strong>
            {" — "}
            {lastExtraction.counts.changes} changes,{" "}
            {lastExtraction.counts.audiences} audiences,{" "}
            {lastExtraction.counts.stakeholders} stakeholders,{" "}
            {lastExtraction.counts.interventions} interventions
          </div>
          {onOpenReview && (
            <button
              onClick={onOpenReview}
              className="text-xs font-semibold whitespace-nowrap px-2.5 py-1 rounded"
              style={{
                background: tokens.primary,
                color: "white",
                transition: tokens.transition.fast,
              }}
            >
              Review →
            </button>
          )}
        </div>
      )}

      {/* Extraction toolbar — shown when there are files */}
      {canEdit && files.length > 0 && (
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <button
            onClick={extractFromAll}
            disabled={anyExtracting}
            className="text-xs font-semibold flex items-center gap-1.5 px-3 py-1.5 rounded-md disabled:opacity-50 disabled:cursor-wait"
            style={{
              background: tokens.primary,
              color: "white",
              boxShadow: tokens.shadow.sm,
              transition: tokens.transition.fast,
            }}
            title="Run AI extraction across every uploaded file"
          >
            {anyExtracting ? (
              <span
                className="inline-block w-3 h-3 rounded-full animate-spin"
                style={{
                  border: "2px solid rgba(255,255,255,0.4)",
                  borderTopColor: "white",
                }}
              />
            ) : (
              <Sparkles size={12} />
            )}
            Extract entities from all files
          </button>
          {pendingCount > 0 && onOpenReview && (
            <button
              onClick={onOpenReview}
              className="text-xs font-medium flex items-center gap-1.5 px-3 py-1.5 rounded-md"
              style={{
                background: "white",
                border: `1px solid ${tokens.primary}66`,
                color: tokens.primaryDark,
                transition: tokens.transition.fast,
              }}
            >
              {pendingCount} pending review →
            </button>
          )}
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div className="mt-4 space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-1.5">
            Uploaded ({files.length})
          </div>
          {files.map((f) => {
            const isExtractingThis = extracting.has(f.id);
            return (
              <div
                key={f.id}
                className="flex items-center gap-3 px-3 py-2 bg-stone-50 border border-stone-200 rounded"
              >
                <div className="flex-1 min-w-0">
                  <div
                    className="text-sm font-medium text-stone-900 truncate"
                    title={f.filename}
                  >
                    {f.filename}
                  </div>
                  <div className="text-[10px] text-stone-500 mt-0.5">
                    {fmtSize(f.sizeBytes)} ·{" "}
                    {Math.round((f.text?.length || 0) / 1000)}k chars extracted
                    · added {f.addedAt?.slice(0, 10)}
                  </div>
                </div>
                {canEdit && (
                  <>
                    <button
                      onClick={() => extractFromFile(f)}
                      disabled={isExtractingThis || anyExtracting}
                      title="Extract changes, audiences, stakeholders and interventions from this document"
                      className="text-[11px] font-medium flex items-center gap-1 px-2 py-1 rounded disabled:opacity-50 disabled:cursor-wait"
                      style={{
                        background: "white",
                        border: `1px solid ${tokens.primary}55`,
                        color: tokens.primaryDark,
                        transition: tokens.transition.fast,
                      }}
                    >
                      {isExtractingThis ? (
                        <span
                          className="inline-block w-2.5 h-2.5 rounded-full animate-spin"
                          style={{
                            border: `2px solid ${tokens.primary}33`,
                            borderTopColor: tokens.primary,
                          }}
                        />
                      ) : (
                        <Sparkles size={10} />
                      )}
                      Extract
                    </button>
                    <button
                      onClick={() => removeFile(f.id)}
                      className="text-stone-400 hover:text-red-600 p-1"
                      title="Remove"
                    >
                      <X size={14} />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {files.length === 0 && !canEdit && (
        <div className="mt-3 px-3 py-3 text-xs text-stone-500 italic bg-stone-50 border border-stone-200 rounded text-center">
          No client context uploaded yet.
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PROPOSAL REVIEW — accept / edit / reject / merge AI-extracted proposals
// ============================================================================
// The consultant works through a queue of AI-suggested entities (changes,
// audiences, stakeholders, interventions). Each proposal can be:
//   - Accepted as-is → entity created, proposal marked 'applied'
//   - Edited → fields tweaked first, then accepted
//   - Rejected → proposal marked 'rejected' (kept in history so re-running
//                on the same doc doesn't suggest it again)
//   - Merged → for proposals flagged as possible duplicates, the existing
//              entity gets updated instead of a new one being created

function ProposalReview({ state, setState, role, onClose }) {
  const canEdit = role !== "client_viewer";
  const proposals = state.pendingProposals || [];
  const pending = proposals.filter((p) => p.status === "pending");
  const [tab, setTab] = useState("change");
  // Track which proposal is currently in edit mode (id → edited data)
  const [editing, setEditing] = useState({});
  const [filter, setFilter] = useState("pending"); // 'pending' | 'all'

  const tabs = [
    { id: "change", label: "Changes", color: tokens.primary },
    { id: "audience", label: "Audiences", color: "#0F766E" },
    { id: "stakeholder", label: "Stakeholders", color: "#9333EA" },
    { id: "intervention", label: "Interventions", color: "#EA580C" },
  ];

  const tabCounts = tabs.reduce((acc, t) => {
    acc[t.id] = pending.filter((p) => p.kind === t.id).length;
    return acc;
  }, {});

  // Auto-switch to the first tab that has items, so the consultant lands on
  // something useful instead of an empty tab.
  useEffect(() => {
    if (tabCounts[tab] === 0) {
      const firstNonEmpty = tabs.find((t) => tabCounts[t.id] > 0);
      if (firstNonEmpty) setTab(firstNonEmpty.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending.length]);

  const visibleProposals = proposals.filter(
    (p) => p.kind === tab && (filter === "all" ? true : p.status === "pending")
  );

  // ---- Mutation helpers --------------------------------------------------
  function updateProposalStatus(proposalId, status) {
    setState((p) => ({
      ...p,
      pendingProposals: (p.pendingProposals || []).map((prop) =>
        prop.id === proposalId ? { ...prop, status } : prop
      ),
    }));
  }

  function applyChangeProposal(prop) {
    const newId = uid("c");
    setState((p) => ({
      ...p,
      changes: [
        ...p.changes,
        {
          id: newId,
          name: prop.data.name,
          description: prop.data.description || "",
          isKey: !!prop.data.isKey,
        },
      ],
      pendingProposals: (p.pendingProposals || []).map((x) =>
        x.id === prop.id ? { ...x, status: "applied" } : x
      ),
    }));
  }

  function applyAudienceProposal(prop) {
    // Audiences need a parent CTI. If suggestedChange matches an existing change,
    // create a placeholder CTI under it. Otherwise, create a "Misc" change + CTI.
    const newAudId = uid("a");
    setState((p) => {
      let changes = [...p.changes];
      let ctis = [...p.changesToImplement];

      // Find or create the parent change
      let parentChangeId = null;
      const suggested = (prop.data.suggestedChange || "").toLowerCase().trim();
      if (suggested) {
        const matchedChange = changes.find(
          (c) => c.name.toLowerCase() === suggested
        );
        if (matchedChange) parentChangeId = matchedChange.id;
      }
      if (!parentChangeId) {
        // Auto-create a placeholder change so audiences without a clear parent still land somewhere visible
        parentChangeId = uid("c");
        changes.push({
          id: parentChangeId,
          name:
            prop.data.suggestedChange || "Extracted (review and reorganize)",
          description:
            "Auto-created to hold audiences extracted from a client document. Reorganize as needed.",
          isKey: false,
        });
      }
      // Find or create a CTI under that change (audiences must hang off a CTI)
      let parentCtiId = ctis.find((c) => c.changeId === parentChangeId)?.id;
      if (!parentCtiId) {
        parentCtiId = uid("ci");
        ctis.push({
          id: parentCtiId,
          changeId: parentChangeId,
          name: "General impacts",
          description: "",
          workstreamId: "",
          from: "",
          to: "",
        });
      }
      return {
        ...p,
        changes,
        changesToImplement: ctis,
        audiences: [
          ...p.audiences,
          {
            id: newAudId,
            changeToImplementId: parentCtiId,
            name: prop.data.name,
            impactDescription: prop.data.impactDescription || "",
            audienceType: "end_user",
            impactTypeIds: [],
            reactionIds: [],
            leverIds: [],
          },
        ],
        pendingProposals: (p.pendingProposals || []).map((x) =>
          x.id === prop.id ? { ...x, status: "applied" } : x
        ),
      };
    });
  }

  function applyStakeholderProposal(prop) {
    const newId = uid("s");
    setState((p) => ({
      ...p,
      stakeholders: [
        ...p.stakeholders,
        {
          id: newId,
          name: prop.data.name,
          role: prop.data.role || "",
          stakeholderType: "individual", // AI extraction defaults to individual; user can change in editor
          currentCommitment: 2,
          requiredCommitment: 3,
          abilityToImpact: prop.data.abilityToImpact || "medium",
          audienceLinkIds: [],
          engagementApproach: "",
          frequency: "",
          owner: "",
          materials: "",
        },
      ],
      pendingProposals: (p.pendingProposals || []).map((x) =>
        x.id === prop.id ? { ...x, status: "applied" } : x
      ),
    }));
  }

  function applyInterventionProposal(prop) {
    // Interventions need a parent audience and lever. Without those, we still
    // record the intervention but flag it as needing audience/lever assignment.
    const newId = uid("iv");
    let audienceId = "";
    const suggested = (prop.data.suggestedAudience || "").toLowerCase().trim();
    if (suggested) {
      const match = state.audiences.find(
        (a) => a.name.toLowerCase() === suggested
      );
      if (match) audienceId = match.id;
    }
    setState((p) => ({
      ...p,
      interventions: [
        ...p.interventions,
        {
          id: newId,
          audienceId,
          leverId: "",
          name: prop.data.name,
          description: prop.data.description || "",
          channel: "",
          owner: "",
          startDate: "",
          endDate: "",
          status: "planned",
          cadence: "one_time", // AI-proposed interventions default to one_time; user can change in the editor
        },
      ],
      pendingProposals: (p.pendingProposals || []).map((x) =>
        x.id === prop.id ? { ...x, status: "applied" } : x
      ),
    }));
  }

  function applyProposal(prop) {
    if (prop.kind === "change") applyChangeProposal(prop);
    else if (prop.kind === "audience") applyAudienceProposal(prop);
    else if (prop.kind === "stakeholder") applyStakeholderProposal(prop);
    else if (prop.kind === "intervention") applyInterventionProposal(prop);
  }

  function mergeProposal(prop) {
    if (!prop.dupOf) return;
    // Merge strategy: enrich the existing entity with any fields from the proposal
    // that the existing one is missing.
    setState((p) => {
      const enrichEntity = (collection, entityId) =>
        collection.map((e) => {
          if (e.id !== entityId) return e;
          const merged = { ...e };
          if (prop.kind === "change") {
            if (!merged.description && prop.data.description)
              merged.description = prop.data.description;
            if (!merged.isKey && prop.data.isKey) merged.isKey = true;
          } else if (prop.kind === "audience") {
            if (!merged.impactDescription && prop.data.impactDescription)
              merged.impactDescription = prop.data.impactDescription;
          } else if (prop.kind === "stakeholder") {
            if (!merged.role && prop.data.role) merged.role = prop.data.role;
            if (
              !merged.abilityToImpact ||
              merged.abilityToImpact === "medium"
            ) {
              if (prop.data.abilityToImpact)
                merged.abilityToImpact = prop.data.abilityToImpact;
            }
          } else if (prop.kind === "intervention") {
            if (!merged.description && prop.data.description)
              merged.description = prop.data.description;
          }
          return merged;
        });

      const next = { ...p };
      if (prop.kind === "change")
        next.changes = enrichEntity(p.changes, prop.dupOf);
      else if (prop.kind === "audience")
        next.audiences = enrichEntity(p.audiences, prop.dupOf);
      else if (prop.kind === "stakeholder")
        next.stakeholders = enrichEntity(p.stakeholders, prop.dupOf);
      else if (prop.kind === "intervention")
        next.interventions = enrichEntity(p.interventions, prop.dupOf);
      next.pendingProposals = (p.pendingProposals || []).map((x) =>
        x.id === prop.id ? { ...x, status: "merged" } : x
      );
      return next;
    });
  }

  function startEdit(prop) {
    setEditing((prev) => ({ ...prev, [prop.id]: { ...prop.data } }));
  }
  function cancelEdit(propId) {
    setEditing((prev) => {
      const n = { ...prev };
      delete n[propId];
      return n;
    });
  }
  function saveEditAndApply(prop) {
    const edited = editing[prop.id];
    if (!edited) return;
    const updatedProp = { ...prop, data: { ...prop.data, ...edited } };
    setState((p) => ({
      ...p,
      pendingProposals: (p.pendingProposals || []).map((x) =>
        x.id === prop.id ? updatedProp : x
      ),
    }));
    applyProposal(updatedProp);
    cancelEdit(prop.id);
  }

  function clearAllRejected() {
    if (
      !window.confirm(
        "Permanently remove all rejected proposals from history? They will no longer block re-extraction from the same documents."
      )
    )
      return;
    setState((p) => ({
      ...p,
      pendingProposals: (p.pendingProposals || []).filter(
        (x) => x.status !== "rejected"
      ),
    }));
  }

  function bulkAccept(kind) {
    const eligible = pending.filter((p) => p.kind === kind && !p.dupOf);
    if (eligible.length === 0) return;
    if (
      !window.confirm(
        `Accept all ${eligible.length} ${kind} proposal${
          eligible.length === 1 ? "" : "s"
        } as-is? (Duplicates are skipped — review those manually.)`
      )
    )
      return;
    eligible.forEach(applyProposal);
  }

  // ---- Lookup helper: find name of existing entity by id ----
  function existingName(kind, id) {
    if (!id) return "";
    if (kind === "change") return state.changes.find((c) => c.id === id)?.name;
    if (kind === "audience")
      return state.audiences.find((a) => a.id === id)?.name;
    if (kind === "stakeholder")
      return state.stakeholders.find((s) => s.id === id)?.name;
    if (kind === "intervention")
      return state.interventions.find((i) => i.id === id)?.name;
    return "";
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-medium mb-1">
            AI Extraction · Review queue
          </div>
          <h2
            className="text-3xl font-semibold text-stone-900"
            style={{
              fontFamily: "Georgia, serif",
              letterSpacing: "-0.015em",
              lineHeight: 1.15,
            }}
          >
            Proposed entities — your turn to decide
          </h2>
          <p className="text-sm text-stone-600 mt-2 max-w-2xl">
            The AI has extracted candidate entities from your client documents.
            Accept the good ones, edit when needed, reject what doesn't fit.
            Items flagged as possible duplicates can be merged into existing
            records.
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-xs text-stone-500 hover:text-stone-900 flex items-center gap-1"
          >
            <X size={14} /> Close
          </button>
        )}
      </div>

      {/* Empty state */}
      {proposals.length === 0 && (
        <div
          className="rounded-2xl p-12 text-center"
          style={{
            background: "white",
            border: "1.5px dashed #D6D3D1",
            boxShadow: tokens.shadow.xs,
          }}
        >
          <div
            className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
            style={{
              background: tokens.surface.panelMuted,
              boxShadow: tokens.shadow.xs,
            }}
          >
            <Sparkles size={22} className="text-stone-400" />
          </div>
          <div
            className="text-base font-semibold text-stone-800 mb-1.5"
            style={{ fontFamily: "Georgia, serif" }}
          >
            No proposals yet
          </div>
          <div className="text-[13px] text-stone-500 max-w-md mx-auto leading-relaxed">
            Upload a client document in the Context drawer, then click "Extract
            entities" to have the AI pull out changes, audiences, stakeholders,
            and interventions for you to review.
          </div>
        </div>
      )}

      {proposals.length > 0 && (
        <>
          {/* Tabs */}
          <div className="border-b border-stone-200 mb-5 flex gap-1 items-center">
            {tabs.map((t) => {
              const count = tabCounts[t.id];
              const isActive = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className="px-4 py-2 text-sm font-medium border-b-2 -mb-px flex items-center gap-2"
                  style={{
                    borderColor: isActive ? t.color : "transparent",
                    color: isActive ? t.color : "#6B7280",
                    transition: tokens.transition.fast,
                  }}
                >
                  {t.label}
                  {count > 0 && (
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                      style={{
                        background: isActive ? t.color : "#E7E5E4",
                        color: isActive ? "white" : "#57534E",
                      }}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-2">
              <label className="text-xs text-stone-600 flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filter === "all"}
                  onChange={(e) =>
                    setFilter(e.target.checked ? "all" : "pending")
                  }
                  className="accent-purple-700"
                />
                Show accepted / rejected
              </label>
              {canEdit && tabCounts[tab] > 1 && (
                <button
                  onClick={() => bulkAccept(tab)}
                  className="text-xs font-medium px-2.5 py-1 rounded"
                  style={{
                    background: tokens.surface.panelMuted,
                    color: tokens.primaryDark,
                    border: `1px solid ${tokens.primary}33`,
                  }}
                  title="Accept all non-duplicate proposals in this tab"
                >
                  Accept all non-duplicates
                </button>
              )}
            </div>
          </div>

          {/* Proposal list */}
          {visibleProposals.length === 0 ? (
            <div className="text-center py-12 text-sm text-stone-400 italic">
              {tabCounts[tab] === 0
                ? "No proposals in this category."
                : "All proposals in this category have been reviewed."}
            </div>
          ) : (
            <div className="space-y-3">
              {visibleProposals.map((prop) => {
                const isEditing = !!editing[prop.id];
                const isDup = !!prop.dupOf;
                const statusBg = {
                  pending: "white",
                  applied: "#F0FDF4",
                  rejected: "#FEF2F2",
                  merged: "#EFF6FF",
                };
                const statusLabel = {
                  applied: "✓ Added",
                  rejected: "✕ Rejected",
                  merged: "⇄ Merged",
                };
                return (
                  <div
                    key={prop.id}
                    className="rounded-lg p-4"
                    style={{
                      background: statusBg[prop.status] || "white",
                      border: `1px solid ${
                        isDup && prop.status === "pending"
                          ? "#F59E0B66"
                          : "rgb(229, 231, 235)"
                      }`,
                      boxShadow:
                        prop.status === "pending" ? tokens.shadow.xs : "none",
                      opacity: prop.status === "rejected" ? 0.6 : 1,
                      transition: tokens.transition.base,
                    }}
                  >
                    {/* Status banner */}
                    {prop.status !== "pending" && (
                      <div className="text-[10px] uppercase tracking-wider font-semibold mb-2 text-stone-600">
                        {statusLabel[prop.status]}
                        {prop.status === "merged" && prop.dupOf && (
                          <span className="text-stone-500 ml-1">
                            into "{existingName(prop.kind, prop.dupOf)}"
                          </span>
                        )}
                      </div>
                    )}

                    {/* Duplicate flag */}
                    {isDup && prop.status === "pending" && (
                      <div
                        className="text-[11px] mb-2 px-2 py-1.5 rounded flex items-center gap-1.5"
                        style={{
                          background: "#FFFBEB",
                          color: "#92400E",
                          border: "1px solid #FCD34D",
                        }}
                      >
                        <AlertCircle size={12} />
                        <span>
                          Possible duplicate of existing{" "}
                          <strong>{existingName(prop.kind, prop.dupOf)}</strong>{" "}
                          — merge to enrich the existing record instead of
                          creating a new one.
                        </span>
                      </div>
                    )}

                    {/* Source attribution */}
                    <div className="text-[10px] uppercase tracking-wider text-stone-400 mb-1.5">
                      from {prop.source}
                    </div>

                    {/* Editable / readonly content */}
                    {!isEditing ? (
                      <ProposalCardContent prop={prop} />
                    ) : (
                      <ProposalCardEditor
                        prop={prop}
                        edited={editing[prop.id]}
                        onChange={(d) =>
                          setEditing((prev) => ({
                            ...prev,
                            [prop.id]: { ...prev[prop.id], ...d },
                          }))
                        }
                      />
                    )}

                    {/* Source quote */}
                    {prop.sourceQuote && (
                      <div className="mt-2.5 text-[11px] text-stone-500 italic border-l-2 border-stone-200 pl-3 py-0.5">
                        "{prop.sourceQuote}"
                      </div>
                    )}

                    {/* Action buttons — only on pending */}
                    {prop.status === "pending" && canEdit && (
                      <div className="mt-3 pt-3 border-t border-stone-100 flex items-center gap-2 flex-wrap">
                        {!isEditing ? (
                          <>
                            {isDup ? (
                              <button
                                onClick={() => mergeProposal(prop)}
                                className="text-xs font-semibold px-3 py-1.5 rounded-md text-white flex items-center gap-1.5"
                                style={{
                                  background: "#2563EB",
                                  boxShadow: tokens.shadow.sm,
                                }}
                              >
                                <Check size={11} /> Merge into existing
                              </button>
                            ) : (
                              <button
                                onClick={() => applyProposal(prop)}
                                className="text-xs font-semibold px-3 py-1.5 rounded-md text-white flex items-center gap-1.5"
                                style={{
                                  background: tokens.primary,
                                  boxShadow: tokens.shadow.sm,
                                }}
                              >
                                <Check size={11} /> Accept
                              </button>
                            )}
                            <button
                              onClick={() => startEdit(prop)}
                              className="text-xs font-medium px-3 py-1.5 rounded-md flex items-center gap-1.5"
                              style={{
                                background: "white",
                                border: "1px solid rgb(214, 211, 209)",
                                color: "#44403C",
                              }}
                            >
                              Edit first
                            </button>
                            <button
                              onClick={() =>
                                updateProposalStatus(prop.id, "rejected")
                              }
                              className="text-xs font-medium px-3 py-1.5 rounded-md ml-auto"
                              style={{
                                background: "white",
                                border: "1px solid rgb(214, 211, 209)",
                                color: "#A8A29E",
                              }}
                            >
                              Reject
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => saveEditAndApply(prop)}
                              className="text-xs font-semibold px-3 py-1.5 rounded-md text-white flex items-center gap-1.5"
                              style={{
                                background: tokens.primary,
                                boxShadow: tokens.shadow.sm,
                              }}
                            >
                              <Check size={11} /> Save and accept
                            </button>
                            <button
                              onClick={() => cancelEdit(prop.id)}
                              className="text-xs font-medium px-3 py-1.5 rounded-md"
                              style={{
                                background: "white",
                                border: "1px solid rgb(214, 211, 209)",
                                color: "#44403C",
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer utilities */}
          {canEdit && proposals.some((p) => p.status === "rejected") && (
            <div className="mt-8 pt-4 border-t border-stone-200 flex justify-end">
              <button
                onClick={clearAllRejected}
                className="text-xs text-stone-500 hover:text-red-700"
              >
                Clear rejected from history (
                {proposals.filter((p) => p.status === "rejected").length})
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Read-only card content per proposal kind
function ProposalCardContent({ prop }) {
  if (prop.kind === "change") {
    return (
      <div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-stone-900">
            {prop.data.name}
          </span>
          {prop.data.isKey && (
            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">
              Key
            </span>
          )}
        </div>
        {prop.data.description && (
          <div className="text-xs text-stone-600 mt-1.5 leading-relaxed">
            {prop.data.description}
          </div>
        )}
      </div>
    );
  }
  if (prop.kind === "audience") {
    return (
      <div>
        <div className="text-sm font-semibold text-stone-900">
          {prop.data.name}
        </div>
        {prop.data.impactDescription && (
          <div className="text-xs text-stone-600 mt-1.5 leading-relaxed">
            {prop.data.impactDescription}
          </div>
        )}
        {prop.data.suggestedChange && (
          <div className="text-[11px] text-stone-500 mt-2">
            → suggested under change:{" "}
            <span className="font-medium text-stone-700">
              {prop.data.suggestedChange}
            </span>
          </div>
        )}
      </div>
    );
  }
  if (prop.kind === "stakeholder") {
    const impactColor =
      { high: "#DC2626", medium: "#D97706", low: "#65A30D" }[
        prop.data.abilityToImpact
      ] || "#78716C";
    return (
      <div>
        <div className="text-sm font-semibold text-stone-900">
          {prop.data.name}
        </div>
        {prop.data.role && (
          <div className="text-xs text-stone-600 mt-0.5">{prop.data.role}</div>
        )}
        <div className="text-[11px] mt-1.5 flex items-center gap-1.5">
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: impactColor }}
          />
          <span className="text-stone-600">
            Ability to impact:{" "}
            <span
              className="font-medium capitalize"
              style={{ color: impactColor }}
            >
              {prop.data.abilityToImpact}
            </span>
          </span>
        </div>
      </div>
    );
  }
  if (prop.kind === "intervention") {
    return (
      <div>
        <div className="text-sm font-semibold text-stone-900">
          {prop.data.name}
        </div>
        {prop.data.description && (
          <div className="text-xs text-stone-600 mt-1.5 leading-relaxed">
            {prop.data.description}
          </div>
        )}
        {prop.data.suggestedAudience && (
          <div className="text-[11px] text-stone-500 mt-2">
            → suggested for audience:{" "}
            <span className="font-medium text-stone-700">
              {prop.data.suggestedAudience}
            </span>
          </div>
        )}
      </div>
    );
  }
  return null;
}

// Inline editor for a proposal before accepting
function ProposalCardEditor({ prop, edited, onChange }) {
  const inputCls =
    "w-full text-sm border border-stone-300 rounded p-2 bg-white focus:outline-none focus:border-purple-400";
  if (prop.kind === "change") {
    return (
      <div className="space-y-2">
        <input
          value={edited.name || ""}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Change name"
          className={inputCls}
        />
        <textarea
          value={edited.description || ""}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Description"
          rows={2}
          className={`${inputCls} resize-none`}
        />
        <label className="flex items-center gap-2 text-xs text-stone-700">
          <input
            type="checkbox"
            checked={!!edited.isKey}
            onChange={(e) => onChange({ isKey: e.target.checked })}
            className="accent-amber-500"
          />
          Key change
        </label>
      </div>
    );
  }
  if (prop.kind === "audience") {
    return (
      <div className="space-y-2">
        <input
          value={edited.name || ""}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Audience name"
          className={inputCls}
        />
        <textarea
          value={edited.impactDescription || ""}
          onChange={(e) => onChange({ impactDescription: e.target.value })}
          placeholder="What changes for this audience"
          rows={2}
          className={`${inputCls} resize-none`}
        />
        <input
          value={edited.suggestedChange || ""}
          onChange={(e) => onChange({ suggestedChange: e.target.value })}
          placeholder="Suggested parent change (optional)"
          className={inputCls}
        />
      </div>
    );
  }
  if (prop.kind === "stakeholder") {
    return (
      <div className="space-y-2">
        <input
          value={edited.name || ""}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Stakeholder name"
          className={inputCls}
        />
        <input
          value={edited.role || ""}
          onChange={(e) => onChange({ role: e.target.value })}
          placeholder="Role / title"
          className={inputCls}
        />
        <select
          value={edited.abilityToImpact || "medium"}
          onChange={(e) => onChange({ abilityToImpact: e.target.value })}
          className={inputCls}
        >
          <option value="high">High ability to impact</option>
          <option value="medium">Medium ability to impact</option>
          <option value="low">Low ability to impact</option>
        </select>
      </div>
    );
  }
  if (prop.kind === "intervention") {
    return (
      <div className="space-y-2">
        <input
          value={edited.name || ""}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="Intervention name"
          className={inputCls}
        />
        <textarea
          value={edited.description || ""}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="What this intervention does"
          rows={2}
          className={`${inputCls} resize-none`}
        />
        <input
          value={edited.suggestedAudience || ""}
          onChange={(e) => onChange({ suggestedAudience: e.target.value })}
          placeholder="Suggested audience (optional)"
          className={inputCls}
        />
      </div>
    );
  }
  return null;
}

// ============================================================================
// SETTINGS / DEFINE — project info, client context, terminology overrides
// ============================================================================
function SettingsView({ state, setState, role, setView }) {
  const canEdit = role !== "client_viewer";
  const [tab, setTab] = useState("project");

  function updateProject(field, value) {
    setState((p) => ({ ...p, project: { ...p.project, [field]: value } }));
  }
  function setOverride(taxonomyKey, rowId, label) {
    setState((p) => {
      const next = { ...p };
      next.terminologyOverrides = { ...p.terminologyOverrides };
      next.terminologyOverrides[taxonomyKey] = {
        ...(p.terminologyOverrides[taxonomyKey] || {}),
      };
      if (label) next.terminologyOverrides[taxonomyKey][rowId] = label;
      else delete next.terminologyOverrides[taxonomyKey][rowId];
      return next;
    });
  }

  const taxonomyKeys = [
    { key: "roles", label: "Roles" },
    { key: "functions", label: "Functions" },
    { key: "levels", label: "Levels" },
    { key: "workstreams", label: "Workstreams" },
    { key: "audienceSizes", label: "Audience Sizes" },
    { key: "impactLevels", label: "Impact Levels" },
    { key: "effortLevels", label: "Effort Levels" },
    { key: "reactionTypes", label: "Reaction Types" },
    { key: "impactTypes", label: "Impact Types" },
    { key: "changeLevers", label: "Change Levers" },
  ];
  const [activeTax, setActiveTax] = useState(taxonomyKeys[0].key);

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-medium mb-1">
          Define · Project Setup
        </div>
        <h2
          className="text-3xl font-semibold text-stone-900"
          style={{
            fontFamily: "Georgia, serif",
            letterSpacing: "-0.015em",
            lineHeight: 1.15,
          }}
        >
          Project & Terminology
        </h2>
        <p className="text-sm text-stone-600 mt-2 max-w-2xl">
          Set the project basics, drop in the client's background materials so
          the AI has context to work with, and (optionally) rename WTW's
          vocabulary to match the client's language.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-stone-200 mb-6 flex gap-1">
        {[
          { id: "project", label: "Project info" },
          {
            id: "context",
            label: "Client context",
            count: (state.clientContext || []).length,
          },
          {
            id: "terminology",
            label: "Terminology",
            count: Object.values(state.terminologyOverrides || {}).reduce(
              (s, o) => s + Object.keys(o).length,
              0
            ),
          },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-purple-700 text-purple-900"
                : "border-transparent text-stone-600 hover:text-stone-900"
            }`}
          >
            {t.label}
            {t.count > 0 && (
              <span className="ml-2 text-[10px] bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded">
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* PROJECT INFO TAB */}
      {tab === "project" && (
        <div className="border border-stone-200 rounded-lg bg-white p-6">
          <div className="grid grid-cols-2 gap-5">
            <Field label="Project name">
              <input
                disabled={!canEdit}
                value={state.project.name || ""}
                onChange={(e) => updateProject("name", e.target.value)}
                placeholder="e.g. ACME Q3 ERP Migration"
                className="w-full text-sm border border-stone-200 rounded p-2 bg-white focus:outline-none focus:border-purple-400"
              />
            </Field>
            <Field label="Client">
              <input
                disabled={!canEdit}
                value={state.project.client || ""}
                onChange={(e) => updateProject("client", e.target.value)}
                placeholder="Client organization name"
                className="w-full text-sm border border-stone-200 rounded p-2 bg-white focus:outline-none focus:border-purple-400"
              />
            </Field>
          </div>
          <Field label="Description" className="mt-5">
            <textarea
              disabled={!canEdit}
              value={state.project.description || ""}
              onChange={(e) => updateProject("description", e.target.value)}
              rows={3}
              placeholder="A short paragraph about what this change is and why. The AI uses this on every prompt."
              className="w-full text-sm border border-stone-200 rounded p-2 bg-white focus:outline-none focus:border-purple-400 resize-none"
            />
          </Field>
          <div className="grid grid-cols-2 gap-5 mt-5">
            <Field label="Start date">
              <input
                disabled={!canEdit}
                type="date"
                value={state.project.startDate || ""}
                onChange={(e) => updateProject("startDate", e.target.value)}
                className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
              />
            </Field>
            <Field label="End date">
              <input
                disabled={!canEdit}
                type="date"
                value={state.project.endDate || ""}
                onChange={(e) => updateProject("endDate", e.target.value)}
                className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
              />
            </Field>
          </div>

          <div className="mt-8 border-t border-stone-200 pt-5 text-[12px] text-stone-500">
            <strong className="text-stone-700">Tip:</strong> next, head to the{" "}
            <em>Client context</em> tab to upload the client's strategy docs,
            briefing memos, and any other background. The AI will use them on
            every draft.
          </div>
        </div>
      )}

      {/* CLIENT CONTEXT TAB */}
      {tab === "context" && (
        <ClientContextPanel
          state={state}
          setState={setState}
          canEdit={canEdit}
          onOpenReview={setView ? () => setView("review") : undefined}
        />
      )}

      {/* TERMINOLOGY TAB */}
      {tab === "terminology" && (
        <div>
          <p className="text-sm text-stone-600 mb-4 max-w-2xl">
            Override the default WTW vocabulary with your client's language. The
            canonical taxonomy stays unchanged — only the display labels are
            localized to this project. Toggle "Client terms" in the header to
            see overrides applied throughout the app.
          </p>
          <div className="grid grid-cols-[200px_1fr] gap-6">
            <nav className="flex flex-col gap-0.5">
              {taxonomyKeys.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTax(t.key)}
                  className={`text-left text-sm px-3 py-2 rounded ${
                    activeTax === t.key
                      ? "bg-stone-900 text-white"
                      : "hover:bg-stone-100 text-stone-700"
                  }`}
                >
                  {t.label}
                  {state.terminologyOverrides[t.key] &&
                    Object.keys(state.terminologyOverrides[t.key]).length >
                      0 && (
                      <span className="ml-2 text-[10px] bg-purple-100 text-purple-800 px-1.5 py-0.5 rounded">
                        {Object.keys(state.terminologyOverrides[t.key]).length}
                      </span>
                    )}
                </button>
              ))}
            </nav>

            <div className="border border-stone-200 rounded-lg bg-white">
              <div className="grid grid-cols-2 px-4 py-2.5 border-b border-stone-200 bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
                <div>Default (WTW)</div>
                <div>Client-specific label</div>
              </div>
              {state.taxonomies[activeTax].map((row) => {
                const override =
                  state.terminologyOverrides[activeTax]?.[row.id] || "";
                return (
                  <div
                    key={row.id}
                    className="grid grid-cols-2 px-4 py-2.5 border-b border-stone-100 items-center gap-3"
                  >
                    <div className="text-sm text-stone-800">{row.label}</div>
                    <input
                      disabled={!canEdit}
                      value={override}
                      onChange={(e) =>
                        setOverride(activeTax, row.id, e.target.value)
                      }
                      placeholder="(use default)"
                      className="w-full text-sm border border-stone-200 rounded px-2.5 py-1.5 focus:outline-none focus:border-purple-400 placeholder:text-stone-400"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// STAKEHOLDERS — Identification, 2×2 map, and engagement plan
// ============================================================================
function StakeholdersView({ state, setState, role }) {
  const canEdit = role !== "client_viewer";
  const [selectedId, setSelectedId] = useState(
    state.stakeholders[0]?.id || null
  );
  const selected = state.stakeholders.find((s) => s.id === selectedId);

  function update(updated) {
    setState((p) => ({
      ...p,
      stakeholders: p.stakeholders.map((s) =>
        s.id === updated.id ? updated : s
      ),
    }));
  }
  function remove(id) {
    setState((p) => ({
      ...p,
      stakeholders: p.stakeholders.filter((s) => s.id !== id),
    }));
    setSelectedId(null);
  }
  function add() {
    const id = uid("s");
    setState((p) => ({
      ...p,
      stakeholders: [
        ...p.stakeholders,
        {
          id,
          name: "New stakeholder",
          role: "",
          stakeholderType: "individual",
          currentCommitment: 2,
          requiredCommitment: 3,
          abilityToImpact: "medium",
          audienceLinkIds: [],
          engagementApproach: "",
          frequency: "",
          owner: "",
          materials: "",
        },
      ],
    }));
    setSelectedId(id);
  }

  // Quadrant for the 2×2: low commit + low impact = Bystander, high impact + low commit = Barrier, high impact + high commit = Champion, low impact + high commit = Advocate
  function quadrantOf(s) {
    const hiImpact = s.abilityToImpact === "high";
    const hiCommit = (s.currentCommitment || 0) >= 3;
    if (hiImpact && hiCommit) return "champion";
    if (hiImpact && !hiCommit) return "barrier";
    if (!hiImpact && hiCommit) return "advocate";
    return "bystander";
  }

  const quadrants = {
    // Colors match the WTW Stakeholder ID & Mapping reference template
    champion: {
      label: "Champions",
      desc: "Critical to success, and highly committed. Expect them to play the role of champion.",
      color: "#0E9F8C",
      bg: "#5EEAD4",
    },
    barrier: {
      label: "Barriers",
      desc: "Critical to success, but commitment is low. Get them more committed, or they could be barriers to success.",
      color: "#B91C5C",
      bg: "#F472B6",
    },
    advocate: {
      label: "Advocates",
      desc: "Not critical to success, but highly committed. Expect them to play the role of advocate.",
      color: "#0E9F8C",
      bg: "#99F6E4",
    },
    bystander: {
      label: "Bystanders",
      desc: "Not very critical to success, and commitment is low. You may not need to act unless they become more critical.",
      color: "#57534E",
      bg: "#E7E5E4",
    },
  };

  // Map a stakeholder's (abilityToImpact, currentCommitment) → quadrant key.
  // Reference: Barriers (top-left), Champions (top-right), Bystanders (bottom-left), Advocates (bottom-right)
  const stakeholdersByQuad = {
    champion: state.stakeholders.filter((s) => quadrantOf(s) === "champion"),
    advocate: state.stakeholders.filter((s) => quadrantOf(s) === "advocate"),
    barrier: state.stakeholders.filter((s) => quadrantOf(s) === "barrier"),
    bystander: state.stakeholders.filter((s) => quadrantOf(s) === "bystander"),
  };

  // Auto-size the 2×2 grid based on the densest quadrant — keeps the chart compact
  // when there are few stakeholders and prevents in-cell scroll when there are many.
  // Each quadrant needs ~94px chrome (title block + count badge + padding) plus
  // ~24px per stakeholder chip. Each cell is half the grid height, so the grid
  // height = 2 × densest-cell content height, clamped between 360px and 900px.
  const maxPerQuad = Math.max(
    stakeholdersByQuad.champion.length,
    stakeholdersByQuad.advocate.length,
    stakeholdersByQuad.barrier.length,
    stakeholdersByQuad.bystander.length,
    1 // ensure at least one row's worth of space even when all quadrants are empty
  );
  const cellChrome = 94;
  const chipHeight = 24;
  const cellContent = cellChrome + maxPerQuad * chipHeight;
  const gridHeight = Math.min(900, Math.max(360, cellContent * 2));

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-medium mb-1">
            Discover · Stakeholders
          </div>
          <h2
            className="text-3xl font-semibold text-stone-900"
            style={{
              fontFamily: "Georgia, serif",
              letterSpacing: "-0.015em",
              lineHeight: 1.15,
            }}
          >
            Who shapes this change
          </h2>
          <p className="text-sm text-stone-600 mt-2 max-w-2xl">
            Stakeholders <em>influence</em> the change — leaders, sponsors,
            regulators, key partners. (Audiences <em>experience</em> the change
            — managed separately in Change Impact.)
          </p>
        </div>
        {canEdit && (
          <button
            onClick={add}
            className="text-xs text-white px-3 py-1.5 rounded font-medium flex items-center gap-1.5"
            style={{ background: tokens.primary }}
          >
            <Plus size={12} /> Stakeholder
          </button>
        )}
      </div>

      {/* 2x2 quadrant map — matches WTW reference layout:
            ┌──────────┬───────────┐
            │ Barriers │ Champions │   ← high ability to impact
            ├──────────┼───────────┤
            │ Bystndrs │ Advocates │   ← low ability to impact
            └──────────┴───────────┘
              low commit  high commit
       */}
      <div className="border border-stone-200 rounded-lg bg-white p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-sm font-semibold text-stone-900">
              Stakeholder map
            </div>
            <div className="text-[11px] text-stone-500">
              Ability to impact project success (y-axis) × Level of commitment
              to project success (x-axis)
            </div>
          </div>
          <div className="text-[10px] text-stone-400">
            Click any name to edit →
          </div>
        </div>

        {/* Layout: [y-axis labels column | quadrants grid]
                   below: spacer | x-axis labels
            The grid auto-sizes based on the densest quadrant so a small project doesn't
            waste space and a big one doesn't force scroll inside cells. Per-quadrant
            content needs: ~70px header (title + desc + count) + ~24px per stakeholder
            chip + ~16px padding. Each cell is half the grid height, so we double that
            and clamp between sensible min/max. */}
        <div className="grid grid-cols-[60px_1fr] gap-2">
          {/* Y-axis: 'High' at top, axis title centered, 'Low' at bottom */}
          <div
            className="flex flex-col justify-between items-end pr-1 text-[10px] font-semibold text-stone-700 uppercase tracking-wider"
            style={{ minHeight: gridHeight }}
          >
            <span>High</span>
            <span
              className="self-center -rotate-90 whitespace-nowrap text-stone-500"
              style={{ transformOrigin: "center" }}
            >
              Ability to impact →
            </span>
            <span>Low</span>
          </div>

          {/* Quadrants */}
          <div
            className="grid grid-cols-2 grid-rows-2 gap-0"
            style={{ minHeight: gridHeight }}
          >
            {/* Top-left: BARRIERS (high impact, low commitment) */}
            <Quadrant
              q={quadrants.barrier}
              stakeholders={stakeholdersByQuad.barrier}
              selectedId={selectedId}
              onSelect={setSelectedId}
              position="tl"
            />
            {/* Top-right: CHAMPIONS (high impact, high commitment) */}
            <Quadrant
              q={quadrants.champion}
              stakeholders={stakeholdersByQuad.champion}
              selectedId={selectedId}
              onSelect={setSelectedId}
              position="tr"
            />
            {/* Bottom-left: BYSTANDERS (low impact, low commitment) */}
            <Quadrant
              q={quadrants.bystander}
              stakeholders={stakeholdersByQuad.bystander}
              selectedId={selectedId}
              onSelect={setSelectedId}
              position="bl"
            />
            {/* Bottom-right: ADVOCATES (low impact, high commitment) */}
            <Quadrant
              q={quadrants.advocate}
              stakeholders={stakeholdersByQuad.advocate}
              selectedId={selectedId}
              onSelect={setSelectedId}
              position="br"
            />
          </div>
        </div>

        {/* X-axis: 'Low' at left, axis title centered, 'High' at right */}
        <div className="grid grid-cols-[60px_1fr] gap-2 mt-1">
          <div />
          <div className="flex justify-between items-center text-[10px] font-semibold text-stone-700 uppercase tracking-wider">
            <span>Low</span>
            <span className="text-stone-500 normal-case tracking-normal">
              Level of commitment to project success →
            </span>
            <span>High</span>
          </div>
        </div>
      </div>

      {/* Engagement plan + editor */}
      <div className="grid grid-cols-[1fr_1.4fr] gap-6">
        {/* Stakeholder list */}
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-stone-200 bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500 font-semibold flex items-center justify-between">
            <span>All stakeholders ({state.stakeholders.length})</span>
          </div>
          <div className="max-h-[500px] overflow-y-auto">
            {state.stakeholders.map((s) => {
              const isSelected = s.id === selectedId;
              const q = quadrants[quadrantOf(s)];
              const gap =
                (s.requiredCommitment || 0) - (s.currentCommitment || 0);
              // Group stakeholders get a Users icon; individuals get a User icon.
              // Default to individual when stakeholderType is missing (back-compat).
              const isGroup = s.stakeholderType === "group";
              const IconC = isGroup ? Users : User;
              return (
                <div
                  key={s.id}
                  onClick={() => setSelectedId(s.id)}
                  className={`group relative px-4 py-2.5 border-b border-stone-100 cursor-pointer ${
                    isSelected ? "bg-purple-50" : "hover:bg-stone-50"
                  }`}
                >
                  <div className="flex items-center gap-2 pr-6">
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: q.color }}
                    />
                    <IconC
                      size={13}
                      className="flex-shrink-0"
                      style={{ color: isGroup ? "#7C3AED" : "#78716C" }}
                      aria-label={
                        isGroup ? "Group stakeholder" : "Individual stakeholder"
                      }
                    />
                    <span className="text-sm font-medium text-stone-900 flex-1 truncate">
                      {s.name}
                    </span>
                    {gap > 0 && (
                      <span
                        className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold"
                        title={`Need to move from ${s.currentCommitment} to ${s.requiredCommitment}`}
                      >
                        +{gap} gap
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-stone-500 mt-0.5 ml-4 truncate">
                    {s.role}
                  </div>
                  {canEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (
                          window.confirm(
                            `Delete "${s.name}"? This cannot be undone.`
                          )
                        )
                          remove(s.id);
                      }}
                      title="Delete stakeholder"
                      className="absolute top-2 right-2 w-6 h-6 rounded flex items-center justify-center text-stone-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Editor */}
        {selected ? (
          <div className="border border-stone-200 rounded-lg bg-white p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-stone-700 font-semibold">
                {selected.stakeholderType === "group"
                  ? "Stakeholder group"
                  : "Individual stakeholder"}
              </div>
              {/* Type toggle — flip between individual and group. Default to
                  individual if missing for backwards compat. */}
              <div className="flex items-center gap-1 text-[10px]">
                <button
                  disabled={!canEdit}
                  onClick={() =>
                    update({ ...selected, stakeholderType: "individual" })
                  }
                  className={`px-2 py-1 rounded flex items-center gap-1 transition-colors ${
                    (selected.stakeholderType || "individual") === "individual"
                      ? "bg-stone-900 text-white"
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                  title="Mark as a named individual"
                >
                  <User size={10} /> Individual
                </button>
                <button
                  disabled={!canEdit}
                  onClick={() =>
                    update({ ...selected, stakeholderType: "group" })
                  }
                  className={`px-2 py-1 rounded flex items-center gap-1 transition-colors ${
                    selected.stakeholderType === "group"
                      ? "text-white"
                      : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                  }`}
                  style={
                    selected.stakeholderType === "group"
                      ? { background: "#7C3AED" }
                      : {}
                  }
                  title="Mark as a group, team, or function"
                >
                  <Users size={10} /> Group
                </button>
              </div>
            </div>
            <input
              disabled={!canEdit}
              value={selected.name}
              onChange={(e) => update({ ...selected, name: e.target.value })}
              className="text-lg font-semibold text-stone-900 bg-transparent w-full focus:outline-none focus:bg-stone-50 px-2 -ml-2 py-1 rounded"
            />

            <Field
              label={
                selected.stakeholderType === "group"
                  ? "Description"
                  : "Role / Position"
              }
              className="mt-4"
            >
              <input
                disabled={!canEdit}
                value={selected.role || ""}
                onChange={(e) => update({ ...selected, role: e.target.value })}
                placeholder={
                  selected.stakeholderType === "group"
                    ? "What is this group? Who's in it? Approximate size?"
                    : "Their title and reporting line"
                }
                className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
              />
            </Field>

            <div className="grid grid-cols-3 gap-3 mt-4">
              <Field label="Ability to impact">
                <select
                  disabled={!canEdit}
                  value={selected.abilityToImpact || "medium"}
                  onChange={(e) =>
                    update({ ...selected, abilityToImpact: e.target.value })
                  }
                  className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </Field>
              <Field label="Current commitment (1–4)">
                <input
                  disabled={!canEdit}
                  type="number"
                  min="1"
                  max="4"
                  value={selected.currentCommitment || 2}
                  onChange={(e) =>
                    update({
                      ...selected,
                      currentCommitment: parseInt(e.target.value) || 2,
                    })
                  }
                  className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                />
              </Field>
              <Field label="Required commitment (1–4)">
                <input
                  disabled={!canEdit}
                  type="number"
                  min="1"
                  max="4"
                  value={selected.requiredCommitment || 3}
                  onChange={(e) =>
                    update({
                      ...selected,
                      requiredCommitment: parseInt(e.target.value) || 3,
                    })
                  }
                  className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                />
              </Field>
            </div>

            <div className="mt-6 border-t border-stone-200 pt-4">
              <div className="text-xs font-semibold text-stone-900 mb-3">
                Engagement plan
              </div>
              <Field label="Approach" subtitle="How will you engage them?">
                <textarea
                  disabled={!canEdit}
                  value={selected.engagementApproach || ""}
                  onChange={(e) =>
                    update({ ...selected, engagementApproach: e.target.value })
                  }
                  rows={2}
                  className="w-full text-sm border border-stone-200 rounded p-2 bg-white resize-none"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Field label="Frequency">
                  <input
                    disabled={!canEdit}
                    value={selected.frequency || ""}
                    onChange={(e) =>
                      update({ ...selected, frequency: e.target.value })
                    }
                    placeholder="Weekly, monthly, at milestones…"
                    className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                  />
                </Field>
                <Field label="Owner">
                  <input
                    disabled={!canEdit}
                    value={selected.owner || ""}
                    onChange={(e) =>
                      update({ ...selected, owner: e.target.value })
                    }
                    className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                  />
                </Field>
              </div>
              <Field label="Supporting materials" className="mt-3">
                <input
                  disabled={!canEdit}
                  value={selected.materials || ""}
                  onChange={(e) =>
                    update({ ...selected, materials: e.target.value })
                  }
                  className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                />
              </Field>
            </div>

            <div className="mt-6 border-t border-stone-200 pt-4">
              <div className="text-xs font-semibold text-stone-900 mb-2">
                Linked audiences
              </div>
              <div className="text-[11px] text-stone-500 mb-2">
                Audiences this stakeholder influences or represents
              </div>
              <div className="flex flex-wrap gap-2">
                {state.audiences.map((a) => {
                  const linked = (selected.audienceLinkIds || []).includes(
                    a.id
                  );
                  return (
                    <button
                      key={a.id}
                      disabled={!canEdit}
                      onClick={() => {
                        const next = linked
                          ? (selected.audienceLinkIds || []).filter(
                              (x) => x !== a.id
                            )
                          : [...(selected.audienceLinkIds || []), a.id];
                        update({ ...selected, audienceLinkIds: next });
                      }}
                      className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                        linked
                          ? "bg-stone-900 text-white border-stone-900"
                          : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
                      }`}
                    >
                      {a.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {canEdit && (
              <button
                onClick={() => remove(selected.id)}
                className="mt-6 text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
              >
                <Trash2 size={12} /> Delete this stakeholder
              </button>
            )}
          </div>
        ) : (
          <div className="border border-stone-200 rounded-lg bg-stone-50 p-8 text-center text-sm text-stone-500">
            Select a stakeholder from the list to view or edit their engagement
            plan.
          </div>
        )}
      </div>
    </div>
  );
}

function Quadrant({ q, stakeholders, selectedId, onSelect, position }) {
  // Borders between cells form the 2×2 grid lines
  const borders = {
    tl: "border-r-2 border-b-2",
    tr: "border-b-2",
    bl: "border-r-2",
    br: "",
  };
  return (
    <div
      className={`flex flex-col ${borders[position]} border-white p-4 overflow-hidden`}
      style={{ background: q.bg }}
    >
      <div className="text-center mb-2">
        <div className="text-sm font-bold text-stone-900">{q.label}</div>
        <div className="text-[10px] text-stone-700 mt-0.5 leading-snug px-2">
          {q.desc}
        </div>
      </div>
      <div className="text-[10px] text-stone-700 mb-1.5 text-right">
        {stakeholders.length}{" "}
        {stakeholders.length === 1 ? "stakeholder" : "stakeholders"}
      </div>
      {/* Scrollable list — uses flex-1 + overflow-y-auto so long quadrants stay inside their cell */}
      <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
        {stakeholders.map((s) => {
          const isSel = s.id === selectedId;
          const isGroup = s.stakeholderType === "group";
          const IconC = isGroup ? Users : User;
          return (
            <div
              key={s.id}
              onClick={() => onSelect(s.id)}
              className={`text-[11px] px-2 py-1 rounded cursor-pointer truncate flex items-center gap-1.5 ${
                isSel
                  ? "bg-stone-900 text-white"
                  : "bg-white/80 text-stone-800 hover:bg-white border border-white/60"
              }`}
              title={s.role || s.name}
            >
              <IconC
                size={11}
                className="flex-shrink-0"
                style={{ opacity: isSel ? 0.85 : 0.65 }}
              />
              <span className="truncate">{s.name}</span>
            </div>
          );
        })}
        {stakeholders.length === 0 && (
          <div className="text-[10px] text-stone-600 italic text-center mt-2">
            No stakeholders in this quadrant
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// CHANGE MANAGEMENT PLAN — Gantt-by-lever (the WTW signature output)
// ============================================================================
function CMPlanView({ state, setState, role }) {
  const canEdit = role !== "client_viewer";
  const [selectedId, setSelectedId] = useState(null);
  const selected = state.interventions.find((i) => i.id === selectedId);

  // Filter state — each is an array of selected IDs/values (empty = "all")
  const [filters, setFilters] = useState({
    audienceIds: [],
    personaIds: [],
    roleIds: [],
    functionIds: [],
    regions: [],
    countries: [],
    statuses: [],
  });

  function toggleFilter(key, value) {
    setFilters((f) => ({
      ...f,
      [key]: f[key].includes(value)
        ? f[key].filter((v) => v !== value)
        : [...f[key], value],
    }));
  }
  function setFilterValues(key, values) {
    setFilters((f) => ({ ...f, [key]: values }));
  }
  function clearFilters() {
    setFilters({
      audienceIds: [],
      personaIds: [],
      roleIds: [],
      functionIds: [],
      regions: [],
      countries: [],
      statuses: [],
    });
  }

  // Apply filters to interventions
  const filteredInterventions = useMemo(() => {
    return state.interventions.filter((iv) => {
      // Status filter
      if (filters.statuses.length > 0 && !filters.statuses.includes(iv.status))
        return false;
      // Audience filter
      if (
        filters.audienceIds.length > 0 &&
        !filters.audienceIds.includes(iv.audienceId)
      )
        return false;
      // Persona, role, function, region, country — look up the audience for this intervention
      if (
        filters.personaIds.length > 0 ||
        filters.roleIds.length > 0 ||
        filters.functionIds.length > 0 ||
        filters.regions.length > 0 ||
        filters.countries.length > 0
      ) {
        const aud = state.audiences.find((a) => a.id === iv.audienceId);
        if (!aud) return false;
        if (
          filters.personaIds.length > 0 &&
          !filters.personaIds.includes(aud.personaId)
        )
          return false;
        if (filters.roleIds.length > 0 && !filters.roleIds.includes(aud.roleId))
          return false;
        if (
          filters.functionIds.length > 0 &&
          !filters.functionIds.includes(aud.functionId)
        )
          return false;
        if (filters.regions.length > 0 && !filters.regions.includes(aud.region))
          return false;
        if (
          filters.countries.length > 0 &&
          !filters.countries.includes(aud.country)
        )
          return false;
      }
      return true;
    });
  }, [state.interventions, state.audiences, filters]);

  const activeFilterCount =
    filters.audienceIds.length +
    filters.personaIds.length +
    filters.roleIds.length +
    filters.functionIds.length +
    filters.regions.length +
    filters.countries.length +
    filters.statuses.length;

  // Distinct values for region and country, derived from audience data
  const distinctRegions = useMemo(() => {
    const s = new Set();
    state.audiences.forEach((a) => {
      if (a.region) s.add(a.region);
    });
    return [...s].sort();
  }, [state.audiences]);
  const distinctCountries = useMemo(() => {
    const s = new Set();
    state.audiences.forEach((a) => {
      if (a.country) s.add(a.country);
    });
    return [...s].sort();
  }, [state.audiences]);

  function update(updated) {
    setState((p) => ({
      ...p,
      interventions: p.interventions.map((i) =>
        i.id === updated.id ? updated : i
      ),
    }));
  }
  function remove(id) {
    setState((p) => ({
      ...p,
      interventions: p.interventions.filter((i) => i.id !== id),
    }));
    setSelectedId(null);
  }
  function addIntervention(audienceId, leverId) {
    const id = uid("iv");
    const aud = state.audiences.find((a) => a.id === audienceId);
    setState((p) => ({
      ...p,
      interventions: [
        ...p.interventions,
        {
          id,
          audienceId,
          leverId,
          name: "New intervention",
          channel: "",
          owner: "",
          startDate: aud?.startDate || "",
          endDate: aud?.endDate || "",
          status: "planned",
        },
      ],
    }));
    setSelectedId(id);
  }

  // AI: draft an intervention for an audience+lever combination
  const [aiBusyKey, setAiBusyKey] = useState(null); // `${audienceId}-${leverId}` when busy
  const [aiError, setAiError] = useState(null);

  async function aiDraftIntervention(audienceId, leverId) {
    const key = `${audienceId}-${leverId}`;
    setAiBusyKey(key);
    setAiError(null);
    try {
      const aud = state.audiences.find((a) => a.id === audienceId);
      const lever = state.taxonomies.changeLevers.find((l) => l.id === leverId);
      const ctx = audienceContext(state, aud);
      const clientCtx = formatClientContextForPrompt(state.clientContext);
      const result = await aiDraft(
        "You are a WTW change management consultant assistant. You design specific, actionable change-management interventions for impacted audiences.",
        `Design one specific intervention for this audience using the "${
          lever.label
        }" change lever.\nLever purpose: ${
          lever.desc
        }\n\nGive concrete specifics — not generic. If client background materials describe existing comms channels, named leaders, or particular cadences, prefer those over generic recommendations. Output JSON:\n{\n  "name": "Short intervention name (4-8 words)",\n  "channel": "Delivery channel (e.g. workshop, email, town hall, 1:1, pulse survey)",\n  "owner": "Role of the owner (e.g. CHRO, VP Engineering, Internal Comms)",\n  "startDate": "YYYY-MM-DD within or near the audience's date range",\n  "endDate": "YYYY-MM-DD within or near the audience's date range"\n}\n\nContext:\n${JSON.stringify(
          ctx,
          null,
          2
        )}${clientCtx}`
      );
      // Validate dates — must be YYYY-MM-DD, fall back to audience dates if not
      const isoDate = /^\d{4}-\d{2}-\d{2}$/;
      let startDate = isoDate.test(result.startDate)
        ? result.startDate
        : aud?.startDate || "";
      let endDate = isoDate.test(result.endDate)
        ? result.endDate
        : aud?.endDate || "";
      // If the AI returned both dates but in reverse order, swap them rather than
      // saving a backwards intervention that would render as 0-width on the Gantt.
      if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
        [startDate, endDate] = [endDate, startDate];
      }
      const id = uid("iv");
      // Avoid orphan intervention if the audience was deleted during the AI call.
      // We read state directly here (it may be slightly stale, but the setState callback
      // below will use the freshest state for the actual write). The risk window is
      // small (between the snapshot and the dispatch), but the setState updater is
      // the authoritative guard.
      setState((p) => {
        if (!p.audiences.find((a) => a.id === audienceId)) return p; // audience gone — drop the write
        return {
          ...p,
          interventions: [
            ...p.interventions,
            {
              id,
              audienceId,
              leverId,
              name: (result.name || "").slice(0, 120) || "New intervention",
              channel: (result.channel || "").slice(0, 200),
              owner: (result.owner || "").slice(0, 100),
              startDate,
              endDate,
              status: "planned",
            },
          ],
        };
      });
      setSelectedId(id); // benign even if the write was dropped; selection just won't match anything
    } catch (e) {
      setAiError(
        `Failed for ${
          state.audiences.find((a) => a.id === audienceId)?.name || audienceId
        } + ${
          state.taxonomies.changeLevers.find((l) => l.id === leverId)?.label ||
          leverId
        }: ${e.message}`
      );
    } finally {
      setAiBusyKey(null);
    }
  }

  // Determine date extent for the Gantt — use ALL interventions so the axis stays stable as user filters
  const dateExtent = useMemo(() => {
    const dates = state.interventions
      .flatMap((i) => [i.startDate, i.endDate])
      .filter(Boolean);
    if (dates.length === 0) return null;
    const all = dates.map((d) => new Date(d));
    return { min: new Date(Math.min(...all)), max: new Date(Math.max(...all)) };
  }, [state.interventions]);

  const monthTicks = useMemo(() => {
    if (!dateExtent) return [];
    const ticks = [];
    const cur = new Date(
      dateExtent.min.getFullYear(),
      dateExtent.min.getMonth(),
      1
    );
    const end = new Date(
      dateExtent.max.getFullYear(),
      dateExtent.max.getMonth() + 1,
      1
    );
    while (cur <= end) {
      ticks.push(new Date(cur));
      cur.setMonth(cur.getMonth() + 1);
    }
    return ticks;
  }, [dateExtent]);

  // Group FILTERED interventions by lever
  const byLever = useMemo(() => {
    const m = {};
    state.taxonomies.changeLevers.forEach((l) => (m[l.id] = []));
    filteredInterventions.forEach((i) => {
      if (m[i.leverId]) m[i.leverId].push(i);
    });
    return m;
  }, [filteredInterventions, state.taxonomies.changeLevers]);

  const statusColors = {
    planned: { bg: "#E5E7EB", text: "#374151", label: "Planned" },
    in_progress: { bg: "#FDE68A", text: "#92400E", label: "In progress" },
    done: { bg: "#A7F3D0", text: "#065F46", label: "Done" },
    blocked: { bg: "#FECACA", text: "#991B1B", label: "Blocked" },
  };

  if (!dateExtent) {
    return (
      <div className="p-8 text-stone-500">
        No interventions to display yet. Add interventions per audience-lever
        combination below.
      </div>
    );
  }

  const totalMs = dateExtent.max - dateExtent.min;
  // Same date-arithmetic guards as Timeline view — see comment there.
  function pct(date) {
    if (!totalMs || isNaN(totalMs)) return 0;
    const d = new Date(date).getTime();
    if (isNaN(d)) return 0;
    return Math.max(0, Math.min(100, ((d - dateExtent.min) / totalMs) * 100));
  }
  function widthPct(start, end) {
    if (!totalMs || isNaN(totalMs)) return 0;
    const s = new Date(start).getTime();
    const e = new Date(end).getTime();
    if (isNaN(s) || isNaN(e)) return 0;
    return Math.max(0, ((e - s) / totalMs) * 100);
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-medium mb-1">
            Design · Change Management Plan
          </div>
          <h2
            className="text-3xl font-semibold text-stone-900"
            style={{
              fontFamily: "Georgia, serif",
              letterSpacing: "-0.015em",
              lineHeight: 1.15,
            }}
          >
            Interventions by lever, across time
          </h2>
          <p className="text-sm text-stone-600 mt-2 max-w-2xl">
            The high-level CM plan: every audience's selected levers turned into
            specific interventions. Drag a date or click an intervention to
            edit.
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="border border-stone-200 rounded-lg bg-white p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={14} className="text-stone-500" />
          <span className="text-[11px] uppercase tracking-wider font-semibold text-stone-600">
            Filters
          </span>
          {activeFilterCount > 0 && (
            <>
              <span className="text-[11px] text-stone-500">·</span>
              <span className="text-[11px] text-stone-600">
                Showing{" "}
                <span className="font-semibold text-stone-900">
                  {filteredInterventions.length}
                </span>{" "}
                of {state.interventions.length} interventions
              </span>
              <button
                onClick={clearFilters}
                className="text-[11px] text-purple-700 hover:text-purple-900 font-medium ml-auto flex items-center gap-1"
              >
                <X size={11} /> Clear all
              </button>
            </>
          )}
          {activeFilterCount === 0 && (
            <span className="text-[11px] text-stone-400 ml-2">
              Click any chip to filter. Combine across rows to narrow further.
            </span>
          )}
        </div>

        <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-2 items-start">
          {/* Status filter */}
          <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 pt-1.5">
            Status
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[
              { v: "planned", label: "Planned" },
              { v: "in_progress", label: "In progress" },
              { v: "done", label: "Done" },
              { v: "blocked", label: "Blocked" },
            ].map((s) => {
              const active = filters.statuses.includes(s.v);
              const sc = statusColors[s.v];
              return (
                <button
                  key={s.v}
                  onClick={() => toggleFilter("statuses", s.v)}
                  className="text-[11px] px-2.5 py-1 rounded-full border transition-colors flex items-center gap-1.5"
                  style={
                    active
                      ? {
                          background: sc.bg,
                          color: sc.text,
                          borderColor: sc.text,
                        }
                      : {
                          background: "white",
                          color: "#57534e",
                          borderColor: "#d6d3d1",
                        }
                  }
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{
                      background: sc.bg,
                      border: active ? `1px solid ${sc.text}` : "none",
                    }}
                  />
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Role filter */}
          <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 pt-1.5">
            Role
          </div>
          <div className="flex flex-wrap gap-1.5">
            {state.taxonomies.roles.map((r) => {
              const active = filters.roleIds.includes(r.id);
              return (
                <button
                  key={r.id}
                  onClick={() => toggleFilter("roleIds", r.id)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    active
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
                  }`}
                >
                  {lookupLabel(state, "roles", r.id)}
                </button>
              );
            })}
          </div>

          {/* Function filter */}
          <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 pt-1.5">
            Function
          </div>
          <div className="flex flex-wrap gap-1.5">
            {state.taxonomies.functions.map((f) => {
              const active = filters.functionIds.includes(f.id);
              return (
                <button
                  key={f.id}
                  onClick={() => toggleFilter("functionIds", f.id)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    active
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
                  }`}
                >
                  {lookupLabel(state, "functions", f.id)}
                </button>
              );
            })}
          </div>

          {/* Persona filter — multi-select dropdown. 18 personas would crowd as
              chips; the dropdown keeps the layout tight. */}
          <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 pt-1.5">
            Persona
          </div>
          <div>
            <MultiSelect
              options={state.taxonomies.personas.map((p) => ({
                id: p.id,
                label: lookupLabel(state, "personas", p.id),
                sublabel: p.desc,
              }))}
              selected={filters.personaIds}
              onChange={(vals) => setFilterValues("personaIds", vals)}
              placeholder="All personas"
              itemNoun="personas"
            />
          </div>

          {/* Region filter */}
          <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 pt-1.5">
            Region
          </div>
          <div className="flex flex-wrap gap-1.5">
            {distinctRegions.map((r) => {
              const active = filters.regions.includes(r);
              return (
                <button
                  key={r}
                  onClick={() => toggleFilter("regions", r)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    active
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
                  }`}
                >
                  {r}
                </button>
              );
            })}
          </div>

          {/* Country filter */}
          <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 pt-1.5">
            Country
          </div>
          <div className="flex flex-wrap gap-1.5">
            {distinctCountries.map((c) => {
              const active = filters.countries.includes(c);
              return (
                <button
                  key={c}
                  onClick={() => toggleFilter("countries", c)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    active
                      ? "bg-stone-900 text-white border-stone-900"
                      : "bg-white text-stone-600 border-stone-300 hover:border-stone-500"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>

          {/* Audience filter — multi-select dropdown so 15+ audiences don't
              dominate the filter panel. Searchable. */}
          <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 pt-1.5">
            Audience
          </div>
          <div>
            <MultiSelect
              options={state.audiences.map((a) => ({
                id: a.id,
                label: a.name,
                sublabel: lookupLabel(state, "functions", a.functionId),
              }))}
              selected={filters.audienceIds}
              onChange={(vals) => setFilterValues("audienceIds", vals)}
              placeholder="All audiences"
              itemNoun="audiences"
            />
          </div>
        </div>
      </div>

      {/* Gantt-by-lever */}
      <div className="border border-stone-200 rounded-lg bg-white overflow-hidden mb-6">
        {/* Month header */}
        <div className="flex border-b border-stone-200">
          <div className="w-48 px-4 py-2 bg-stone-50 border-r border-stone-200 text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
            Lever
          </div>
          <div className="flex-1 relative" style={{ height: 36 }}>
            {monthTicks.map((t, i) => (
              <div
                key={i}
                className="absolute top-0 bottom-0 border-l border-stone-200"
                style={{ left: `${pct(t)}%` }}
              >
                <div className="text-[10px] text-stone-500 px-1 py-1.5 whitespace-nowrap">
                  {t.toLocaleDateString("en-US", { month: "short" })}
                  {t.getMonth() === 0 && (
                    <span className="text-stone-400 ml-1">
                      {t.getFullYear()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* One row per lever */}
        {state.taxonomies.changeLevers.map((lever) => {
          const items = byLever[lever.id] || [];
          // Sort by start date so layering is consistent. Treat missing/invalid dates
          // as the epoch so they sort to the start rather than scrambling the order.
          const safeTime = (d) => {
            const t = new Date(d).getTime();
            return isNaN(t) ? 0 : t;
          };
          const sorted = [...items].sort(
            (a, b) => safeTime(a.startDate) - safeTime(b.startDate)
          );

          // Truncate long names so the label can't grow beyond a sensible
          // horizontal budget. 42 chars keeps most labels intact while preventing
          // pathological cases like "Hands-on platform migration training
          // (SoftWave engineers)" (56 chars) from sprawling.
          const MAX_LABEL_CHARS = 42;
          const truncateLabel = (s) => {
            if (!s) return "";
            return s.length > MAX_LABEL_CHARS
              ? s.slice(0, MAX_LABEL_CHARS - 1).trimEnd() + "…"
              : s;
          };

          // Estimate the horizontal "footprint" of an intervention, in
          // milliseconds, to feed into lane-packing. The footprint extends from
          // where the label begins (first dot for point series, bar-start for
          // bars) to whichever is later: the actual end of the marker, or the
          // right edge of the label text.
          //
          // We approximate label width as: chars * 6.5px at 10px font, then
          // convert that pixel width to a time duration using the assumed
          // visible width of the timeline column (~900px on desktop). This is a
          // heuristic — close enough to push competing labels onto separate
          // lanes without being mathematically exact.
          const TIMELINE_PX_WIDTH = 900; // assumed visible width of the timeline column
          const PX_PER_CHAR = 6.5; // for 10px sans-serif label font
          const LABEL_GAP_PX = 8; // breathing room between labels
          const computeFootprint = (iv) => {
            const points = getInterventionRenderPoints(iv);
            const cadenceLabel = describeCadence(iv);
            const displayName = truncateLabel(iv.name || "");
            const fullLabel = cadenceLabel
              ? `${displayName} · ${cadenceLabel.toLowerCase()}`
              : displayName;
            const labelPx = fullLabel.length * PX_PER_CHAR + LABEL_GAP_PX;
            const labelMs = (labelPx / TIMELINE_PX_WIDTH) * totalMs;

            // Determine where the label begins
            let labelStartMs;
            let markerEndMs;
            if (points.length === 0) {
              labelStartMs = safeTime(iv.startDate);
              markerEndMs = safeTime(iv.endDate);
            } else if (points[0].kind === "span") {
              labelStartMs = points[0].date.getTime();
              markerEndMs = points[0].endDate.getTime();
            } else {
              labelStartMs = points[0].date.getTime();
              markerEndMs = points[points.length - 1].date.getTime();
            }
            const labelEndMs = labelStartMs + labelMs;
            return {
              start: labelStartMs, // leftmost x the intervention occupies
              end: Math.max(markerEndMs, labelEndMs), // rightmost x the label or marker reaches
            };
          };

          // Lane-pack interventions using their *label-aware* footprint so two
          // adjacent items whose markers don't overlap but whose labels do will
          // be placed on different lanes.
          const lanes = [];
          sorted.forEach((iv) => {
            const fp = computeFootprint(iv);
            iv._displayName = truncateLabel(iv.name || ""); // stash for render below
            let placed = false;
            for (let li = 0; li < lanes.length; li++) {
              const fits = lanes[li].every(
                (other) => other._fp.end < fp.start || other._fp.start > fp.end
              );
              if (fits) {
                lanes[li].push(iv);
                iv._lane = li;
                iv._fp = fp;
                placed = true;
                break;
              }
            }
            if (!placed) {
              iv._lane = lanes.length;
              iv._fp = fp;
              lanes.push([iv]);
            }
          });
          const laneCount = Math.max(lanes.length, 1);
          // Each lane is now 40px tall to fit: label row (~14px) + gap (4px) +
          // dot/bar row (~12px) + breathing room. Up from 26px when label and
          // marker shared the same row.
          const LANE_HEIGHT = 40;
          const rowHeight = laneCount * LANE_HEIGHT + 12;

          return (
            <div key={lever.id} className="flex border-b border-stone-100">
              <div className="w-48 px-4 py-3 border-r border-stone-200 bg-stone-50/30">
                <div className="text-sm font-semibold text-stone-800">
                  {lookupLabel(state, "changeLevers", lever.id)}
                </div>
                <div className="text-[10px] text-stone-500 mt-0.5">
                  {items.length} intervention{items.length === 1 ? "" : "s"}
                </div>
              </div>
              <div
                className="flex-1 relative"
                style={{ minHeight: rowHeight, padding: "6px 0" }}
              >
                {/* gridlines */}
                {monthTicks.map((t, i) => (
                  <div
                    key={i}
                    className="absolute top-0 bottom-0 border-l border-stone-100"
                    style={{ left: `${pct(t)}%` }}
                  />
                ))}
                {sorted.map((iv) => {
                  const aud = state.audiences.find(
                    (a) => a.id === iv.audienceId
                  );
                  const sc = statusColors[iv.status] || statusColors.planned;
                  const isSel = iv.id === selectedId;
                  const points = getInterventionRenderPoints(iv);
                  const cadenceLabel = describeCadence(iv);

                  // Decide rendering strategy from the points returned:
                  //   - One 'span' point  → bar with label above
                  //   - Multiple 'point's → dot series with label above
                  const isPointSeries =
                    points.length > 0 &&
                    points.every((p) => p.kind === "point");
                  const isSingleSpan =
                    points.length === 1 && points[0].kind === "span";

                  // Lane vertical offsets — label row, then marker row below.
                  // Label sits at top of the lane; marker sits below the label.
                  const laneTop = 4 + iv._lane * LANE_HEIGHT;
                  const labelTop = laneTop;
                  const markerTop = laneTop + 18; // label height + small gap

                  if (isSingleSpan) {
                    // For one-time bars: render the label as a separate element
                    // above the bar so it can extend beyond the bar's width when
                    // the date range is short. Bar itself is just a colored pill.
                    const leftPct = pct(iv.startDate);
                    const wPct = Math.max(
                      widthPct(iv.startDate, iv.endDate),
                      1.5
                    );
                    return (
                      <div
                        key={iv.id}
                        onClick={() => setSelectedId(iv.id)}
                        className="absolute cursor-pointer"
                        style={{
                          left: 0,
                          right: 0,
                          top: laneTop,
                          height: LANE_HEIGHT - 4,
                        }}
                        title={`${iv.name} — ${aud?.name || ""} (${sc.label})`}
                      >
                        {/* Label, free to extend beyond the bar */}
                        <div
                          className="absolute text-[10px] font-medium whitespace-nowrap pointer-events-none"
                          style={{
                            left: `${leftPct}%`,
                            top: 0,
                            color: sc.text,
                            maxWidth: "300px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {iv._displayName || iv.name}
                        </div>
                        {/* The bar itself, with selection ring */}
                        <div
                          className={`absolute rounded ${
                            isSel ? "ring-2 ring-purple-400" : ""
                          }`}
                          style={{
                            left: `${leftPct}%`,
                            width: `${wPct}%`,
                            top: 18,
                            height: 10,
                            background: sc.bg,
                            border: `1px solid ${sc.text}33`,
                          }}
                        />
                      </div>
                    );
                  }

                  if (isPointSeries) {
                    const firstX = pct(points[0].date);
                    const lastX = pct(points[points.length - 1].date);
                    // For dense recurring series (≥10 points), make dots
                    // smaller so they read as a rhythm strip rather than a row
                    // of individually-emphasized events.
                    const isDense = points.length >= 10;
                    const dotSize = isDense ? 7 : 10;
                    return (
                      <div
                        key={iv.id}
                        onClick={() => setSelectedId(iv.id)}
                        className="absolute cursor-pointer"
                        style={{
                          left: 0,
                          right: 0,
                          top: laneTop,
                          height: LANE_HEIGHT - 4,
                        }}
                        title={`${iv.name} — ${aud?.name || ""} (${
                          cadenceLabel || "series"
                        }, ${sc.label})`}
                      >
                        {/* Label above the marker row */}
                        <div
                          className="absolute text-[10px] font-medium whitespace-nowrap pointer-events-none"
                          style={{
                            left: `${firstX}%`,
                            top: 0,
                            color: sc.text,
                            maxWidth: "320px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {iv._displayName || iv.name}
                          {cadenceLabel && (
                            <span className="ml-1 text-stone-500 font-normal">
                              · {cadenceLabel.toLowerCase()}
                            </span>
                          )}
                        </div>
                        {/* Connecting dashed line between first and last dot */}
                        {points.length > 1 && (
                          <div
                            className="absolute"
                            style={{
                              left: `${firstX}%`,
                              width: `${lastX - firstX}%`,
                              top: 18 + dotSize / 2 - 0.5,
                              height: 0,
                              borderTop: `1px dashed ${sc.text}`,
                              opacity: 0.4,
                            }}
                          />
                        )}
                        {/* The dots themselves */}
                        {points.map((p, pi) => (
                          <div
                            key={pi}
                            className="absolute rounded-full"
                            style={{
                              left: `calc(${pct(p.date)}% - ${dotSize / 2}px)`,
                              top: 18,
                              width: dotSize,
                              height: dotSize,
                              background: sc.bg,
                              border: isSel
                                ? `2px solid ${tokens.primary}`
                                : `1.5px solid ${sc.text}`,
                              boxShadow: isSel
                                ? `0 0 0 2px ${tokens.primary}33`
                                : "none",
                            }}
                          />
                        ))}
                      </div>
                    );
                  }

                  // No render points (intervention has malformed cadence/dates) —
                  // skip drawing rather than throwing
                  return null;
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Status legend */}
      <div className="flex items-center gap-4 text-[11px] text-stone-600 mb-6">
        <span className="font-medium uppercase tracking-wider text-stone-500">
          Status
        </span>
        {Object.entries(statusColors).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-sm" style={{ background: v.bg }} />
            {v.label}
          </span>
        ))}
      </div>

      {/* Editor + audience gap matrix */}
      <div className="grid grid-cols-[1.4fr_1fr] gap-6">
        {/* Intervention editor */}
        {selected ? (
          <div className="border border-stone-200 rounded-lg bg-white p-5">
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-700 font-semibold mb-2">
              Intervention
            </div>
            <input
              disabled={!canEdit}
              value={selected.name}
              onChange={(e) => update({ ...selected, name: e.target.value })}
              className="text-lg font-semibold text-stone-900 bg-transparent w-full focus:outline-none focus:bg-stone-50 px-2 -ml-2 py-1 rounded"
            />

            <div className="grid grid-cols-2 gap-3 mt-4">
              <Field label="Audience">
                <select
                  disabled={!canEdit}
                  value={selected.audienceId || ""}
                  onChange={(e) =>
                    update({ ...selected, audienceId: e.target.value })
                  }
                  className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                >
                  {state.audiences.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Lever">
                <select
                  disabled={!canEdit}
                  value={selected.leverId || ""}
                  onChange={(e) =>
                    update({ ...selected, leverId: e.target.value })
                  }
                  className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                >
                  {state.taxonomies.changeLevers.map((l) => (
                    <option key={l.id} value={l.id}>
                      {lookupLabel(state, "changeLevers", l.id)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <Field label="Channel">
                <input
                  disabled={!canEdit}
                  value={selected.channel || ""}
                  onChange={(e) =>
                    update({ ...selected, channel: e.target.value })
                  }
                  placeholder="Townhall, email, workshop…"
                  className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                />
              </Field>
              <Field label="Owner">
                <input
                  disabled={!canEdit}
                  value={selected.owner || ""}
                  onChange={(e) =>
                    update({ ...selected, owner: e.target.value })
                  }
                  className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                />
              </Field>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-3">
              <Field label="Start">
                <input
                  disabled={!canEdit}
                  type="date"
                  value={selected.startDate || ""}
                  onChange={(e) =>
                    update({ ...selected, startDate: e.target.value })
                  }
                  className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                />
              </Field>
              <Field label="End">
                <input
                  disabled={!canEdit}
                  type="date"
                  value={selected.endDate || ""}
                  onChange={(e) =>
                    update({ ...selected, endDate: e.target.value })
                  }
                  className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                />
              </Field>
              <Field label="Status">
                <select
                  disabled={!canEdit}
                  value={selected.status || "planned"}
                  onChange={(e) =>
                    update({ ...selected, status: e.target.value })
                  }
                  className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                >
                  <option value="planned">Planned</option>
                  <option value="in_progress">In progress</option>
                  <option value="done">Done</option>
                  <option value="blocked">Blocked</option>
                </select>
              </Field>
            </div>

            {/* Cadence controls. Determines how this intervention renders on the
                timeline: one continuous bar, repeating dots, or specific milestones. */}
            <div className="mt-4 pt-4 border-t border-stone-100">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Cadence">
                  <select
                    disabled={!canEdit}
                    value={selected.cadence || "one_time"}
                    onChange={(e) => {
                      const next = { ...selected, cadence: e.target.value };
                      // Initialise any required companion fields so the renderer never crashes
                      if (
                        e.target.value === "milestones" &&
                        !Array.isArray(next.milestoneDates)
                      ) {
                        next.milestoneDates = [];
                      }
                      if (
                        e.target.value === "recurring" &&
                        !next.recurrenceInterval
                      ) {
                        next.recurrenceInterval = "monthly";
                      }
                      update(next);
                    }}
                    className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                  >
                    <option value="one_time">One-time (continuous bar)</option>
                    <option value="recurring">
                      Recurring (repeating dots)
                    </option>
                    <option value="milestones">
                      Milestones (specific dates)
                    </option>
                  </select>
                </Field>
                {selected.cadence === "recurring" && (
                  <Field label="Repeats">
                    <select
                      disabled={!canEdit}
                      value={selected.recurrenceInterval || "monthly"}
                      onChange={(e) =>
                        update({
                          ...selected,
                          recurrenceInterval: e.target.value,
                        })
                      }
                      className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                    >
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-weekly</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                    </select>
                  </Field>
                )}
              </div>

              {/* Milestone date list — only when cadence is milestones. Each date
                  is editable; user can add or remove entries. */}
              {selected.cadence === "milestones" && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-stone-500 font-semibold mb-2">
                    Milestone dates
                  </div>
                  <div className="space-y-1.5">
                    {(selected.milestoneDates || []).map((d, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          disabled={!canEdit}
                          type="date"
                          value={d}
                          onChange={(e) => {
                            const next = [...(selected.milestoneDates || [])];
                            next[idx] = e.target.value;
                            update({ ...selected, milestoneDates: next });
                          }}
                          className="text-sm border border-stone-200 rounded p-1.5 bg-white"
                        />
                        {canEdit && (
                          <button
                            onClick={() => {
                              const next = (
                                selected.milestoneDates || []
                              ).filter((_, i) => i !== idx);
                              update({ ...selected, milestoneDates: next });
                            }}
                            className="text-stone-400 hover:text-red-600"
                            title="Remove this milestone"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    {canEdit && (
                      <button
                        onClick={() => {
                          // Add a new empty date entry; user types the date in
                          const next = [...(selected.milestoneDates || []), ""];
                          update({ ...selected, milestoneDates: next });
                        }}
                        className="text-[11px] text-purple-700 hover:text-purple-900 flex items-center gap-1 mt-1"
                      >
                        + Add milestone date
                      </button>
                    )}
                    {(selected.milestoneDates || []).length === 0 && (
                      <div className="text-[11px] text-stone-400 italic">
                        No milestone dates yet — add one above.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {canEdit && (
              <button
                onClick={() => remove(selected.id)}
                className="mt-6 text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
              >
                <Trash2 size={12} /> Delete this intervention
              </button>
            )}
          </div>
        ) : (
          <div className="border border-stone-200 rounded-lg bg-stone-50 p-6 text-sm text-stone-500">
            <div className="font-medium text-stone-800 mb-1">
              Select an intervention above to edit it
            </div>
            <div className="text-[12px]">
              Or use the matrix on the right to add interventions where
              audiences have selected levers but no intervention exists yet.
            </div>
          </div>
        )}

        {/* Coverage matrix: audiences × levers */}
        <div className="border border-stone-200 rounded-lg bg-white p-5">
          <div className="text-sm font-semibold text-stone-900 mb-1">
            Coverage gaps
          </div>
          <div className="text-[11px] text-stone-500 mb-3">
            Audiences where a lever was selected but no intervention exists yet
          </div>
          {aiError && (
            <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-2 flex items-start gap-2">
              <span className="flex-1">{aiError}</span>
              <button
                onClick={() => setAiError(null)}
                className="text-red-700 hover:text-red-900 flex-shrink-0"
                title="Dismiss"
              >
                <X size={11} />
              </button>
            </div>
          )}
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {state.audiences
              .flatMap((aud) =>
                (aud.leverIds || []).map((leverId) => {
                  const hasIv = state.interventions.some(
                    (i) => i.audienceId === aud.id && i.leverId === leverId
                  );
                  if (hasIv) return null;
                  const key = `${aud.id}-${leverId}`;
                  const isBusy = aiBusyKey === key;
                  return (
                    <div
                      key={key}
                      className="flex items-center gap-2 text-[12px] border border-stone-100 rounded p-2 hover:bg-stone-50"
                    >
                      <span className="text-stone-800 flex-1 truncate">
                        {aud.name}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded">
                        {lookupLabel(state, "changeLevers", leverId)}
                      </span>
                      {canEdit && (
                        <>
                          <button
                            onClick={() => aiDraftIntervention(aud.id, leverId)}
                            disabled={aiBusyKey !== null}
                            title="Use Claude to draft a specific intervention"
                            className="text-[10px] text-purple-700 hover:text-purple-900 font-medium px-2 flex items-center gap-1 disabled:opacity-40 disabled:cursor-wait"
                          >
                            {isBusy ? (
                              <span className="inline-block w-2.5 h-2.5 border-2 border-purple-300 border-t-purple-700 rounded-full animate-spin" />
                            ) : (
                              <Sparkles size={10} />
                            )}
                            {isBusy ? "drafting…" : "AI draft"}
                          </button>
                          <button
                            onClick={() => addIntervention(aud.id, leverId)}
                            className="text-[10px] text-stone-500 hover:text-stone-900 font-medium px-2"
                          >
                            + blank
                          </button>
                        </>
                      )}
                    </div>
                  );
                })
              )
              .filter(Boolean)}
            {state.audiences
              .flatMap((a) => a.leverIds || [])
              .every((lid) =>
                state.interventions.some((i) => i.leverId === lid)
              ) && (
              <div className="text-[12px] text-stone-400 italic text-center py-4">
                All audience-lever combinations have interventions ✓
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// INTERVENTION STATUS — Transform phase
// ============================================================================
function InterventionsView({ state, setState, role }) {
  const canEdit = role !== "client_viewer";
  const [filter, setFilter] = useState("all");

  const grouped = useMemo(() => {
    const g = { planned: [], in_progress: [], done: [], blocked: [] };
    state.interventions.forEach((i) => {
      if (g[i.status]) g[i.status].push(i);
      else g.planned.push(i);
    });
    return g;
  }, [state.interventions]);

  const filtered =
    filter === "all" ? state.interventions : grouped[filter] || [];

  const statusMeta = {
    planned: { label: "Planned", color: "#6B7280", bg: "#F3F4F6" },
    in_progress: { label: "In progress", color: "#B45309", bg: "#FEF3C7" },
    done: { label: "Done", color: "#047857", bg: "#D1FAE5" },
    blocked: { label: "Blocked", color: "#B91C1C", bg: "#FEE2E2" },
  };

  function updateStatus(iv, status) {
    setState((p) => ({
      ...p,
      interventions: p.interventions.map((i) =>
        i.id === iv.id ? { ...i, status } : i
      ),
    }));
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-medium mb-1">
            Transform · Intervention Status
          </div>
          <h2
            className="text-3xl font-semibold text-stone-900"
            style={{
              fontFamily: "Georgia, serif",
              letterSpacing: "-0.015em",
              lineHeight: 1.15,
            }}
          >
            How execution is going
          </h2>
          <p className="text-sm text-stone-600 mt-2 max-w-2xl">
            Track every intervention from planned to done. Click a status pill
            on any row to update it inline.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-6">
        {Object.entries(statusMeta).map(([k, meta]) => (
          <button
            key={k}
            onClick={() => setFilter(filter === k ? "all" : k)}
            className={`text-left p-4 rounded-lg border transition-all ${
              filter === k
                ? "ring-2 ring-stone-900 border-stone-900"
                : "border-stone-200 hover:border-stone-400"
            }`}
            style={{ background: filter === k ? meta.bg : "white" }}
          >
            <div
              className="text-2xl font-semibold"
              style={{ color: meta.color, fontFamily: "Georgia, serif" }}
            >
              {grouped[k].length}
            </div>
            <div
              className="text-[11px] uppercase tracking-wider mt-1"
              style={{ color: meta.color }}
            >
              {meta.label}
            </div>
          </button>
        ))}
      </div>

      <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
        <div className="grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr_120px] px-4 py-2.5 border-b border-stone-200 bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
          <div>Intervention</div>
          <div>Audience</div>
          <div>Lever</div>
          <div>Owner</div>
          <div>Dates</div>
          <div>Status</div>
        </div>
        {filtered.map((iv) => {
          const aud = state.audiences.find((a) => a.id === iv.audienceId);
          const meta = statusMeta[iv.status] || statusMeta.planned;
          return (
            <div
              key={iv.id}
              className="grid grid-cols-[2fr_1.5fr_1fr_1fr_1fr_120px] px-4 py-3 border-b border-stone-100 items-center text-sm hover:bg-stone-50"
            >
              <div>
                <div className="font-medium text-stone-900">{iv.name}</div>
                <div className="text-[11px] text-stone-500">{iv.channel}</div>
              </div>
              <div className="text-stone-700 text-[13px] truncate">
                {aud?.name}
              </div>
              <div className="text-[12px] text-stone-700">
                {lookupLabel(state, "changeLevers", iv.leverId)}
              </div>
              <div className="text-[12px] text-stone-700">{iv.owner}</div>
              <div className="text-[11px] text-stone-500">
                {iv.startDate} → {iv.endDate}
                {describeCadence(iv) && (
                  <span
                    className="ml-1.5 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-semibold"
                    style={{ background: "#F5F1FA", color: "#4B2E83" }}
                  >
                    {describeCadence(iv)}
                  </span>
                )}
              </div>
              <div>
                <select
                  disabled={!canEdit}
                  value={iv.status}
                  onChange={(e) => updateStatus(iv, e.target.value)}
                  className="text-[11px] font-medium rounded px-2 py-1 border-0 cursor-pointer"
                  style={{ background: meta.bg, color: meta.color }}
                >
                  {Object.entries(statusMeta).map(([k, m]) => (
                    <option key={k} value={k}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-stone-400 italic">
            No interventions in this status.
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// EXEC SUMMARY — AI-generated briefing for steering committees
// ============================================================================
function ExecSummaryView({ state }) {
  const [scope, setScope] = useState("all"); // 'all' or a specific change id
  const [audience, setAudience] = useState("steering"); // 'steering' | 'sponsor' | 'board'
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function generate() {
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      // Build a structured project snapshot
      const relevantChanges =
        scope === "all"
          ? state.changes
          : state.changes.filter((c) => c.id === scope);
      const relevantCTIs = state.changesToImplement.filter((c) =>
        relevantChanges.some((ch) => ch.id === c.changeId)
      );
      const relevantAudiences = state.audiences.filter((a) =>
        relevantCTIs.some((c) => c.id === a.changeToImplementId)
      );
      const relevantInterventions = state.interventions.filter((i) =>
        relevantAudiences.some((a) => a.id === i.audienceId)
      );

      if (relevantChanges.length === 0) {
        throw new Error(
          "No changes in scope. Add at least one change before generating a briefing."
        );
      }
      if (relevantAudiences.length === 0) {
        throw new Error(
          "No audiences in scope. The selected change has no audiences yet — add audiences first."
        );
      }

      const snapshot = {
        project: state.project.name,
        scope: scope === "all" ? "All changes" : relevantChanges[0]?.name,
        audienceForSummary: audience,
        date: new Date().toISOString().slice(0, 10),
        changes: relevantChanges.map((c) => ({
          name: c.name,
          isKey: c.isKey,
          description: c.description,
        })),
        changesToImplement: relevantCTIs.map((c) => ({
          name: c.name,
          workstream: lookupLabel(state, "workstreams", c.workstreamId),
          from: c.from,
          to: c.to,
        })),
        audienceCounts: {
          total: relevantAudiences.length,
          highSeverity: relevantAudiences.filter((a) =>
            ["il4", "il5"].includes(a.impactLevelId)
          ).length,
          totalPeople: relevantAudiences.reduce(
            (s, a) =>
              s +
              (state.taxonomies.audienceSizes.find(
                (z) => z.id === a.audienceSizeId
              )?.value || 0),
            0
          ),
        },
        interventionStatus: {
          planned: relevantInterventions.filter((i) => i.status === "planned")
            .length,
          in_progress: relevantInterventions.filter(
            (i) => i.status === "in_progress"
          ).length,
          done: relevantInterventions.filter((i) => i.status === "done").length,
          blocked: relevantInterventions.filter((i) => i.status === "blocked")
            .length,
        },
        blockedInterventions: relevantInterventions
          .filter((i) => i.status === "blocked")
          .map((i) => {
            const aud = state.audiences.find((a) => a.id === i.audienceId);
            return { name: i.name, audience: aud?.name, owner: i.owner };
          }),
        topImpactedAudiences: relevantAudiences
          .map((a) => ({
            name: a.name,
            severity:
              state.taxonomies.impactLevels.find(
                (l) => l.id === a.impactLevelId
              )?.value || 0,
            dimensions: (a.impactTypeIds || []).length,
            reactions: (a.reactionIds || []).map((id) =>
              lookupLabel(state, "reactionTypes", id)
            ),
          }))
          .sort((a, b) => b.severity * b.dimensions - a.severity * a.dimensions)
          .slice(0, 5),
        barrierStakeholders: state.stakeholders
          .filter(
            (s) =>
              s.abilityToImpact === "high" &&
              (s.currentCommitment || 0) < (s.requiredCommitment || 0)
          )
          .map((s) => ({
            name: s.name,
            role: s.role,
            gap: (s.requiredCommitment || 0) - (s.currentCommitment || 0),
          })),
      };

      const audienceFraming = {
        steering:
          "a weekly steering committee — operators who need to know status, risks, and decisions needed.",
        sponsor:
          "the executive sponsor (CEO/CHRO) — strategic narrative, key risks, asks of leadership.",
        board:
          "a quarterly board update — high-level progress, headline risks, strategic alignment.",
      }[audience];

      const result = await aiDraft(
        "You are a WTW change management consultant assistant. You write crisp, professional executive briefings — direct, no hedging, no jargon. Use the data provided; do not invent facts.",
        `Write an executive briefing for ${audienceFraming}\n\nReturn JSON with these fields:\n{\n  "headline": "One-sentence summary of where things stand",\n  "wins": ["3 short bullets of recent or impending wins"],\n  "risks": ["3 short bullets of current risks or watch-list items"],\n  "decisions_needed": ["1-3 short bullets of decisions or asks for the audience"],\n  "next_two_weeks": ["3-5 short bullets of what's coming up"]\n}\n\nIf the client background materials below give specific named programs, stated commitments, or strategic priorities, ground the briefing in those facts rather than inventing detail.\n\nData snapshot:\n${JSON.stringify(
          snapshot,
          null,
          2
        )}${formatClientContextForPrompt(state.clientContext)}`
      );
      // Coerce the AI's response into safe types before storing. If Claude returns
      // a string instead of an array for any of these fields, the UI would crash on .map().
      setSummary({
        headline: asString(result.headline),
        wins: asStringArray(result.wins),
        risks: asStringArray(result.risks),
        decisions_needed: asStringArray(result.decisions_needed),
        next_two_weeks: asStringArray(result.next_two_weeks),
      });
    } catch (e) {
      setError(e.message || "AI request failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-medium mb-1">
            Transform · Executive Summary
          </div>
          <h2
            className="text-3xl font-semibold text-stone-900"
            style={{
              fontFamily: "Georgia, serif",
              letterSpacing: "-0.015em",
              lineHeight: 1.15,
            }}
          >
            Brief me on the project
          </h2>
          <p className="text-sm text-stone-600 mt-2 max-w-2xl">
            AI-generated briefing from the live project data. Pick the scope and
            audience, then generate. Always review before sharing.
          </p>
        </div>
      </div>

      <div className="border border-stone-200 rounded-lg bg-white p-5 mb-6">
        <div className="grid grid-cols-2 gap-4 mb-4">
          <Field label="Scope">
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
            >
              <option value="all">All changes ({state.changes.length})</option>
              {state.changes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Audience for this briefing">
            <select
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
            >
              <option value="steering">Weekly steering committee</option>
              <option value="sponsor">Executive sponsor (CEO / CHRO)</option>
              <option value="board">Quarterly board update</option>
            </select>
          </Field>
        </div>
        <button
          onClick={generate}
          disabled={busy}
          className="text-sm text-white px-4 py-2 rounded font-medium flex items-center gap-2 disabled:opacity-60 disabled:cursor-wait"
          style={{ background: tokens.primary }}
        >
          {busy ? (
            <span className="inline-block w-3.5 h-3.5 border-2 border-purple-200 border-t-white rounded-full animate-spin" />
          ) : (
            <Sparkles size={14} />
          )}
          {busy
            ? "Generating briefing…"
            : summary
            ? "Regenerate"
            : "Generate briefing"}
        </button>
        {error && (
          <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
            <strong>Error:</strong> {error}
          </div>
        )}
      </div>

      {summary && (
        <div className="border border-stone-200 rounded-lg bg-white p-6">
          <div className="text-[10px] uppercase tracking-[0.18em] text-purple-700 font-semibold mb-2 flex items-center gap-2">
            <Sparkles size={11} /> Briefing ·{" "}
            {scope === "all"
              ? "All changes"
              : state.changes.find((c) => c.id === scope)?.name}{" "}
            · {audience} · {new Date().toLocaleDateString()}
          </div>

          <h3
            className="text-xl font-semibold text-stone-900 mb-6 leading-snug"
            style={{ fontFamily: "Georgia, serif" }}
          >
            {summary.headline}
          </h3>

          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-emerald-800 mb-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                Wins
              </div>
              <ul className="space-y-2">
                {(summary.wins || []).map((w, i) => (
                  <li key={i} className="text-sm text-stone-800 flex gap-2">
                    <span className="text-emerald-600">·</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-red-800 mb-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-red-600" />
                Risks
              </div>
              <ul className="space-y-2">
                {(summary.risks || []).map((r, i) => (
                  <li key={i} className="text-sm text-stone-800 flex gap-2">
                    <span className="text-red-600">·</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-amber-800 mb-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-600" />
                Decisions needed
              </div>
              <ul className="space-y-2">
                {(summary.decisions_needed || []).map((d, i) => (
                  <li key={i} className="text-sm text-stone-800 flex gap-2">
                    <span className="text-amber-600">·</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wider font-semibold text-purple-800 mb-2 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-600" />
                Next two weeks
              </div>
              <ul className="space-y-2">
                {(summary.next_two_weeks || []).map((n, i) => (
                  <li key={i} className="text-sm text-stone-800 flex gap-2">
                    <span className="text-purple-600">·</span>
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-stone-100 text-[10px] text-stone-400 italic">
            Generated by AI from live project data. Review before sharing
            externally.
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// READINESS — Change Readiness Assessment (CRA)
// ============================================================================
// Scoring scale: 1=Strongly Disagree → 5=Strongly Agree. Max 40 (8 × 5).
// Per-category traffic light: R if score 1-2, Y if 3, G if 4-5.
// Overall: G if total 32-40, Y if 16-31, R if below 16.

const LIKERT = [
  { value: 1, label: "Strongly Disagree", short: "SD" },
  { value: 2, label: "Disagree", short: "D" },
  { value: 3, label: "Neither", short: "N" },
  { value: 4, label: "Agree", short: "A" },
  { value: 5, label: "Strongly Agree", short: "SA" },
];

function ratingForCategoryScore(v) {
  if (!v) return null;
  if (v <= 2) return "R";
  if (v === 3) return "Y";
  return "G";
}
function overallRating(totalScore) {
  if (totalScore >= 32) return "G";
  if (totalScore >= 16) return "Y";
  return "R";
}
const RATING_META = {
  R: {
    label: "Urgent action needed",
    color: "#B91C1C",
    bg: "#FEE2E2",
    dot: "#DC2626",
    textOverall:
      "Prioritize actions to address the most critical areas — as these could derail your change. You may need additional resources and capability to give you the best chance of success.",
  },
  Y: {
    label: "Room for improvement",
    color: "#B45309",
    bg: "#FEF3C7",
    dot: "#F59E0B",
    textOverall:
      "There are some areas you need to address to give you the best chance of landing this change. Prioritize critical areas and consider what needs to be done to enhance or maintain areas you are stronger in.",
  },
  G: {
    label: "On track",
    color: "#047857",
    bg: "#D1FAE5",
    dot: "#10B981",
    textOverall:
      "Overall, you should be well positioned to land this change. You're strong across most categories, but there's always room for improvement! Think about efficiencies that could be gained, and what's required to maintain or boost your core strengths.",
  },
};

function ReadinessView({ state, setState, role }) {
  const canEdit = role !== "client_viewer";
  const [selectedId, setSelectedId] = useState(
    state.assessments[0]?.id || null
  );
  const [mode, setMode] = useState("view"); // 'view' | 'edit'

  const selected = state.assessments.find((a) => a.id === selectedId);
  const categories = state.taxonomies.craCategories || [];

  function createAssessment() {
    const id = uid("as");
    const blank = {
      id,
      name: `New assessment (${new Date().toLocaleDateString()})`,
      respondent: "",
      createdAt: new Date().toISOString().slice(0, 10),
      responses: {},
    };
    setState((p) => ({ ...p, assessments: [...p.assessments, blank] }));
    setSelectedId(id);
    setMode("edit");
  }
  function updateAssessment(updated) {
    setState((p) => ({
      ...p,
      assessments: p.assessments.map((a) =>
        a.id === updated.id ? updated : a
      ),
    }));
  }
  function removeAssessment(id) {
    setState((p) => ({
      ...p,
      assessments: p.assessments.filter((a) => a.id !== id),
    }));
    setSelectedId(null);
  }
  function setResponse(catNum, value) {
    if (!selected) return;
    updateAssessment({
      ...selected,
      responses: { ...selected.responses, [catNum]: value },
    });
  }

  // Compute results
  const results = useMemo(() => {
    if (!selected) return null;
    const perCategory = categories.map((c) => {
      const score = selected.responses[c.num] || 0;
      const rating = ratingForCategoryScore(score);
      // Consultant's per-question commentary on this category (optional)
      const comment = selected.comments?.[c.num] || "";
      return {
        ...c,
        score,
        rating,
        tacticsForRating: rating ? c[`tactics${rating}`] : null,
        comment,
      };
    });
    const totalScore = perCategory.reduce((s, c) => s + c.score, 0);
    const answered = perCategory.filter((c) => c.score > 0).length;
    const overall =
      answered === categories.length ? overallRating(totalScore) : null;
    return { perCategory, totalScore, answered, overall };
  }, [selected, categories]);

  const isComplete = results && results.answered === categories.length;

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-sky-700 font-medium mb-1">
            Readiness · Change Readiness Assessment
          </div>
          <h2
            className="text-3xl font-semibold text-stone-900"
            style={{
              fontFamily: "Georgia, serif",
              letterSpacing: "-0.015em",
              lineHeight: 1.15,
            }}
          >
            How ready are we to change?
          </h2>
          <p className="text-sm text-stone-600 mt-2 max-w-2xl">
            8 questions across 8 categories. Run it at baseline to set a
            starting point, and re-run periodically to track improvement. WTW's
            expert recommendations are surfaced based on your scores.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={createAssessment}
            className="text-xs text-white px-3 py-1.5 rounded font-medium flex items-center gap-1.5"
            style={{ background: "#0369A1" }}
          >
            <Plus size={12} /> New assessment
          </button>
        )}
      </div>

      <div className="grid grid-cols-[280px_1fr] gap-6">
        {/* Sidebar: list of assessments */}
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden self-start">
          <div className="px-4 py-2.5 border-b border-stone-200 bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
            Saved assessments ({state.assessments.length})
          </div>
          {state.assessments.length === 0 && (
            <div className="px-4 py-6 text-center text-xs text-stone-400 italic">
              No assessments yet. Click "New assessment" to begin.
            </div>
          )}
          {state.assessments.map((a) => {
            const isSel = a.id === selectedId;
            // Count only responses whose keys match a valid category number.
            // Prevents stale data with bad keys from inflating "answered" counts.
            const validResponses = categories
              .map((c) => a.responses?.[c.num])
              .filter((v) => v > 0);
            const answered = validResponses.length;
            const total = validResponses.reduce((s, v) => s + v, 0);
            const overall =
              answered === categories.length ? overallRating(total) : null;
            return (
              <div
                key={a.id}
                onClick={() => {
                  setSelectedId(a.id);
                  setMode("view");
                }}
                className={`group relative px-4 py-3 border-b border-stone-100 cursor-pointer ${
                  isSel ? "bg-sky-50" : "hover:bg-stone-50"
                }`}
              >
                <div className="flex items-center gap-2 pr-6">
                  {overall && (
                    <span
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: RATING_META[overall].dot }}
                    />
                  )}
                  <span className="text-sm font-medium text-stone-900 flex-1 truncate">
                    {a.name}
                  </span>
                </div>
                <div className="text-[11px] text-stone-500 mt-0.5">
                  {a.createdAt}
                  {a.respondent && ` · ${a.respondent}`}
                </div>
                <div className="text-[10px] text-stone-400 mt-0.5">
                  {answered}/{categories.length} answered
                  {overall && ` · ${total}/40`}
                </div>
                {canEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        window.confirm(
                          `Delete "${a.name}"? This cannot be undone.`
                        )
                      )
                        removeAssessment(a.id);
                    }}
                    title="Delete assessment"
                    className="absolute top-2 right-2 w-6 h-6 rounded flex items-center justify-center text-stone-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Main: assessment / results */}
        {!selected ? (
          <div className="border border-stone-200 rounded-lg bg-stone-50 p-12 text-center text-sm text-stone-500">
            Select an assessment from the left, or create a new one to begin.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Assessment header */}
            <div className="border border-stone-200 rounded-lg bg-white p-5">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <input
                    disabled={!canEdit}
                    value={selected.name}
                    onChange={(e) =>
                      updateAssessment({ ...selected, name: e.target.value })
                    }
                    className="text-lg font-semibold text-stone-900 bg-transparent w-full focus:outline-none focus:bg-stone-50 px-2 -ml-2 py-1 rounded"
                  />
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <Field label="Respondent">
                      {/* Respondent display merges name + role for clarity. Writes
                          back to `respondent` field for backward compatibility. */}
                      <input
                        disabled={!canEdit}
                        value={
                          selected.respondent ||
                          [selected.respondentName, selected.respondentRole]
                            .filter(Boolean)
                            .join(" — ") ||
                          ""
                        }
                        onChange={(e) =>
                          updateAssessment({
                            ...selected,
                            respondent: e.target.value,
                            respondentName: undefined,
                            respondentRole: undefined,
                          })
                        }
                        placeholder="Who completed this? (e.g. Steering committee, n=42 sample)"
                        className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                      />
                    </Field>
                    <Field label="Date">
                      <input
                        disabled={!canEdit}
                        type="date"
                        value={selected.createdAt || ""}
                        onChange={(e) =>
                          updateAssessment({
                            ...selected,
                            createdAt: e.target.value,
                          })
                        }
                        className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                      />
                    </Field>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setMode(mode === "edit" ? "view" : "edit")}
                      className={`text-xs px-2.5 py-1 rounded border ${
                        mode === "edit"
                          ? "bg-sky-700 text-white border-sky-700"
                          : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
                      }`}
                    >
                      {mode === "edit" ? "Done editing" : "Edit responses"}
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm("Delete this assessment?"))
                          removeAssessment(selected.id);
                      }}
                      className="text-xs text-red-600 hover:text-red-800 p-1.5"
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Overall score banner — only shown if all questions answered */}
            {isComplete && results.overall && (
              <div
                className="border rounded-lg p-5"
                style={{
                  background: RATING_META[results.overall].bg,
                  borderColor: RATING_META[results.overall].color + "40",
                }}
              >
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div
                      className="text-5xl font-bold leading-none"
                      style={{
                        color: RATING_META[results.overall].color,
                        fontFamily: "Georgia, serif",
                      }}
                    >
                      {results.totalScore}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-stone-600 mt-1">
                      of 40
                    </div>
                  </div>
                  <div className="flex-1">
                    <div
                      className="text-[10px] uppercase tracking-wider font-semibold"
                      style={{ color: RATING_META[results.overall].color }}
                    >
                      Overall
                    </div>
                    <div
                      className="text-xl font-semibold text-stone-900"
                      style={{ fontFamily: "Georgia, serif" }}
                    >
                      {RATING_META[results.overall].label}
                    </div>
                    <div className="text-sm text-stone-700 mt-1">
                      {RATING_META[results.overall].textOverall}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Progress when not complete */}
            {!isComplete && (
              <div className="border border-sky-200 bg-sky-50/40 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-xs text-sky-900 font-medium">
                      {results.answered}/{categories.length} questions answered
                    </div>
                    <div className="mt-1.5 h-2 bg-sky-100 rounded overflow-hidden">
                      <div
                        className="h-full bg-sky-700 transition-all"
                        style={{
                          width: `${
                            categories.length
                              ? (results.answered / categories.length) * 100
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                  {mode !== "edit" && canEdit && (
                    <button
                      onClick={() => setMode("edit")}
                      className="text-xs text-white px-3 py-1.5 rounded font-medium"
                      style={{ background: "#0369A1" }}
                    >
                      Continue assessment
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Category list */}
            <div className="space-y-2">
              {results.perCategory.map((cat) => (
                <CategoryCard
                  key={cat.num}
                  category={cat}
                  isEditing={mode === "edit"}
                  canEdit={canEdit}
                  onScore={(v) => setResponse(cat.num, v)}
                />
              ))}
            </div>

            {/* Overall notes / synthesis — appears below the category list when a
                consultant has written summary observations about the entire assessment. */}
            {(selected.notes || mode === "edit") && (
              <div className="border border-purple-200 bg-purple-50/30 rounded-lg p-5">
                <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-purple-900 mb-2">
                  Overall synthesis & recommendations
                </div>
                {mode === "edit" && canEdit ? (
                  <textarea
                    value={selected.notes || ""}
                    onChange={(e) =>
                      updateAssessment({ ...selected, notes: e.target.value })
                    }
                    rows={6}
                    placeholder="Synthesize the across-category picture: where are the biggest gaps, what do they imply for the change plan, what would you prioritize fixing in the next 30/60/90 days?"
                    className="w-full text-sm border border-purple-200 rounded p-3 bg-white resize-none focus:outline-none focus:border-purple-400 placeholder:text-stone-400"
                  />
                ) : (
                  <div className="text-sm text-stone-800 leading-relaxed whitespace-pre-wrap">
                    {selected.notes}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryCard({ category, isEditing, canEdit, onScore }) {
  const [expanded, setExpanded] = useState(false);
  const ratingMeta = category.rating ? RATING_META[category.rating] : null;

  return (
    <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
      {/* Header row — always visible */}
      <div className="px-4 py-3 flex items-center gap-3">
        {/* Traffic light */}
        <div
          className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-bold"
          style={{
            background: ratingMeta ? ratingMeta.bg : "#F3F4F6",
            color: ratingMeta ? ratingMeta.color : "#9CA3AF",
            border: ratingMeta
              ? `1px solid ${ratingMeta.color}40`
              : "1px solid #E5E7EB",
          }}
        >
          {category.score || "—"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-stone-900">
            {category.num}. {category.name}
          </div>
          <div className="text-[12px] text-stone-600 truncate">
            {category.question}
          </div>
        </div>
        {ratingMeta && !isEditing && (
          <span
            className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded flex-shrink-0"
            style={{ background: ratingMeta.bg, color: ratingMeta.color }}
          >
            {ratingMeta.label}
          </span>
        )}
        {category.score > 0 && !isEditing && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-[11px] text-sky-700 hover:text-sky-900 font-medium flex items-center gap-0.5 flex-shrink-0"
          >
            {expanded ? "Hide" : "View"} recommendations
            {expanded ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
          </button>
        )}
      </div>

      {/* Likert scale when editing */}
      {isEditing && canEdit && (
        <div className="border-t border-stone-100 px-4 py-3 bg-stone-50/40">
          <div className="grid grid-cols-5 gap-1.5">
            {LIKERT.map((opt) => {
              const selected = category.score === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => onScore(opt.value)}
                  className={`text-center py-2 rounded border transition-colors ${
                    selected
                      ? "border-sky-700"
                      : "border-stone-200 hover:border-stone-400"
                  }`}
                  style={
                    selected
                      ? { background: "#0369A1", color: "white" }
                      : { background: "white", color: "#57534e" }
                  }
                >
                  <div className="text-lg font-bold leading-none">
                    {opt.value}
                  </div>
                  <div className="text-[10px] mt-1 leading-tight">
                    {opt.label}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Expanded recommendations */}
      {expanded && !isEditing && category.score > 0 && (
        <div className="border-t border-stone-100 bg-stone-50/30">
          {/* Consultant's per-question commentary, if any */}
          {category.comment && (
            <div className="p-4 border-b border-stone-100 bg-purple-50/30">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-purple-900 mb-2 flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-purple-700" />
                Consultant notes for this category
              </div>
              <div className="text-[12px] text-stone-800 leading-relaxed italic">
                {category.comment}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 divide-x divide-stone-100">
            <div className="p-4">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-700 mb-2 flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-stone-700" />
                Keys to Success
              </div>
              <div className="text-[12px] text-stone-800 whitespace-pre-wrap leading-relaxed">
                {category.keysToSuccess}
              </div>
            </div>
            <div className="p-4">
              <div
                className="text-[10px] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1.5"
                style={{ color: ratingMeta.color }}
              >
                <span
                  className="w-1 h-1 rounded-full"
                  style={{ background: ratingMeta.color }}
                />
                Solutions & Tactics — {ratingMeta.label}
              </div>
              <div className="text-[12px] text-stone-800 whitespace-pre-wrap leading-relaxed">
                {category.tacticsForRating}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SIZING & IMPACT ASSESSMENT — Plot a change on the Sizing × Impact 2×2
// to determine Tier 1/2/3 and surface a prescriptive intervention list.
// ============================================================================

// Compute scores from a responses object { 1..13: 1-5 }
function computeSizingScores(responses, questions) {
  let sizingRaw = 0,
    impactRaw = 0;
  questions.forEach((q) => {
    const v = responses[q.num];
    if (!v) return;
    const score = q.reverse ? 6 - v : v; // 5 → 1, 1 → 5
    if (q.category === "sizing") sizingRaw += score;
    else impactRaw += score;
  });
  // Scale Sizing to 0–28 (max 25 raw with 5 questions). Add the buffer logic from source: cap at 28.
  // Scale Impact to 0–25 with weighting 25/40 = 0.625.
  // For consistency with the source (which calls out Max Sizing=28, Max Impact=25), we report normalized.
  // Sizing: raw is 0-25; show as raw (label axis 0-28 to give room for buffer convention).
  // Impact: raw is 0-40 (8 questions × 5); normalize to 0-25.
  const sizingScore = sizingRaw; // 0-25 actual
  const impactScore = (impactRaw * 25) / 40; // 0-25 normalized
  return { sizingRaw, impactRaw, sizingScore, impactScore };
}

function tierForScores(sizingScore, impactScore) {
  // Per source: thresholds at midpoint (14.5 of 28 sizing, 14.5 of 29 impact axis)
  // Use 12.5 of 25 for both since we normalize to 25 max
  const sizingHigh = sizingScore >= 12.5;
  const impactHigh = impactScore >= 12.5;
  if (sizingHigh && impactHigh) return 1; // both high — heaviest CM
  if (!sizingHigh && !impactHigh) return 3; // both low — lightest CM
  return 2; // one high, one low
}

const TIER_META = {
  1: {
    label: "Tier 1",
    desc: "Heavy change management — broad scope, deep impact",
    color: "#B91C1C",
    bg: "#FEE2E2",
  },
  2: {
    label: "Tier 2",
    desc: "Medium change management — significant on one axis",
    color: "#B45309",
    bg: "#FEF3C7",
  },
  3: {
    label: "Tier 3",
    desc: "Light change management — bounded scope and impact",
    color: "#047857",
    bg: "#D1FAE5",
  },
};

// Mapping from intervention names in the sizing taxonomy → in-app view IDs (so we can deep-link)
const INTERVENTION_LINKS = {
  "Change Readiness Assessment": { view: "cra", label: "Open CRA" },
  "Stakeholder Assessment": {
    view: "stakeholders",
    label: "Open Stakeholders",
  },
  "Stakeholder Alignment": { view: "stakeholders", label: "Open Stakeholders" },
  "Stakeholder Engagement Plan": {
    view: "stakeholders",
    label: "Open Stakeholders",
  },
  "Change Impact Assessment - High Level": {
    view: "editor",
    label: "Open Change Impact",
  },
  "Change Impact Assessment - Detailed": {
    view: "editor",
    label: "Open Change Impact",
  },
  "Change Management Plan": { view: "cmplan", label: "Open CM Plan" },
  "Change Management Deliverables Creation": {
    view: "cmplan",
    label: "Open CM Plan",
  },
  "Deliverables Rollout": {
    view: "interventions",
    label: "Open Intervention Status",
  },
  "Measurement dashboard": { view: "dashboard", label: "Open Insights" },
  "Persona Development": { view: "editor", label: "Open Change Impact" },
};

function SizingView({ state, setState, role, onNavigate }) {
  const canEdit = role !== "client_viewer";
  const [selectedId, setSelectedId] = useState(
    state.sizingAssessments[0]?.id || null
  );
  const [mode, setMode] = useState("view");

  const questions = state.taxonomies.sizingQuestions || [];
  const interventions = state.taxonomies.sizingInterventions || [];
  const selected = state.sizingAssessments.find((s) => s.id === selectedId);

  function createAssessment() {
    const id = uid("sz");
    const blank = {
      id,
      name: `New sizing assessment (${new Date().toLocaleDateString()})`,
      respondent: "",
      createdAt: new Date().toISOString().slice(0, 10),
      responses: {},
    };
    setState((p) => ({
      ...p,
      sizingAssessments: [...p.sizingAssessments, blank],
    }));
    setSelectedId(id);
    setMode("edit");
  }
  function updateAssessment(updated) {
    setState((p) => ({
      ...p,
      sizingAssessments: p.sizingAssessments.map((s) =>
        s.id === updated.id ? updated : s
      ),
    }));
  }
  function removeAssessment(id) {
    setState((p) => ({
      ...p,
      sizingAssessments: p.sizingAssessments.filter((s) => s.id !== id),
    }));
    setSelectedId(null);
  }
  function setResponse(qNum, value) {
    if (!selected) return;
    updateAssessment({
      ...selected,
      responses: { ...selected.responses, [qNum]: value },
    });
  }
  // Save consultant commentary for an individual sizing question. Mirrors the
  // setResponse pattern but writes to the comments map instead of responses.
  function setComment(qNum, text) {
    if (!selected) return;
    updateAssessment({
      ...selected,
      comments: { ...(selected.comments || {}), [qNum]: text },
    });
  }

  const results = useMemo(() => {
    if (!selected) return null;
    const answered = questions.filter((q) => selected.responses[q.num]).length;
    const scores = computeSizingScores(selected.responses, questions);
    const isComplete = answered === questions.length;
    const tier = isComplete
      ? tierForScores(scores.sizingScore, scores.impactScore)
      : null;
    return { ...scores, answered, total: questions.length, isComplete, tier };
  }, [selected, questions]);

  // Group interventions by phase for the results panel
  const interventionsByPhase = useMemo(() => {
    const phases = ["Define", "Discover", "Design", "Transform"];
    return phases.map((phase) => ({
      phase,
      items: interventions.filter((iv) => iv.phase === phase),
    }));
  }, [interventions]);

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-sky-700 font-medium mb-1">
            Readiness · Sizing & Impact Assessment
          </div>
          <h2
            className="text-3xl font-semibold text-stone-900"
            style={{
              fontFamily: "Georgia, serif",
              letterSpacing: "-0.015em",
              lineHeight: 1.15,
            }}
          >
            How much change management does this change need?
          </h2>
          <p className="text-sm text-stone-600 mt-2 max-w-2xl">
            13 questions across two dimensions — <em>Change Sizing</em> (scope)
            and <em>Change Impact</em> (depth). The result places your change in
            a tier and recommends specific interventions.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={createAssessment}
            className="text-xs text-white px-3 py-1.5 rounded font-medium flex items-center gap-1.5"
            style={{ background: "#0369A1" }}
          >
            <Plus size={12} /> New sizing
          </button>
        )}
      </div>

      {/* Cross-assessment comparison — visible when 2+ complete sizings exist.
          Provides at-a-glance contrast between scenarios so consultants can
          see which workstreams need which depth of CM treatment side-by-side. */}
      {(() => {
        const complete = state.sizingAssessments.filter((a) => {
          const answered = questions.filter((q) => a.responses[q.num]).length;
          return answered === questions.length;
        });
        if (complete.length < 2) return null;
        return (
          <div className="border border-sky-200 bg-gradient-to-br from-sky-50/40 to-white rounded-lg p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-sky-900">
                  Cross-assessment view
                </div>
                <div className="text-sm text-stone-700 mt-0.5">
                  Different workstreams warrant different depth of CM. Compare
                  them at a glance.
                </div>
              </div>
              <div className="text-[11px] text-stone-500">
                {complete.length} complete sizings
              </div>
            </div>
            <div
              className={`grid gap-3 ${
                complete.length === 2 ? "grid-cols-2" : "grid-cols-3"
              }`}
            >
              {complete.map((a) => {
                const sc = computeSizingScores(a.responses, questions);
                const tier = tierForScores(sc.sizingScore, sc.impactScore);
                const meta = TIER_META[tier];
                return (
                  <div
                    key={a.id}
                    onClick={() => {
                      setSelectedId(a.id);
                      setMode("view");
                    }}
                    className="border border-stone-200 rounded-lg bg-white p-4 cursor-pointer hover:border-sky-400 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <span
                        className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded"
                        style={{ background: meta.bg, color: meta.color }}
                      >
                        TIER {tier}
                      </span>
                      <span className="text-[10px] text-stone-400">
                        {a.createdAt}
                      </span>
                    </div>
                    <div className="text-sm font-semibold text-stone-900 leading-tight mb-2">
                      {a.name}
                    </div>
                    <div className="grid grid-cols-2 gap-2 mt-2 text-[11px]">
                      <div className="bg-stone-50 rounded p-1.5">
                        <div className="text-stone-500">Sizing</div>
                        <div className="font-bold text-stone-900">
                          {sc.sizingScore}
                          <span className="text-stone-400 font-normal">
                            /25
                          </span>
                        </div>
                      </div>
                      <div className="bg-stone-50 rounded p-1.5">
                        <div className="text-stone-500">Impact</div>
                        <div className="font-bold text-stone-900">
                          {sc.impactScore}
                          <span className="text-stone-400 font-normal">
                            /25
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Plain-English commentary when exactly 2 sizings — most common case */}
            {complete.length === 2 &&
              (() => {
                const a1 = complete[0],
                  a2 = complete[1];
                const s1 = computeSizingScores(a1.responses, questions);
                const s2 = computeSizingScores(a2.responses, questions);
                const t1 = tierForScores(s1.sizingScore, s1.impactScore);
                const t2 = tierForScores(s2.sizingScore, s2.impactScore);
                const tierDiff = Math.abs(t1 - t2);
                return (
                  <div className="mt-4 pt-4 border-t border-sky-100 text-[12px] text-stone-700 leading-relaxed">
                    <span className="font-semibold text-stone-900">Read:</span>{" "}
                    {tierDiff === 0 ? (
                      <>
                        Both workstreams land at the same tier. Plan symmetrical
                        treatment with the same depth of interventions on each.
                      </>
                    ) : tierDiff === 1 ? (
                      <>
                        <em>{t1 < t2 ? a1.name : a2.name}</em> is one tier
                        higher than <em>{t1 < t2 ? a2.name : a1.name}</em> —
                        meaning it warrants more depth of CM treatment (more
                        measurement, more sponsorship cadence, more structured
                        interventions). The lighter workstream can lean on
                        lighter touch + reuse comms infrastructure.
                      </>
                    ) : (
                      <>
                        The two workstreams are{" "}
                        <strong>{tierDiff} tiers apart</strong>. This is a
                        strong signal that resourcing and governance attention
                        should be unequal: the higher-tier workstream needs the
                        lead consultant; the lower-tier can be supported with
                        senior associate effort + reused materials.
                      </>
                    )}
                  </div>
                );
              })()}
          </div>
        );
      })()}

      <div className="grid grid-cols-[280px_1fr] gap-6">
        {/* Sidebar */}
        <div className="border border-stone-200 rounded-lg bg-white overflow-hidden self-start">
          <div className="px-4 py-2.5 border-b border-stone-200 bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
            Saved sizings ({state.sizingAssessments.length})
          </div>
          {state.sizingAssessments.length === 0 && (
            <div className="px-4 py-6 text-center text-xs text-stone-400 italic">
              No sizings yet. Click "New sizing" to begin.
            </div>
          )}
          {state.sizingAssessments.map((a) => {
            const isSel = a.id === selectedId;
            const answered = questions.filter((q) => a.responses[q.num]).length;
            const sc = computeSizingScores(a.responses, questions);
            const tier =
              answered === questions.length
                ? tierForScores(sc.sizingScore, sc.impactScore)
                : null;
            return (
              <div
                key={a.id}
                onClick={() => {
                  setSelectedId(a.id);
                  setMode("view");
                }}
                className={`group relative px-4 py-3 border-b border-stone-100 cursor-pointer ${
                  isSel ? "bg-sky-50" : "hover:bg-stone-50"
                }`}
              >
                <div className="flex items-center gap-2 pr-6">
                  {tier && (
                    <span
                      className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded flex-shrink-0"
                      style={{
                        background: TIER_META[tier].bg,
                        color: TIER_META[tier].color,
                      }}
                    >
                      T{tier}
                    </span>
                  )}
                  <span className="text-sm font-medium text-stone-900 flex-1 truncate">
                    {a.name}
                  </span>
                </div>
                <div className="text-[11px] text-stone-500 mt-0.5 truncate">
                  {a.createdAt}
                  {a.respondent && ` · ${a.respondent}`}
                </div>
                <div className="text-[10px] text-stone-400 mt-0.5">
                  {answered}/{questions.length} answered
                </div>
                {canEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        window.confirm(
                          `Delete "${a.name}"? This cannot be undone.`
                        )
                      )
                        removeAssessment(a.id);
                    }}
                    title="Delete sizing assessment"
                    className="absolute top-2 right-2 w-6 h-6 rounded flex items-center justify-center text-stone-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Main */}
        {!selected ? (
          <div className="border border-stone-200 rounded-lg bg-stone-50 p-12 text-center text-sm text-stone-500">
            Select a sizing from the left, or create a new one to begin.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Header */}
            <div className="border border-stone-200 rounded-lg bg-white p-5">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <input
                    disabled={!canEdit}
                    value={selected.name}
                    onChange={(e) =>
                      updateAssessment({ ...selected, name: e.target.value })
                    }
                    className="text-lg font-semibold text-stone-900 bg-transparent w-full focus:outline-none focus:bg-stone-50 px-2 -ml-2 py-1 rounded"
                  />
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <Field label="Respondent">
                      <input
                        disabled={!canEdit}
                        value={
                          selected.respondent ||
                          [selected.respondentName, selected.respondentRole]
                            .filter(Boolean)
                            .join(" — ") ||
                          ""
                        }
                        onChange={(e) =>
                          updateAssessment({
                            ...selected,
                            respondent: e.target.value,
                            respondentName: undefined,
                            respondentRole: undefined,
                          })
                        }
                        placeholder="Who completed this?"
                        className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                      />
                    </Field>
                    <Field label="Date">
                      <input
                        disabled={!canEdit}
                        type="date"
                        value={selected.createdAt || ""}
                        onChange={(e) =>
                          updateAssessment({
                            ...selected,
                            createdAt: e.target.value,
                          })
                        }
                        className="w-full text-sm border border-stone-200 rounded p-2 bg-white"
                      />
                    </Field>
                  </div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setMode(mode === "edit" ? "view" : "edit")}
                      className={`text-xs px-2.5 py-1 rounded border ${
                        mode === "edit"
                          ? "bg-sky-700 text-white border-sky-700"
                          : "bg-white text-stone-700 border-stone-300 hover:border-stone-500"
                      }`}
                    >
                      {mode === "edit" ? "Done editing" : "Edit responses"}
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm("Delete this sizing assessment?"))
                          removeAssessment(selected.id);
                      }}
                      className="text-xs text-red-600 hover:text-red-800 p-1.5"
                      title="Delete"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Progress when not complete */}
            {!results.isComplete && (
              <div className="border border-sky-200 bg-sky-50/40 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-xs text-sky-900 font-medium">
                      {results.answered}/{results.total} questions answered
                    </div>
                    <div className="mt-1.5 h-2 bg-sky-100 rounded overflow-hidden">
                      <div
                        className="h-full bg-sky-700 transition-all"
                        style={{
                          width: `${
                            results.total
                              ? (results.answered / results.total) * 100
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                  {mode !== "edit" && canEdit && (
                    <button
                      onClick={() => setMode("edit")}
                      className="text-xs text-white px-3 py-1.5 rounded font-medium"
                      style={{ background: "#0369A1" }}
                    >
                      Continue assessment
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Results: 2x2 quadrant + tier banner */}
            {results.isComplete && results.tier && (
              <div
                className="border rounded-lg p-5"
                style={{
                  background: TIER_META[results.tier].bg,
                  borderColor: TIER_META[results.tier].color + "40",
                }}
              >
                <div className="grid grid-cols-[200px_1fr] gap-6 items-center">
                  {/* 2x2 quadrant viz */}
                  <SizingQuadrant
                    sizingScore={results.sizingScore}
                    impactScore={results.impactScore}
                    tier={results.tier}
                  />
                  <div>
                    <div
                      className="text-[10px] uppercase tracking-wider font-semibold"
                      style={{ color: TIER_META[results.tier].color }}
                    >
                      Recommended approach
                    </div>
                    <div
                      className="text-2xl font-semibold text-stone-900 mt-1"
                      style={{ fontFamily: "Georgia, serif" }}
                    >
                      {TIER_META[results.tier].label}
                    </div>
                    <div className="text-sm text-stone-700 mt-1">
                      {TIER_META[results.tier].desc}
                    </div>
                    <div className="mt-3 flex gap-4 text-xs">
                      <div>
                        <span className="text-stone-500">Change Sizing: </span>
                        <span className="font-semibold text-stone-900">
                          {results.sizingScore.toFixed(1)} / 25
                        </span>
                      </div>
                      <div>
                        <span className="text-stone-500">Change Impact: </span>
                        <span className="font-semibold text-stone-900">
                          {results.impactScore.toFixed(1)} / 25
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Question list (edit mode shows scale; view mode shows responses summary) */}
            {mode === "edit" && canEdit ? (
              <div className="space-y-2">
                {questions.map((q) => (
                  <SizingQuestionRow
                    key={q.num}
                    q={q}
                    value={selected.responses[q.num]}
                    comment={selected.comments?.[q.num] || ""}
                    onSet={(v) => setResponse(q.num, v)}
                    onComment={(t) => setComment(q.num, t)}
                    canEdit={canEdit}
                  />
                ))}
              </div>
            ) : (
              <div className="border border-stone-200 rounded-lg bg-white overflow-hidden">
                <div className="px-4 py-2.5 border-b border-stone-200 bg-stone-50 text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
                  Responses
                </div>
                {questions.map((q) => {
                  const v = selected.responses[q.num];
                  const score = v ? (q.reverse ? 6 - v : v) : null;
                  const comment = selected.comments?.[q.num];
                  return (
                    <div
                      key={q.num}
                      className="px-4 py-2.5 border-b border-stone-100 text-sm"
                    >
                      <div className="flex items-start gap-3">
                        <span
                          className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5"
                          style={{
                            background:
                              q.category === "sizing" ? "#E0F2FE" : "#FEF3C7",
                            color:
                              q.category === "sizing" ? "#0369A1" : "#B45309",
                          }}
                        >
                          {q.category === "sizing" ? "SZ" : "IM"}
                        </span>
                        <span className="flex-1 text-stone-800">
                          {q.num}. {q.text}
                        </span>
                        <span className="text-stone-600 text-[12px] flex-shrink-0 max-w-[180px] truncate">
                          {v ? (
                            q.scale[v - 1]
                          ) : (
                            <em className="text-stone-400">unanswered</em>
                          )}
                        </span>
                        {score !== null && (
                          <span
                            className="w-6 h-6 rounded text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0"
                            style={{ background: "#0369A1" }}
                          >
                            {score}
                          </span>
                        )}
                      </div>
                      {comment && (
                        <div className="text-[12px] text-stone-600 italic mt-1.5 ml-12 leading-relaxed">
                          {comment}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Tiered interventions */}
            {results.isComplete && results.tier && (
              <div className="border border-stone-200 rounded-lg bg-white">
                <div className="px-5 py-3 border-b border-stone-200">
                  <div className="text-sm font-semibold text-stone-900">
                    Recommended interventions for{" "}
                    {TIER_META[results.tier].label}
                  </div>
                  <div className="text-[11px] text-stone-500 mt-0.5">
                    Cumulative: Tier 3 items are the baseline. Tier 2 adds depth
                    for medium changes. Tier 1 is the full kit for the heaviest
                    changes. This list is suggestive, not exhaustive.
                  </div>
                </div>
                <div className="divide-y divide-stone-100">
                  {interventionsByPhase.map(({ phase, items }) => (
                    <div key={phase} className="px-5 py-3">
                      <div className="text-[10px] uppercase tracking-wider font-semibold text-stone-500 mb-2">
                        {phase}
                      </div>
                      <div className="space-y-1.5">
                        {items.map((iv) => {
                          // Show this intervention if the current tier requires items of this tier-level or lower
                          // Tier 3 = show only tier-3. Tier 2 = show tier-3 and tier-2. Tier 1 = show all.
                          const active = iv.tier >= results.tier;
                          const link = INTERVENTION_LINKS[iv.name];
                          return (
                            <div
                              key={iv.name}
                              className={`flex items-center gap-2 text-[13px] ${
                                active ? "text-stone-800" : "text-stone-300"
                              }`}
                            >
                              <span
                                className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0`}
                                style={
                                  active
                                    ? {
                                        background: TIER_META[iv.tier].bg,
                                        color: TIER_META[iv.tier].color,
                                      }
                                    : {
                                        background: "#F3F4F6",
                                        color: "#D1D5DB",
                                      }
                                }
                              >
                                {iv.tier}
                              </span>
                              <span className="flex-1">{iv.name}</span>
                              {active && link && onNavigate && (
                                <button
                                  onClick={() => onNavigate(link.view)}
                                  className="text-[11px] text-sky-700 hover:text-sky-900 font-medium flex-shrink-0"
                                >
                                  {link.label} →
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overall synthesis & recommendations — consultant's narrative
                interpretation of why this sizing came out where it did and what
                that means for delivery. */}
            {(selected.notes || mode === "edit") && (
              <div className="border border-sky-200 bg-sky-50/30 rounded-lg p-5">
                <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-sky-900 mb-2">
                  Synthesis & delivery recommendations
                </div>
                {mode === "edit" && canEdit ? (
                  <textarea
                    value={selected.notes || ""}
                    onChange={(e) =>
                      updateAssessment({ ...selected, notes: e.target.value })
                    }
                    rows={6}
                    placeholder="Why does this sizing land where it does? What does the tier mean for resourcing, governance, and pace?"
                    className="w-full text-sm border border-sky-200 rounded p-3 bg-white resize-none focus:outline-none focus:border-sky-400 placeholder:text-stone-400"
                  />
                ) : (
                  <div className="text-sm text-stone-800 leading-relaxed whitespace-pre-wrap">
                    {selected.notes}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SizingQuadrant({ sizingScore, impactScore, tier }) {
  // Plot a single point on a 200×200 px 2×2 grid
  // x = sizing (0-25), y = impact (0-25)
  const W = 200,
    H = 200;
  const x = (sizingScore / 25) * W;
  // y inverted (high impact at top)
  const y = H - (impactScore / 25) * H;

  // Quadrant tier colors
  const quads = [
    { x: 0, y: 0, w: W / 2, h: H / 2, tier: 2, label: "T2" }, // top-left: low sizing, high impact
    { x: W / 2, y: 0, w: W / 2, h: H / 2, tier: 1, label: "T1" }, // top-right
    { x: 0, y: H / 2, w: W / 2, h: H / 2, tier: 3, label: "T3" }, // bottom-left
    { x: W / 2, y: H / 2, w: W / 2, h: H / 2, tier: 2, label: "T2" }, // bottom-right
  ];

  return (
    <div>
      <svg
        width={W}
        height={H}
        viewBox={`0 0 ${W} ${H}`}
        className="border border-stone-300 rounded"
      >
        {quads.map((q, i) => (
          <rect
            key={i}
            x={q.x}
            y={q.y}
            width={q.w}
            height={q.h}
            fill={TIER_META[q.tier].bg}
            stroke={q.tier === tier ? TIER_META[q.tier].color : "#E7E5E4"}
            strokeWidth={q.tier === tier ? 2 : 1}
          />
        ))}
        {quads.map((q, i) => (
          <text
            key={"t" + i}
            x={q.x + q.w / 2}
            y={q.y + q.h / 2 + 5}
            textAnchor="middle"
            fontSize="14"
            fontWeight="bold"
            fill={TIER_META[q.tier].color}
            opacity={0.4}
          >
            {q.label}
          </text>
        ))}
        {/* Axes */}
        <line
          x1={W / 2}
          y1={0}
          x2={W / 2}
          y2={H}
          stroke="#D6D3D1"
          strokeWidth={1}
        />
        <line
          x1={0}
          y1={H / 2}
          x2={W}
          y2={H / 2}
          stroke="#D6D3D1"
          strokeWidth={1}
        />
        {/* Plot the point */}
        <circle
          cx={x}
          cy={y}
          r={6}
          fill="#1A1A2E"
          stroke="white"
          strokeWidth={2}
        />
      </svg>
      <div className="text-[10px] text-stone-500 mt-1 flex justify-between px-1">
        <span>← low Sizing</span>
        <span>high Sizing →</span>
      </div>
      <div className="text-[10px] text-stone-500 text-center mt-0.5">
        ↑ high Impact, ↓ low Impact
      </div>
    </div>
  );
}

function SizingQuestionRow({ q, value, comment, onSet, onComment, canEdit }) {
  return (
    <div className="border border-stone-200 rounded-lg bg-white p-4">
      <div className="flex items-start gap-3 mb-3">
        <span
          className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5"
          style={{
            background: q.category === "sizing" ? "#E0F2FE" : "#FEF3C7",
            color: q.category === "sizing" ? "#0369A1" : "#B45309",
          }}
        >
          {q.category === "sizing" ? "Sizing" : "Impact"}
        </span>
        <div className="flex-1">
          <div className="text-sm font-medium text-stone-900">
            {q.num}. {q.text}
          </div>
          {q.reverse && (
            <div className="text-[10px] text-stone-500 italic mt-0.5">
              Reverse-scored: more positive answers indicate less change risk.
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {q.scale.map((label, i) => {
          const v = i + 1;
          const selected = value === v;
          return (
            <button
              key={v}
              onClick={() => onSet(v)}
              className={`text-center py-2 px-1 rounded border transition-colors ${
                selected
                  ? "border-sky-700"
                  : "border-stone-200 hover:border-stone-400"
              }`}
              style={
                selected
                  ? { background: "#0369A1", color: "white" }
                  : { background: "white", color: "#57534e" }
              }
            >
              <div className="text-sm font-bold leading-none">{v}</div>
              <div className="text-[10px] mt-1 leading-tight">{label}</div>
            </button>
          );
        })}
      </div>
      {/* Consultant commentary on this question — shown when present, or
          editable when canEdit is true. Helps explain WHY this score was chosen. */}
      {(comment || canEdit) && (
        <div className="mt-3 pt-3 border-t border-stone-100">
          {canEdit ? (
            <textarea
              value={comment || ""}
              onChange={(e) => onComment && onComment(e.target.value)}
              rows={2}
              placeholder="Why this score? What's the evidence?"
              className="w-full text-[12px] border border-stone-200 rounded p-2 bg-stone-50/40 resize-none focus:outline-none focus:border-sky-400 placeholder:text-stone-400 italic"
            />
          ) : (
            <div className="text-[12px] text-stone-700 italic leading-relaxed">
              {comment}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PERSISTENCE — localStorage hydration & save
// ============================================================================
// Survives page reloads and tab closes. Still browser-local — no backend yet.
// The key is namespaced so two projects in two tabs on different origins don't collide.
const STORAGE_KEY = "wtw-cm-platform:demo:v3";

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Sanity check the shape — if a future version of the app expects new keys,
    // graceful-fall-back means we don't crash on old data; we just merge with SEED defaults.
    if (
      !parsed ||
      typeof parsed !== "object" ||
      !parsed.taxonomies ||
      !parsed.project
    )
      return null;
    // Ensure all top-level arrays exist (for forward-compat with old saved states)
    const required = [
      "changes",
      "changesToImplement",
      "audiences",
      "stakeholders",
      "interventions",
      "assessments",
      "sizingAssessments",
      "clientContext",
      "pendingProposals",
    ];
    required.forEach((k) => {
      if (!Array.isArray(parsed[k])) parsed[k] = [];
    });
    if (!parsed.terminologyOverrides) parsed.terminologyOverrides = {};
    return parsed;
  } catch (e) {
    console.warn("Failed to load state from localStorage:", e);
    return null;
  }
}

function saveToStorage(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    // Most likely quota exceeded (5MB browser limit). Tell the user once.
    console.warn("Failed to save state to localStorage:", e);
  }
}

function clearStorage() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_) {}
}

export default function App() {
  // Hydrate from localStorage on first mount. Falls back to empty SEED if nothing saved.
  const [state, setState] = useState(() => loadFromStorage() || SEED);
  // DEMO BUILD: Always opens to the Set up phase (Project info) so viewers see
  // the welcome experience first. In production this routes based on whether
  // data exists (CRA for returning users, settings for fresh projects).
  const [view, setView] = useState("settings");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedKind, setSelectedKind] = useState(null);
  const [role, setRole] = useState("consultant");
  const [useClientTerms, setUseClientTerms] = useState(false);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  // resetKey is bumped on Reset; used as React key on the main view container so that
  // child components (CMPlanView filters, AIAssistPanel proposals, ReadinessView/SizingView
  // local selection) get force-remounted with fresh state instead of carrying stale UI.
  const [resetKey, setResetKey] = useState(0);
  // Slide-over drawer for the client context panel — accessible from any view via header
  const [contextOpen, setContextOpen] = useState(false);

  // Auto-save: debounce writes so we don't slam localStorage on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => saveToStorage(state), 400);
    return () => clearTimeout(timer);
  }, [state]);

  const displayState = useMemo(() => {
    if (useClientTerms) return state;
    return { ...state, terminologyOverrides: {} };
  }, [state, useClientTerms]);

  const onSelect = useCallback(
    (id, kind) => {
      setSelectedId(id);
      setSelectedKind(kind);
      if (view !== "editor") setView("editor");
    },
    [view]
  );

  const updateChange = useCallback((updated) => {
    setState((p) => ({
      ...p,
      changes: p.changes.map((c) => (c.id === updated.id ? updated : c)),
    }));
  }, []);
  const removeChange = useCallback((id) => {
    setState((p) => {
      const ctiIds = p.changesToImplement
        .filter((c) => c.changeId === id)
        .map((c) => c.id);
      const audIds = p.audiences
        .filter((a) => ctiIds.includes(a.changeToImplementId))
        .map((a) => a.id);
      const audIdSet = new Set(audIds);
      return {
        ...p,
        changes: p.changes.filter((c) => c.id !== id),
        changesToImplement: p.changesToImplement.filter(
          (c) => c.changeId !== id
        ),
        audiences: p.audiences.filter((a) => !audIdSet.has(a.id)),
        // Cascade: remove interventions pointing at deleted audiences
        interventions: p.interventions.filter(
          (iv) => !audIdSet.has(iv.audienceId)
        ),
        // Cascade: scrub deleted audience ids from stakeholder.audienceLinkIds
        stakeholders: p.stakeholders.map((s) => ({
          ...s,
          audienceLinkIds: (s.audienceLinkIds || []).filter(
            (lid) => !audIdSet.has(lid)
          ),
        })),
      };
    });
    setSelectedId(null);
  }, []);
  const updateCTI = useCallback((updated) => {
    setState((p) => ({
      ...p,
      changesToImplement: p.changesToImplement.map((c) =>
        c.id === updated.id ? updated : c
      ),
    }));
  }, []);
  const removeCTI = useCallback((id) => {
    setState((p) => {
      const audIds = p.audiences
        .filter((a) => a.changeToImplementId === id)
        .map((a) => a.id);
      const audIdSet = new Set(audIds);
      return {
        ...p,
        changesToImplement: p.changesToImplement.filter((c) => c.id !== id),
        audiences: p.audiences.filter((a) => a.changeToImplementId !== id),
        // Cascade: remove interventions pointing at deleted audiences
        interventions: p.interventions.filter(
          (iv) => !audIdSet.has(iv.audienceId)
        ),
        // Cascade: scrub deleted audience ids from stakeholder.audienceLinkIds
        stakeholders: p.stakeholders.map((s) => ({
          ...s,
          audienceLinkIds: (s.audienceLinkIds || []).filter(
            (lid) => !audIdSet.has(lid)
          ),
        })),
      };
    });
    setSelectedId(null);
  }, []);
  const updateAudience = useCallback((updated) => {
    setState((p) => ({
      ...p,
      audiences: p.audiences.map((a) => (a.id === updated.id ? updated : a)),
    }));
  }, []);
  const removeAudience = useCallback((id) => {
    setState((p) => ({
      ...p,
      audiences: p.audiences.filter((a) => a.id !== id),
      // Cascade: remove any interventions for the deleted audience
      interventions: p.interventions.filter((iv) => iv.audienceId !== id),
      // Cascade: scrub from stakeholder.audienceLinkIds
      stakeholders: p.stakeholders.map((s) => ({
        ...s,
        audienceLinkIds: (s.audienceLinkIds || []).filter((lid) => lid !== id),
      })),
    }));
    setSelectedId(null);
  }, []);

  const addChange = useCallback(() => {
    const id = uid("c");
    setState((p) => ({
      ...p,
      changes: [
        ...p.changes,
        { id, name: "New change", description: "", isKey: false },
      ],
    }));
    setSelectedId(id);
    setSelectedKind("change");
  }, []);
  const addCTI = useCallback((changeId) => {
    const id = uid("ci");
    setState((p) => ({
      ...p,
      changesToImplement: [
        ...p.changesToImplement,
        {
          id,
          changeId,
          name: "New change to implement",
          description: "",
          workstreamId: "",
          from: "",
          to: "",
        },
      ],
    }));
    setSelectedId(id);
    setSelectedKind("cti");
  }, []);
  const addAudience = useCallback((ctiId) => {
    const id = uid("a");
    setState((p) => ({
      ...p,
      audiences: [
        ...p.audiences,
        {
          id,
          changeToImplementId: ctiId,
          name: "New audience",
          impactDescription: "",
          audienceType: "end_user",
          impactTypeIds: [],
          reactionIds: [],
          leverIds: [],
        },
      ],
    }));
    setSelectedId(id);
    setSelectedKind("audience");
  }, []);

  const handleReset = useCallback(() => {
    if (
      !window.confirm(
        "Reset will erase everything in this project — all changes, audiences, stakeholders, interventions, assessments, and uploaded client context files. This cannot be undone. Proceed?"
      )
    )
      return;
    clearStorage();
    setState(SEED);
    setSelectedId(null);
    setSelectedKind(null);
    setView("settings");
    setResetKey((k) => k + 1); // force-remount all view components to clear their local state
  }, []);

  const selectedEntity = useMemo(() => {
    if (!selectedId) return null;
    if (selectedKind === "change")
      return displayState.changes.find((c) => c.id === selectedId);
    if (selectedKind === "cti")
      return displayState.changesToImplement.find((c) => c.id === selectedId);
    if (selectedKind === "audience")
      return displayState.audiences.find((a) => a.id === selectedId);
    return null;
  }, [selectedId, selectedKind, displayState]);

  return (
    <div
      className="h-screen flex flex-col"
      style={{
        background: tokens.surface.page,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", system-ui, sans-serif',
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
      }}
    >
      <Header
        state={displayState}
        view={view}
        setView={setView}
        role={role}
        setRole={setRole}
        useClientTerms={useClientTerms}
        setUseClientTerms={setUseClientTerms}
        onReset={handleReset}
        onOpenContext={() => setContextOpen(true)}
      />

      <div className="flex-1 flex overflow-hidden">
        {view === "editor" && (
          <aside
            className={`overflow-y-auto flex-shrink-0 ${
              leftCollapsed ? "w-12" : "w-72"
            }`}
            style={{
              background: "white",
              borderRight: "1px solid rgb(229, 231, 235)",
              transition: tokens.transition.base,
            }}
          >
            <div className="p-4">
              <button
                onClick={() => setLeftCollapsed((p) => !p)}
                className="text-stone-400 mb-3 w-7 h-7 rounded-md flex items-center justify-center"
                title={leftCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                style={{ transition: tokens.transition.base }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = tokens.surface.panelMuted;
                  e.currentTarget.style.color = "#44403C";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "#A8A29E";
                }}
              >
                {leftCollapsed ? (
                  <ChevronsRight size={14} />
                ) : (
                  <ChevronsLeft size={14} />
                )}
              </button>
              {!leftCollapsed && (
                <HierarchyTree
                  state={displayState}
                  selectedId={selectedId}
                  onSelect={onSelect}
                  onAddChange={addChange}
                  onAddCTI={addCTI}
                  onAddAudience={addAudience}
                  role={role}
                />
              )}
            </div>
          </aside>
        )}

        <main key={resetKey} className="flex-1 overflow-y-auto">
          {view === "editor" &&
            (!selectedEntity ? (
              <EmptyState state={displayState} setView={setView} />
            ) : selectedKind === "change" ? (
              <ChangeEditor
                change={selectedEntity}
                state={displayState}
                update={updateChange}
                remove={() => removeChange(selectedEntity.id)}
                role={role}
              />
            ) : selectedKind === "cti" ? (
              <CTIEditor
                cti={selectedEntity}
                state={displayState}
                update={updateCTI}
                remove={() => removeCTI(selectedEntity.id)}
                role={role}
              />
            ) : (
              <AudienceEditor
                audience={selectedEntity}
                state={displayState}
                update={updateAudience}
                remove={() => removeAudience(selectedEntity.id)}
                role={role}
              />
            ))}
          {view === "dashboard" && <Dashboard state={displayState} />}
          {view === "timeline" && <Timeline state={displayState} />}
          {view === "settings" && (
            <SettingsView
              state={state}
              setState={setState}
              role={role}
              setView={setView}
            />
          )}
          {view === "review" && (
            <ProposalReview
              state={state}
              setState={setState}
              role={role}
              onClose={() => setView("settings")}
            />
          )}
          {view === "stakeholders" && (
            <StakeholdersView
              state={displayState}
              setState={setState}
              role={role}
            />
          )}
          {view === "cmplan" && (
            <CMPlanView state={displayState} setState={setState} role={role} />
          )}
          {view === "interventions" && (
            <InterventionsView
              state={displayState}
              setState={setState}
              role={role}
            />
          )}
          {view === "execsummary" && <ExecSummaryView state={displayState} />}
          {view === "cra" && (
            <ReadinessView
              state={displayState}
              setState={setState}
              role={role}
            />
          )}
          {view === "sizing" && (
            <SizingView
              state={displayState}
              setState={setState}
              role={role}
              onNavigate={setView}
            />
          )}
        </main>
      </div>

      <footer
        className="px-6 py-2 flex items-center justify-between text-[11px]"
        style={{
          background: "white",
          borderTop: "1px solid rgb(229, 231, 235)",
          color: "#78716C",
        }}
      >
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: "#10B981" }}
            />
            <span className="font-medium">
              {state.project.name || "Untitled project"}
            </span>
          </span>
          <span className="text-stone-300">·</span>
          <span>
            {role === "consultant"
              ? "WTW Consultant"
              : role === "client_lead"
              ? "Client Lead"
              : "Read-only"}
          </span>
          {role === "consultant" && (
            <>
              <span className="text-stone-300">·</span>
              <span
                className="text-stone-400"
                title="Project state saved to this browser's localStorage"
              >
                Auto-saved locally
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {role === "consultant" && (
            <>
              <div className="flex -space-x-1.5">
                <div
                  className="w-5 h-5 rounded-full border-2 border-white text-[9px] text-white flex items-center justify-center font-semibold"
                  style={{
                    background: tokens.primary,
                    boxShadow: tokens.shadow.xs,
                  }}
                >
                  CK
                </div>
                <div
                  className="w-5 h-5 rounded-full border-2 border-white text-[9px] text-white flex items-center justify-center font-semibold"
                  style={{ background: "#F59E0B", boxShadow: tokens.shadow.xs }}
                >
                  MR
                </div>
                <div
                  className="w-5 h-5 rounded-full border-2 border-white text-[9px] text-stone-600 flex items-center justify-center font-semibold"
                  style={{ background: "#E7E5E4", boxShadow: tokens.shadow.xs }}
                >
                  +1
                </div>
              </div>
              <span className="text-stone-400 ml-1">3 collaborators</span>
            </>
          )}
        </div>
      </footer>

      {/* Client Context drawer — opens from the Header "Context" button.
          Available on every view so a consultant can drop a doc the moment they get it,
          without having to navigate back to the Define tab. */}
      {contextOpen && (
        <div
          className="fixed inset-0 z-50 flex"
          onClick={() => setContextOpen(false)}
        >
          {/* Backdrop */}
          <div className="flex-1 bg-stone-900/30 backdrop-blur-[1px]" />
          {/* Drawer */}
          <div
            className="w-[480px] bg-white border-l border-stone-200 shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between bg-stone-50">
              <div>
                <div className="text-sm font-semibold text-stone-900">
                  Client context
                </div>
                <div className="text-[11px] text-stone-500 mt-0.5">
                  Background materials the AI will use when drafting impact
                  descriptions, reactions, K/F/D, and exec briefings.
                </div>
              </div>
              <button
                onClick={() => setContextOpen(false)}
                className="text-stone-400 hover:text-stone-700 p-1"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <ClientContextPanel
                state={state}
                setState={setState}
                canEdit={role !== "client_viewer"}
                compact
                onOpenReview={() => {
                  setContextOpen(false);
                  setView("review");
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
